import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom"; // For navigation
import { Button, ButtonGroup, ListItem, List, ListItemText, TextField, Select, MenuItem, FormControl, InputLabel, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, Backdrop, CircularProgress, Typography, Divider } from "@mui/material";
import 'core-js/features/promise'; // Add Promise polyfill
import 'core-js/features/promise'; // Add Promise polyfill
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { LocalizationProvider, DateCalendar } from "@mui/x-date-pickers-pro";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faCommentDots,faCheckCircle,faHourglassHalf,faCircle,faCircleNotch} from "@fortawesome/free-solid-svg-icons";
import Tooltip from '@mui/material/Tooltip';
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import {
  ViewState,
  EditingState,
  IntegratedEditing,
  GroupingState,
  IntegratedGrouping,
  // DragDropProvider,
} from "@devexpress/dx-react-scheduler";
import {
  Scheduler,
  WeekView,
  MonthView,
  Appointments,
  DayView,
  DragDropProvider,
  // GroupingState,
  // IntegratedGrouping,
  GroupingPanel,
  Resources,
} from "@devexpress/dx-react-scheduler-material-ui";
import TableCell from "@mui/material/TableCell";

// import appointments from "./demo-data/today-appointments";
// import { resourcesData } from "./demo-data/appointments";
import "./Style.css";
import MenuBar from "./MenuBar";
import Footer from "./Footer";
import { AvailabilityEditDialog, AvailabilityDialog, LeaveRequestDialog, TaskConflictDialog } from './Calendar/dialogs';

const envVariable = require("../EnvironmentVariables.json");

// Debug flag - set to true to enable verbose console logging
const DEBUG = false;

const PREFIX = "Demo";

// const currentDate = '2024-03-14';

const classes = {
  container: `${PREFIX}-container`,
  text: `${PREFIX}-text`,
  formControlLabel: `${PREFIX}-formControlLabel`,
  icon: `${PREFIX}-icon`,
  textCenter: `${PREFIX}-textCenter`,
  header: `${PREFIX}-header`,
  commandButton: `${PREFIX}-commandButton`,
};

// Global mechanic color map so colors are consistent across views/components
const __mechanicColorMap = new Map();
const __mechanicColorPalette = [
  "#42a5f5", "#66bb6a", "#ffca28", "#ab47bc", "#ef5350",
  "#26a69a", "#8d6e63", "#5c6bc0", "#ec407a", "#ffa726"
];
const getMechanicColor = (id) => {
  if (!id) return undefined;
  if (!__mechanicColorMap.has(id)) {
    __mechanicColorMap.set(id, __mechanicColorPalette[__mechanicColorMap.size % __mechanicColorPalette.length]);
  }
  return __mechanicColorMap.get(id);
};
// Parse UTC datetime string from backend (e.g., "2025-11-21 21:00:00") as UTC
// Backend stores all times in UTC, so we must parse them as UTC, not local time
const parseUTCDateTime = (utcString) => {
  if (!utcString) return new Date('Invalid');
  if (utcString instanceof Date) return utcString;
  if (typeof utcString === 'string') {
    // Append 'Z' to indicate UTC if not already present
    const s = utcString.includes('T') ? utcString : utcString.replace(' ', 'T');
    const withZ = s.endsWith('Z') ? s : s + 'Z';
    return new Date(withZ);
  }
  return new Date(utcString);
};

// Format a Date object as UTC datetime string for sending to backend
// Both tasks and availability blocks are stored in UTC
const formatDateTimeUTC = (datetime) => {
  if (!datetime) return null;
  const d = new Date(datetime);
  if (isNaN(d.getTime())) return null;

  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const hours = String(d.getUTCHours()).padStart(2, '0');
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  const seconds = String(d.getUTCSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};


const getVisibleAppointments = (appointments, maxTasksPerSlot = 2, expandedDate = null) => {
    const slotMap = {};
    const visible = [];

    appointments.forEach((t) => {
      const start = new Date(t.startDate);
      const end = new Date(t.endDate);
      const taskDate = start.toDateString();

      // Availability blocks should always be visible (they're background elements)
      const isAvailabilityBlock = t?.mech_status === 'Available';
      // Leave blocks should only be visible if approved
      const isApprovedLeaveBlock = t?.mech_status === 'Requested off' && t?.status === 'approved';
      const isBackgroundBlock = isAvailabilityBlock || isApprovedLeaveBlock;
      if (isBackgroundBlock) {
        if (!visible.some(v => v.id === t.id)) visible.push(t);
        return;
      }

      // If user expanded this date — show all tasks for it
      if (expandedDate && taskDate === new Date(expandedDate).toDateString()) {
        if (!visible.includes(t)) visible.push(t);
        return;
      }

      // Otherwise enforce per-slot limit for tasks only (not background blocks)
      const durationSlots = Math.ceil((end - start) / (1000 * 60 * 30)); // half-hour slots
      for (let i = 0; i < durationSlots; i++) {
        const slotTime = new Date(start.getTime() + i * 30 * 60 * 1000);
        const slotKey = `${slotTime.toDateString()}-${slotTime.getHours()}-${slotTime.getMinutes() < 30 ? "00" : "30"}`;

        if (!slotMap[slotKey]) slotMap[slotKey] = [];

        // Each slot covers a 30-minute window
        const slotStart = new Date(slotTime);
        const slotEnd = new Date(slotTime.getTime() + 30 * 60 * 1000);

        // ✅ Check if the task overlaps any part of this slot
        const isWithinRange =
          (start < slotEnd && end > slotStart);


        if (isWithinRange) {
          if (!slotMap[slotKey]) slotMap[slotKey] = [];

          if (slotMap[slotKey].length < maxTasksPerSlot) {
            slotMap[slotKey].push(t);
            if (!visible.some(v => v.id === t.id)) visible.push(t);
          }

          break; // ✅ Stop after first matching slot
        }

      }
    });

    return visible;
  };


// const CustomDayScaleCell = ({ startDate, today, ...restProps }) => {
//   const fmt = d => d.toLocaleDateString("en-CA");
//   const isExpanded = expandedDate && fmt(startDate) === fmt(expandedDate);

//   const weekday = startDate
//     .toLocaleDateString("en-US", { weekday: "short" })
//     .toUpperCase();  // MON
//   const dayNum = startDate.getDate(); // 14

//   return (
//     <th
//       {...restProps}
//       style={{
//         backgroundColor: isExpanded ? "#d1ecf1" : today ? "#d9d9d9" : undefined,
//         width: isExpanded ? "480px" : "200px",
//         minWidth: isExpanded ? "480px" : "200px",
//         textAlign: "center",
//         padding: "4px 2px",
//         borderBottom: "1px solid #ccc",
//         color: "#333",
//       }}
//     >
//       <div style={{
//         display: "flex",
//         flexDirection: "column",
//         alignItems: "center",
//         lineHeight: "1.1",
//       }}>
//         <span style={{ fontSize: "10px", fontWeight: 700 }}>
//           {weekday}
//         </span>
//         <span style={{ fontSize: "20px", fontWeight: 700, marginTop: "2px" }}>
//           {dayNum}
//         </span>
//       </div>
//     </th>
//   );
// };


// const CustomTimeTableCell = ({ onCellClick, startDate, endDate, computeMeta, onShowOverflow, ...restProps }) => {
//   // eslint-disable-next-line no-unused-vars
//   const handleMouseDown = (event) => {
//     event.stopPropagation();
//     onCellClick({ start: startDate });
//   };
//   // eslint-disable-next-line no-unused-vars
//   const handleMouseUp = (event) => {
//     onCellClick({ end: startDate });
//   };

//   // computeMeta and overflow handlers are provided by parent component

//   const meta = computeMeta ? computeMeta(startDate, endDate) : null;
//   const maxTasksInline = 2;
//   const overflow = meta && meta.tasksInCell && meta.tasksInCell.length > maxTasksInline ? (meta.tasksInCell.length - maxTasksInline) : 0;

//   const handleCreateClick = (e) => {
//     try {
//       if (typeof onCreate === 'function') {
//         // Only allow single-click create when availability is present in this cell
//         if (!meta || meta.availableCount > 0) {
//           e.stopPropagation();
//           onCreate(startDate);
//         }
//       }
//     } catch (_) { /* noop */ }
//   };

//   return (
//     <WeekView.TimeTableCell
//       {...restProps}
//       style={{
//         position: "relative",
//         cursor: (meta && meta.availableCount > 0 && typeof onCreate === 'function') ? 'pointer' : undefined,
//         // Red tint border for unassigned tasks
//         ...(meta && meta.hasUnassigned ? {
//           boxShadow: 'inset 0 0 0 2px rgba(229, 57, 53, 0.6)',
//           backgroundColor: 'rgba(229, 57, 53, 0.05)'
//         } : {})
//       }}
//       onMouseDown={handleMouseDown}
//       onMouseUp={handleMouseUp}
//       onClick={handleCreateClick}
//       onDoubleClick={handleCreateClick}
//     >
//       {meta && (
//         <>
          
//           {/* Overflow indicator */}
//           {overflow > 0 && (
//             <div
//             onMouseDownCapture={(e) => {
//               e.stopPropagation(); 
//             }}
//               // onClick={(e) => {e.stopPropagation();e.preventDefault(); onShowOverflow && onShowOverflow(e, meta.tasksInCell)}}
//               onClick={(e) => {
//                   e.stopPropagation();
//                   e.preventDefault();

//                   setExpandedDate((prev) =>
//                     prev && new Date(prev).toDateString() === new Date(startDate).toDateString()
//                       ? null // collapse if already expanded
//                       : startDate // expand this date
//                   );
//                 }}
//               style={{
//                 position: "absolute",
//                 bottom: 2,
//                 right: 6,
//                 padding: "0 6px",
//                 borderRadius: 10,
//                 background: "rgba(0,0,0,0.5)",
//                 color: "white",
//                 fontSize: 11,
//                 cursor: "pointer",
//                 zIndex: 5,
//               }}
//               title={`+${overflow} more tasks`}
//             >
//               +{overflow}
//             </div>
//           )}
//           {/* Availability bubble - only show if count > 0 */}
//           {meta.availableCount > 0 && (
//             <div
//               style={{
//                 position: "absolute",
//                 top: 4,
//                 // left: 4,
//                 right:5,
//                 width: 22,
//                 height: 22,
//                 borderRadius: 11,
//                 background: "#1976d2",
//                 color: "white",
//                 display: "flex",
//                 alignItems: "center",
//                 justifyContent: "center",
//                 fontSize: 12,
//                 zIndex: 4,
//                 opacity: 0.9,
//                 pointerEvents: "none",
//               }}
//               title={`Available mechanics: ${meta.availableCount}`}
//             >
//               {meta.availableCount}
//             </div>
//           )}

//         </>
//       )}
//     </WeekView.TimeTableCell>
//   );
// };

// eslint-disable-next-line no-unused-vars -- Usage is commented out at line ~1171
const ExternalViewSwitcher = ({ currentViewName }) => (
  <RadioGroup
    aria-label="Views"
    style={{ flexDirection: "column" }}
    name="views"
    variant="contained"
    value={currentViewName}
    // onClick={() => handleViewChange}
    // onChange={onChange}
  >
    <FormControlLabel value="Day" control={<Radio />} label="Day" />
    <FormControlLabel value="Week" control={<Radio />} label="Week" />
  </RadioGroup>
);

function mergeAvailability(mechSlots) {
  if (!Array.isArray(mechSlots) || mechSlots.length === 0) {
    return [];
  }

  // If there's only a single slot, just return it as a single merged block
  if (mechSlots.length === 1) {
    const slot = mechSlots[0];
    return [{
      ...slot,
      slot_id: slot.slot_id ?? 1,
    }];
  }

  // Ensure a stable sort by mechanic, date, then start time so merging
  // logic is deterministic regardless of backend ordering.
  const sorted = [...mechSlots].sort((a, b) => {
    if (a.mechanic_id !== b.mechanic_id) {
      return (a.mechanic_id || 0) - (b.mechanic_id || 0);
    }
    if (a.date !== b.date) {
      return String(a.date || '').localeCompare(String(b.date || ''));
    }
    return String(a.start_date_time || '').localeCompare(String(b.start_date_time || ''));
  });

  const mergedSlots = [];
  let artificial_id = 1;

  let current = {
    ...sorted[0],
    slot_id: sorted[0].slot_id ?? artificial_id,
  };

  for (let i = 1; i < sorted.length; i++) {
    const slot = sorted[i];

    const canMerge =
      slot.mechanic_id === current.mechanic_id &&
      slot.date === current.date &&
      slot.mech_status === current.mech_status &&
      slot.task_id === current.task_id &&
      slot.start_date_time === current.end_date_time;

    if (canMerge) {
      // Extend the current merged window
      current = {
        ...current,
        end_date_time: slot.end_date_time,
      };
      continue;
    }

    // Push the finished block and start a new one
    mergedSlots.push({ ...current, slot_id: artificial_id });
    artificial_id += 1;

    current = {
      ...slot,
      slot_id: slot.slot_id ?? artificial_id,
    };
  }

  // Push the final accumulated slot
  mergedSlots.push({ ...current, slot_id: artificial_id });

  return mergedSlots;
}

const toDayjs = (value) => {
  if (dayjs.isDayjs(value)) {
    return value;
  }
  if (value instanceof Date) {
    return dayjs(value);
  }
  return dayjs(value);
};

const HiddenAppointment = () => null;

const getAppointmentStatus = (appointment) => {
  const status = appointment?.status ?? appointment?.mech_status ?? "";
  return typeof status === "string" ? status.toLowerCase() : "";
};

const isAvailabilityEntry = (appointment) => {
  const status = getAppointmentStatus(appointment);
  if (!status) {
    return false;
  }
  return status.includes("available");
};

const createMonthCellRenderer = ({ getAppointments, onItemClick }) => {
  const MonthCellComponent = (props) => {
    const { startDate, className, ...restProps } = props;
    if (!startDate) {
      return <MonthView.TimeTableCell {...props} />;
    }
    const currentDay = toDayjs(startDate);
    const currentDate = currentDay.toDate().getDate();
    if (!currentDay.isValid()) {
      return <MonthView.TimeTableCell {...props} />;
    }

    // console.log(`Appointments for ${currentDay.format("YYYY-MM-DD")}:`, getAppointments());
    // const appointments = (getAppointments() || [])
    //   .filter((appointment) => {
    //     if (!appointment?.startDate) {
    //       console.warn("Appointment missing startDate", appointment);
    //       return false;
    //     }
    //     const appointmentStart = toDayjs(appointment.startDate);
    //     return appointmentStart.isValid() && appointmentStart.isSame(currentDay, "day");
    //   })
    //   .sort((a, b) => toDayjs(a.startDate).valueOf() - toDayjs(b.startDate).valueOf());

    const appointments = (getAppointments() || [])
      .filter((appointment) => {
        if (!appointment?.startDate) {
          console.warn("Appointment missing startDate", appointment);
          return false;
        }
        const appointmentStart = toDayjs(appointment.startDate);
        return appointmentStart.isValid() && appointmentStart.isSame(currentDay, "day");
      })
      .sort((a, b) => toDayjs(a.startDate).valueOf() - toDayjs(b.startDate).valueOf());

    const totals = appointments.reduce(
      (acc, appointment) => {
        // Skip availability blocks and vacation/leave blocks from counts
        if (isAvailabilityEntry(appointment)) {
          return acc;
        }
        // Skip vacation/leave blocks (mech_status === 'Requested off')
        if (appointment?.mech_status === 'Requested off') {
          return acc;
        }
        // Skip pending bookings - don't show in month view
        if (appointment?.isPending) {
          return acc;
        }
        // Only count confirmed tasks - check mechanic_id to determine if unassigned
        if (!appointment.mechanic_id && !appointment.mechanic) {
          acc.unassigned += 1;
        } else {
          acc.assigned += 1;
        }
        return acc;
      },
      { assigned: 0, unassigned: 0 }
    );

    const handleCellSelection = () => {
      if (typeof onItemClick === "function") {
        onItemClick({ startDate: currentDay.toDate() });
      }
    };

    const isToday = currentDay.isSame(dayjs(), "day");

    return (
      <TableCell
        {...restProps}
        className={`${className ?? ""} month-view__cell`}
        tabIndex={0}
        onClick={handleCellSelection}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleCellSelection();
          }
        }}
      >
        <div className={`month-cell ${isToday ? "month-cell--today" : ""}`}>
          <div className="month-cell__date">{currentDate}</div>
          {(totals.assigned > 0 || totals.unassigned > 0) ? (
            <div className="month-cell__stats">
              {totals.assigned > 0 && (
                <div className="month-cell__stat month-cell__stat--assigned">
                  <span className="month-cell__stat-value">{totals.assigned}</span>
                  <span className="month-cell__stat-label">Assigned</span>
                </div>
              )}
              {totals.unassigned > 0 && (
                <div className="month-cell__stat month-cell__stat--unassigned">
                  <span className="month-cell__stat-value">{totals.unassigned}</span>
                  <span className="month-cell__stat-label">Unassigned</span>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </TableCell>
    );
  };

  MonthCellComponent.displayName = "SchedulerMonthCell";
  return MonthCellComponent;
};


// function secondsToHms(seconds) {
//   var h = Math.floor(seconds / 3600);
//   var m = Math.floor((seconds % 3600) / 60);
//   var s = Math.floor((seconds % 3600) % 60);

//   var hDisplay = h < 10 ? `0${h}:` : `${h}:`;
//   var mDisplay = m < 10 ? `0${m}:` : `${m}:`;
//   var sDisplay = s < 10 ? `0${s}` : `${s}`;
//   return hDisplay + mDisplay + sDisplay;
// }

// function hmsToSeconds(hms) {
//   let hours = Number(hms.substring(0, 2));
//   let minutes = Number(hms.substring(3, 5));

//   let seconds = hours * 3600;
//   seconds += minutes * 60;

//   return seconds;
// }

// Shared utility function for both MechanicScheduler and DefaultScheduler
  const overlaps = (aStart, aEnd, bStart, bEnd) => {
    const sA = aStart instanceof Date ? aStart : new Date(aStart);
    const eA = aEnd instanceof Date ? aEnd : new Date(aEnd);
    const sB = bStart instanceof Date ? bStart : new Date(bStart);
    const eB = bEnd instanceof Date ? bEnd : new Date(bEnd);
    return sA < eB && eA > sB;
  };

  const DraftAppointment = ({ data, style, ...restProps }) => {
    const isUnassigned = !data?.mechanic_id;
    const mechIdForColor = data?.mechanic_id ?? data?.mechanic ?? data?.user_id;
    const backgroundColor = isUnassigned ? '#FF5722' : (getMechanicColor(mechIdForColor) || '#999');

    return (
      <div
        {...restProps}
        style={{
          ...style,
          backgroundColor: backgroundColor,
          borderRadius: '4px',
          border: '2px solid white',
          opacity: 0.8,
          color: 'white',
          padding: '4px 8px',
          fontSize: '12px',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          cursor: 'move'
        }}
      >
        {data?.service_info || 'Task'}
      </div>
    );
    };

  // const computeCellMeta = useCallback((cellStart, cellEnd) => {
  //   try {
  //     const availMechIds = new Set(
  //       (availability || [])
  //         .filter(a => a.mech_status === 'Available' && overlaps(a.startDate, a.endDate, cellStart, cellEnd))
  //         .map(a => a.mechanic_id)
  //     );
  //     // Filter appointments for this specific mechanic
  //     const tasksInCell = (appointments || []).filter(t => overlaps(t.startDate, t.endDate, cellStart, cellEnd));
  //     // Check if this mechanic is assigned
  //     const isAssigned = tasksInCell.some(t => (t.mechanic_id ?? t.mechanic) === mech_id);
  //     // For mechanic view, show 1 if available and not assigned, 0 otherwise
  //     const availableCount = availMechIds.has(mech_id) && !isAssigned ? 1 : 0;
  //     const hasUnassigned = false; // Not relevant in single-mechanic view
  //     return { availableCount, hasUnassigned, tasksInCell };
  //   } catch (e) {
  //     return { availableCount: 0, hasUnassigned: false, tasksInCell: [] };
  //   }
  // }, [availability, appointments, mech_id]);

  // const [overflowAnchor, setOverflowAnchor] = useState(null);
  // const [overflowTasks, setOverflowTasks] = useState([]);
  // const onShowOverflow = (anchorEl, tasks) => { setOverflowAnchor(anchorEl); setOverflowTasks(tasks || []); };
  // const closeOverflow = () => setOverflowAnchor(null);

  // const jumpToDayView = useCallback(
  //   (rawDate) => {
  //     const targetDate = toDayjs(rawDate);
  //     if (!targetDate.isValid()) {
  //       return;
  //     }
  //     onExternalDateChange(targetDate);
  //     setCurrentDate(targetDate);
  //     setCurrentViewName("Day");
  //   },
  //   [onExternalDateChange, setCurrentDate, setCurrentViewName]
  // );

  // const resolvedMonthAppointments = useMemo(
  //   () => appointments ?? [],
  //   [appointments]
  // );

  // const mechanicMonthCellComponent = useMemo(
  //   () =>
  //     createMonthCellRenderer({
  //       getAppointments: () => resolvedMonthAppointments,
  //       onItemClick: (selection) => jumpToDayView(selection.startDate),
  //     }),
  //   [resolvedMonthAppointments, jumpToDayView]
  // );

  // const schedulerData = currentViewName === "Month" ? resolvedMonthAppointments : appointments;

  // return (
  //   <div>
  //     <div
  //       // onDrop={handleDrop}
  //       // onDragOver={allowDrop}
  //       style={{
  //         height: "85vh", // Adjust the height as needed
  //         overflowY: "auto", // Enable vertical scrolling if content overflows
  //       }}
  //     >
  //       <Scheduler data={schedulerData}>
  //         <div>
  //           <div>
  //             <ViewState
  //               currentDate={currentDate.$d}
  //               onCurrentDateChange={handleCurrDateChange}
  //               currentViewName={currentViewName}
  //               data={schedulerData}
  //               position="top"
  //             />
  //           </div>
  //         </div>
  //         <EditingState
  //           onCommitChanges={onCommitChanges}
  //           addedAppointment={addedAppointment}
  //           onAddedAppointmentChange={onAddedAppointmentChange}
  //         />
  //         <IntegratedEditing />
  //         {currentViewName === "Day" && (
  //           <DayView
  //             name="Day"
  //             // startDayHour={6}
  //             // endDayHour={19.5}
  //             timeTableCellComponent={(props) => (
  //               <CustomTimeTableCell
  //                 {...props}
  //                 onCellClick={handleCellClick}
  //                 computeMeta={computeCellMeta}
  //                 onShowOverflow={onShowOverflow}
  //               />
  //             )}
  //           />
  //         )}
  //         {currentViewName === "Week" && (
  //           <WeekView
  //             name="Week"
  //             excludedDays={[]}
  //             timeTableCellComponent={(props) => (
  //               <CustomTimeTableCell
  //                 {...props}
  //                 onCellClick={handleCellClick}
  //                 computeMeta={computeCellMeta}
  //                 onShowOverflow={onShowOverflow}
  //               />
  //             )}
  //           />
  //         )}
  //         <MonthView name="Month" timeTableCellComponent={mechanicMonthCellComponent} />
  //         {/* <Toolbar /> */}
  //         <Appointments appointmentComponent={currentViewName === "Month" ? HiddenAppointment : Appointment} />
  //         {currentViewName !== "Month" && <DragDropProvider allowDrag={allowDrag} allowResize={allowResize} />}
  //       </Scheduler>
  //       <Popover
  //         open={Boolean(overflowAnchor)}
  //         anchorEl={overflowAnchor}
  //         onClose={closeOverflow}
  //         anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
  //         transformOrigin={{ vertical: 'top', horizontal: 'right' }}
  //       >
  //         <List dense sx={{ maxWidth: 320 }}>
  //           {overflowTasks.map((t, idx) => (
  //             <ListItem key={idx}>
  //               <ListItemText
  //                 primary={`${t.title || t.service_info || 'Task'}${(t.mechanic_id || t.mechanic) ? '' : ' (Unassigned)'}`}
  //                 secondary={`${new Date(t.startDate).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})} - ${new Date(t.endDate).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}`}
  //               />
  //             </ListItem>
  //           ))}
  //         </List>
  //       </Popover>
  //     </div>
  //   </div>
  // );

function DefaultScheduler({
    // appointments,
    // setAppointments,
    // mechData,
    currentDate,
    setCurrentDate,
    onExternalDateChange,
    currentViewName,
    // pendingBooking,
    // setpendingBooking,
    // onViewChange,
    setCurrentViewName,
    mechanics,
    refreshTrigger,
    selectedLocationId = null,
    focusedMechanicId = null,
    onExitFocus,
}) {
    // const [/* isCustomDayView */, setIsCustomDayView] = useState(false);

    // currentDate = currentDate.$d;
    const taskIdCounterRef = React.useRef(1);
    // Mechanic color mapping provided by module-scoped getMechanicColor
    // const [calendarData, setCalendarData] = useState([]);
    const [pendingBooking, setPendingBooking] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [mechDate, setMechData] = useState([])
    const [availability, _setAvailability] = useState([]);
    const [expandedDate, setExpandedDate] = useState(null);
    // Freeze display data during refresh to avoid flicker/empties on UI while waiting for fetch
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [frozenAppointments, setFrozenAppointments] = useState(null);
    const [frozenAvailability, setFrozenAvailability] = useState(null);

    // Task sidebar state
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarTask, setSidebarTask] = useState(null);
    const [sidebarMode, setSidebarMode] = useState('create'); // 'create' or 'edit'

    // Dialog state for availability/leave blocks
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedBlock, setSelectedBlock] = useState(null);
    const [taskConflictDialogOpen, setTaskConflictDialogOpen] = useState(false);
    const [taskConflictData, setTaskConflictData] = useState(null);

    // Snackbar for toast messages
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    // Filter toggle for unassigned tasks column
    const [filterUnassignedByDate, setFilterUnassignedByDate] = useState(false);

    // Customer lookup state for create task dialog
    const [sidebarCustomerEmail, setSidebarCustomerEmail] = useState('');
    const [sidebarCustomerFound, setSidebarCustomerFound] = useState(null);
    const [sidebarCustomerVehicles, setSidebarCustomerVehicles] = useState([]);
    const [sidebarLookupLoading, setSidebarLookupLoading] = useState(false);
    const [sidebarIsExistingCustomer, setSidebarIsExistingCustomer] = useState(false); // Track if customer exists in system (read-only if true)
    const [sidebarChangeCustomerOpen, setSidebarChangeCustomerOpen] = useState(false); // Change Customer dialog
    const [sidebarNewCustomerEmail, setSidebarNewCustomerEmail] = useState(''); // Email input for Change Customer dialog
    const [sidebarAddVehicleOpen, setSidebarAddVehicleOpen] = useState(false);
    const [sidebarEditVehicleOpen, setSidebarEditVehicleOpen] = useState(false);
    const [sidebarNewVehicle, setSidebarNewVehicle] = useState({ car_name: '', make: '', model: '', year: '', submodel: '', vin_number: '' });
    const [sidebarEditVehicle, setSidebarEditVehicle] = useState({ user_car_id: null, car_name: '', make: '', model: '', year: '', submodel: '', vin_number: '' });

    // Mechanic selection dialog
    const [mechanicSelectOpen, setMechanicSelectOpen] = useState(false);
    // Track last successfully updated task to diagnose/rehydrate on refresh
    const lastTaskOpRef = React.useRef(null);
    const [pendingTaskDrop, setPendingTaskDrop] = useState(null);

    // Ref for auto-scrolling to 9am
    const schedulerContainerRef = React.useRef(null);
    // Store scroll position when switching views
    const [savedScrollPosition, setSavedScrollPosition] = React.useState(null);
    // Track if this is the initial load
    const isInitialLoadRef = React.useRef(true);

    // Unassigned tasks - filter and sort
    // Only show confirmed appointments (not pending bookings)
    const allUnassignedTasks = (appointments || [])
      .filter(task => !task.mechanic_id && !task.mechanic)
      .sort((a, b) => {
        const dateA = new Date(a.appointment_time || a.startDate);
        const dateB = new Date(b.appointment_time || b.startDate);
        return dateA - dateB;
      });

    // Filter unassigned tasks by selected date if toggle is on
    const unassignedTasks = filterUnassignedByDate
      ? allUnassignedTasks.filter(task => {
          const taskDate = parseUTCDateTime(task.startDate || task.appointment_time);
          if (!taskDate) return false;
          const selectedDate = new Date(currentDate);
          // Compare year, month, and day
          return taskDate.getFullYear() === selectedDate.getFullYear() &&
                 taskDate.getMonth() === selectedDate.getMonth() &&
                 taskDate.getDate() === selectedDate.getDate();
        })
      : allUnassignedTasks;

    // Assigned tasks only - for displaying in the scheduler
    // Unassigned tasks should only show in the unassigned column
    const effectiveAppointments = (isRefreshing && frozenAppointments) ? frozenAppointments : appointments;
    const effectiveAvailability = (isRefreshing && frozenAvailability) ? frozenAvailability : availability;

    const assignedAppointments = (effectiveAppointments || [])
      .filter(task => task.mechanic_id || task.mechanic);

    const schedulerData = useMemo(() => {
      let nextSchedulerData = currentViewName === 'Day'
        ? [...assignedAppointments, ...(effectiveAvailability || [])]
        : assignedAppointments;

      if (focusedMechanicId && currentViewName === 'Week') {
        nextSchedulerData = nextSchedulerData.filter(item => {
          if (item.mechanic_id === focusedMechanicId || item.mechanic === focusedMechanicId) return true;
          if ((item.mech_status === 'Available' || item.mech_status === 'Requested off') && item.mechanic_id === focusedMechanicId) return true;
          if (!item.mechanic_id && !item.mechanic && item.mech_status !== 'Available' && item.mech_status !== 'Requested off') return true;
          return false;
        });
      }

      return nextSchedulerData;
    }, [assignedAppointments, currentViewName, effectiveAvailability, focusedMechanicId]);

    const displayMechanics = useMemo(() => {
      if (focusedMechanicId && currentViewName === 'Week') {
        const filteredMechanics = mechanics.filter(m => m.user_id === focusedMechanicId);
        if (DEBUG) console.log('[Calendar][filtered] focusedMechanicId:', focusedMechanicId, 'schedulerData:', schedulerData.length, 'displayMechanics:', filteredMechanics.length);
        return filteredMechanics;
      }
      return mechanics;
    }, [currentViewName, focusedMechanicId, mechanics, schedulerData.length]);

    try {
      const missingMechanic = (assignedAppointments || []).filter(t => !t.mechanic_id).length;
    if (DEBUG) console.log('[Calendar][schedulerData] assigned:', assignedAppointments.length, 'day?', currentViewName === 'Day', 'withAvailability:', (effectiveAvailability||[]).length, 'missing mechanic_id:', missingMechanic);
    } catch (_) { /* noop */ }

    // Open the Task Details modal in create mode with a 30-minute default duration
    const openCreateTaskAt = useCallback((cellStart) => {
      try {
        const s = normalizeToMinuteAndSnap(cellStart, 2) || new Date(cellStart);
        s.setSeconds(0, 0);
        const e = new Date(s.getTime() + 30 * 60 * 1000);
        setSidebarTask({
          service_info: '',
          status: 'Pending',
          mechanic_id: '',
          startDate: s,
          endDate: e,
          user_car_id: null,
        });
        // Reset customer lookup state
        setSidebarCustomerEmail('');
        setSidebarCustomerFound(null);
        setSidebarCustomerVehicles([]);
        setSidebarIsExistingCustomer(false); // New task - no customer yet
        setSidebarMode('create');
        setSidebarOpen(true);
      } catch (err) {
        if (DEBUG) console.error('[Calendar][openCreateTaskAt] error', err);
      }
    }, []);

    const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
    const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);

    // Listen for toolbar requests from parent Demo component
    useEffect(() => {
      const onNewTask = (e) => {
        const base = e?.detail?.baseTime || new Date();
        openCreateTaskAt(base);
      };
      const onOpenAvail = () => setAvailabilityDialogOpen(true);
      const onOpenLeave = () => setLeaveDialogOpen(true);
      const onToast = (e) => {
        const { message, severity = 'info' } = e?.detail || {};
        if (!message) return;
        setSnackbar({ open: true, message, severity });
      };
      window.addEventListener('calendar:new-task', onNewTask);
      window.addEventListener('calendar:open-availability-dialog', onOpenAvail);
      window.addEventListener('calendar:open-leave-dialog', onOpenLeave);
      window.addEventListener('calendar:toast', onToast);
      return () => {
        window.removeEventListener('calendar:new-task', onNewTask);
        window.removeEventListener('calendar:open-availability-dialog', onOpenAvail);
        window.removeEventListener('calendar:open-leave-dialog', onOpenLeave);
        window.removeEventListener('calendar:toast', onToast);
      };
    }, [openCreateTaskAt]);

      useEffect (()=>{
        const SC_Id = localStorage.getItem("SC_Id");

        const url = `${envVariable.serverId}/SCO/Mechanic.php?SC_Id=${SC_Id}`;
        fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`); // Throw an error on bad HTTP status
            }
            return response.json();
          })
          .then((data) => {
            if (!data || !data.mechanics || data.mechanics.length === 0) {
    if (DEBUG) console.log("No mechanic data available.");
              setMechData([]); // Setting to empty array or handling accordingly
              return;
            }
    if (DEBUG) console.log("Data:", data.mechanics);
            setMechData(data.mechanics);
          })
          .catch((error) => {
            console.error("Error fetching mechanics:", error);
            setMechData([]); // Handling error by setting mechanics data to empty array
          });
      },[])

    // Customer lookup by email for create task dialog (debounced)
    useEffect(() => {
      // Only lookup when sidebar is open in create mode
      if (!sidebarOpen || sidebarMode !== 'create') return;

      const email = sidebarCustomerEmail.trim();
      if (!email || email.length < 5 || !email.includes('@')) {
        setSidebarCustomerFound(null);
        setSidebarCustomerVehicles([]);
        return;
      }

      setSidebarLookupLoading(true);
      const timer = setTimeout(async () => {
        try {
          const response = await fetch(
            `${envVariable.serverId}/SCO/lookupCustomerByEmail.php?email=${encodeURIComponent(email)}`,
            { credentials: 'include' }
          );
          const data = await response.json();

          // Close any open vehicle edit/add forms when customer changes
          setSidebarEditVehicleOpen(false);
          setSidebarAddVehicleOpen(false);

          if (data.type === 'success' && data.found) {
            setSidebarCustomerFound(data.customer);
            setSidebarCustomerVehicles(data.vehicles || []);
            setSidebarIsExistingCustomer(true); // Customer exists in system - read-only
            // Auto-select first vehicle if available
            if (data.vehicles && data.vehicles.length > 0) {
              setSidebarTask(prev => ({ ...prev, user_car_id: data.vehicles[0].user_car_id }));
            } else {
              setSidebarTask(prev => ({ ...prev, user_car_id: null }));
            }
          } else {
            setSidebarCustomerFound(null);
            setSidebarCustomerVehicles([]);
            setSidebarIsExistingCustomer(false); // New customer - editable
            setSidebarTask(prev => ({ ...prev, user_car_id: null }));
          }
        } catch (error) {
          console.error('Customer lookup error:', error);
          setSidebarCustomerFound(null);
          setSidebarCustomerVehicles([]);
          setSidebarIsExistingCustomer(false);
        } finally {
          setSidebarLookupLoading(false);
        }
      }, 500);

      return () => clearTimeout(timer);
    }, [sidebarCustomerEmail, sidebarOpen, sidebarMode])

    // Load customer vehicles when edit mode sidebar opens (preserves existing user_car_id)
    useEffect(() => {
      if (!sidebarOpen || sidebarMode !== 'edit' || !sidebarTask) return;

      const customerId = sidebarTask.customer_id || sidebarTask.customer_user_id;

      // If task has a customer, fetch their vehicles (but preserve the task's user_car_id)
      if (customerId) {
        setSidebarLookupLoading(true);
        fetch(
          `${envVariable.serverId}/SCO/lookupCustomerByEmail.php?customer_id=${customerId}`,
          { credentials: 'include' }
        )
          .then(response => response.json())
          .then(data => {
            if (data.type === 'success' && data.found) {
              setSidebarCustomerFound(data.customer);
              setSidebarCustomerVehicles(data.vehicles || []);
              setSidebarIsExistingCustomer(true);
              // DON'T reset user_car_id - preserve the task's existing vehicle selection
            } else {
              setSidebarCustomerFound(null);
              setSidebarCustomerVehicles([]);
            }
          })
          .catch(error => {
            console.error('Error fetching customer vehicles for edit:', error);
            setSidebarCustomerFound(null);
            setSidebarCustomerVehicles([]);
          })
          .finally(() => {
            setSidebarLookupLoading(false);
          });
      } else {
        // No customer linked - clear vehicles
        setSidebarCustomerFound(null);
        setSidebarCustomerVehicles([]);
        setSidebarIsExistingCustomer(false);
      }
    // Only run when sidebar opens in edit mode, not on every sidebarTask change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sidebarOpen, sidebarMode])

    // Use overlaps function from parent scope

      const computeCellMeta = useCallback((cellStart, cellEnd) => {
        try {
          const availMechIds = new Set(
            (availability || [])
              .filter(a => a.mech_status === 'Available' && overlaps(a.startDate, a.endDate, cellStart, cellEnd))
              .map(a => a.mechanic_id)
          );
          // Consider both accepted tasks and booking requests for the cell contents
          const tasksAll = [
            ...((appointments || [])),
            ...((pendingBooking || [])),
          ];
          const tasksInCell = tasksAll.filter(t => overlaps(t.startDate, t.endDate, cellStart, cellEnd));
          const assigned = new Set(tasksInCell.map(t => (t.mechanic_id ?? t.mechanic)).filter(Boolean));
          let availableCount = 0;
          availMechIds.forEach(id => { if (!assigned.has(id)) availableCount++; });
          // If any task (accepted or pending) lacks a mechanic, flag the cell
          const hasUnassigned = tasksInCell.some(t => !(t.mechanic_id ?? t.mechanic));
          return { availableCount, hasUnassigned, tasksInCell };
        } catch (e) {
          return { availableCount: 0, hasUnassigned: false, tasksInCell: [] };
        }
      }, [availability, appointments, pendingBooking]);

      // const [overflowAnchor, setOverflowAnchor] = useState(null);
      // const [overflowTasks, setOverflowTasks] = useState([]);
      // const [popoverPosition, setPopoverPosition] = useState(null);
      // const onShowOverflow = (e, tasks) => {
      //     const rect = e.currentTarget.getBoundingClientRect(); // absolute position of +N bubble
      //     setPopoverPosition({
      //       top: rect.bottom + window.scrollY, // below the element
      //       left: rect.left + rect.width / 2 + window.scrollX, // centered horizontally
      //     });
      //     // setOverflowAnchor(null); // we'll use absolute coords instead
      //     setOverflowTasks(tasks || []);
      //   };
      // const closeOverflow = () => setOverflowAnchor(null);

    // Save scroll position before view change using useEffect cleanup
    useEffect(() => {
      const containerRef = schedulerContainerRef.current;
      // Cleanup function runs before the effect runs again (when currentViewName changes)
      return () => {
        if (containerRef) {
          const scrollContainer = containerRef.querySelector('[class*="Layout"]')
            || containerRef.querySelector('.MuiPaper-root')
            || containerRef;

          if (scrollContainer && !isInitialLoadRef.current) {
            const position = scrollContainer.scrollTop;
    if (DEBUG) console.log('[Calendar] Saving scroll position before view change:', position);
            setSavedScrollPosition(position);
          }
        }
      };
    }, [currentViewName]);

    // Auto-scroll logic: scroll to 11am on initial load, restore position on view change
    useEffect(() => {
      const handleScroll = () => {
        // Day view needs slightly more time to render than week view
        const delay = currentViewName === 'Day' ? 300 : 150;

        setTimeout(() => {
          if (schedulerContainerRef.current) {
            // Find the actual scrollable container
            // For week view, it's usually [class*="Layout"]
            // For day view, we need to find the element with scrollHeight > clientHeight
            const findScrollableContainer = (element) => {
              if (!element) return null;

              // In Day view, prefer the explicit scroll wrapper used by the mechanic day view
              if (currentViewName === 'Day') {
                const dayWrapper = element.querySelector('.scheduler-day-scroll');
                if (dayWrapper && dayWrapper.scrollHeight > dayWrapper.clientHeight) {
                  return dayWrapper;
                }
              }

              // Check if this element is scrollable
              if (element.scrollHeight > element.clientHeight) {
                return element;
              }

              // Try common scroll container selectors
              const candidates = [
                element.querySelector('[class*="Layout"]'),
                element.querySelector('.MuiPaper-root'),
                element
              ];

              for (const candidate of candidates) {
                if (candidate && candidate.scrollHeight > candidate.clientHeight) {
                  return candidate;
                }
              }

              // Walk up the DOM tree to find a scrollable parent
              let parent = element.parentElement;
              while (parent && parent !== document.body) {
                if (parent.scrollHeight > parent.clientHeight) {
                  return parent;
                }
                parent = parent.parentElement;
              }

              // Fallback to the original logic
              return element.querySelector('[class*="Layout"]')
                || element.querySelector('.MuiPaper-root')
                || element;
            };

            const scrollContainer = findScrollableContainer(schedulerContainerRef.current);

    if (DEBUG) console.log('[Calendar] Scroll container found:', !!scrollContainer, 'View:', currentViewName);

            if (scrollContainer) {
              // Use requestAnimationFrame to ensure DOM is fully rendered before scrolling
              requestAnimationFrame(() => {
                const pixelsPerHour = 48; // Approximate
                const elevenAmPosition = 11 * pixelsPerHour; // Scroll to 11am

    if (DEBUG) console.log('[Calendar] Scroll container scrollHeight:', scrollContainer.scrollHeight, 'clientHeight:', scrollContainer.clientHeight);

                if (isInitialLoadRef.current) {
                  // Initial load: scroll to 11am
    if (DEBUG) console.log('[Calendar] Initial load - scrolling to 11am, position:', elevenAmPosition);
                  scrollContainer.scrollTop = elevenAmPosition;
                  isInitialLoadRef.current = false;
                } else if (savedScrollPosition !== null && savedScrollPosition > 0) {
                  // Restore saved scroll position when switching views (if not at top)
    if (DEBUG) console.log('[Calendar] Restoring scroll position:', savedScrollPosition);
                  scrollContainer.scrollTop = savedScrollPosition;
                  setSavedScrollPosition(null);
                } else {
                  // If at top or no saved position, scroll to 11am
    if (DEBUG) console.log('[Calendar] View switch with no meaningful position - scrolling to 11am, position:', elevenAmPosition);
                  scrollContainer.scrollTop = elevenAmPosition;
                  setSavedScrollPosition(null);
                }
    if (DEBUG) console.log('[Calendar] After setting scrollTop to', scrollContainer.scrollTop);
              });
            } else {
    if (DEBUG) console.log('[Calendar] ERROR: Scroll container not found!');
            }
          }
        }, delay);
      };

      handleScroll();
    }, [currentViewName, savedScrollPosition]);

      const refreshScheduler = useCallback(() => {
      if (DEBUG) console.log('[Calendar][refreshScheduler] Called at', new Date().toISOString());
      if (DEBUG) console.log('[Calendar][refreshScheduler] Call stack:', new Error().stack);
        const SC_Id = localStorage.getItem("SC_Id");
        let mechanicMap = {};
        fetch(envVariable.serverId + `/SCO/Calendar.php?SC_Id=${SC_Id}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        })
          .then((response) => response.json())
          .then((data) => {
          if (DEBUG) console.log('[Calendar][refreshScheduler] Received response with', data.tasks?.length || 0, 'tasks');
            let mech_details = []
            const url = `${envVariable.serverId}/SCO/Mechanic.php?SC_Id=${SC_Id}`;
            fetch(url, {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
            })
              .then((response) => {
                if (!response.ok) {
                  throw new Error(`HTTP error! status: ${response.status}`); // Throw an error on bad HTTP status
                }
                return response.json();
              })
              .then((mech_data) => {
                if (!mech_data || !mech_data.mechanics || mech_data.mechanics.length === 0) {
    if (DEBUG) console.log("No mechanic data available.");
                  setMechData([]); // Setting to empty array or handling accordingly
                  return;
                }
    if (DEBUG) console.log("Data:", mech_data.mechanics);
                mech_details = mech_data.mechanics
    if (DEBUG) console.log("mech_details",mech_details)
                setMechData(mech_data.mechanics);
                mechanicMap = mech_details.reduce((acc, mechanic) => {
                  acc[mechanic.user_id] = mechanic;
                  return acc;
                }, {});
                let localIdCounter = taskIdCounterRef.current;
            

    if (DEBUG) console.log("RESPONSE IS \n");
              const tasksRaw = data.tasks || [];
    if (DEBUG) console.log("[Calendar][refreshScheduler] tasks fetched:", tasksRaw.length);

              // Log tasks with null mechanic_id for debugging unassignment issues
              const unassignedInResponse = tasksRaw.filter(t => t.mechanic_id === null || t.mechanic_id === undefined);
              if (unassignedInResponse.length > 0 && DEBUG) {
                console.log('[Calendar][refreshScheduler] Tasks with mechanic_id=NULL in response:',
                  unassignedInResponse.map(t => ({ task_id: t.task_id, service_info: t.service_info, mechanic_id: t.mechanic_id })));
              }

              const allTasks = tasksRaw.map((task) => {
                  const mechanic = mechanicMap[task.mechanic_id];
                const startStr = task.calendar_start || task.appointment_time || null;
                const endStr = task.calendar_end || null;

                // Convert strings to Date objects for the Scheduler
                let startDate = null;
                let endDate = null;

                if (startStr) {
                  try {
                    // Tasks are stored in UTC in the database
                    startDate = parseUTCDateTime(startStr);
                    if (isNaN(startDate.getTime())) startDate = null;
                  } catch (e) {
                    console.warn('[refreshScheduler] Invalid startDate:', startStr, e);
                    startDate = null;
                  }
                }

                if (endStr) {
                  try {
                    // Tasks are stored in UTC in the database
                    endDate = parseUTCDateTime(endStr);
                    if (isNaN(endDate.getTime())) endDate = null;
                  } catch (e) {
                    console.warn('[refreshScheduler] Invalid endDate:', endStr, e);
                    endDate = null;
                  }
                }

                // Fallback: if no calendar_end, derive a 30m duration to avoid disappearing
                if (!endDate && startDate) {
                  try {
                    endDate = new Date(startDate.getTime() + 30 * 60 * 1000);
                  } catch (e) { /* ignore */ }
                }

                const restTask = { ...task };
                delete restTask.id;
                const mapped = {
                  ...restTask,
                  id: String(task.task_id ?? task.id ?? (localIdCounter++)),
                    title: task.service_info,
                  // Use mechanic_id consistently to satisfy Scheduler Resources grouping
                  mechanic_id: task.mechanic_id,
                    status: task.status,
                  // Preserve canonical fields for later updates
                  calendar_start: task.calendar_start || null,
                  calendar_end: task.calendar_end || null,
                  appointment_time: task.appointment_time || null,
                  // Scheduler time fields (must be Date objects!)
                  startDate,
                  endDate,
                    mechanic_first_name: mechanic?.first_name || "",
                    mechanic_last_name: mechanic?.last_name || "",
                  };
                return mapped;
              });

              // Sample a few tasks for quick inspection without flooding the console
              try {
                const sample = allTasks.slice(0, 10).map(t => ({
                  task_id: t.task_id,
                  startDate: t.startDate,
                  endDate: t.endDate,
                  mechanic_id: t.mechanic_id,
                  status: t.status,
                }));
    if (DEBUG) console.log("[Calendar][refreshScheduler] mapped sample:", sample);
              } catch (_) { /* noop */ }

              // Reconcile: if the just-updated task is missing or stale, prefer local state briefly
              {
                const last = lastTaskOpRef.current;
                if (last && !allTasks.some(t => t.task_id == last.task_id)) {
                  console.warn('[Calendar][refreshScheduler] last updated task missing from response; showing from local state', last);
                  // NOTE: getTask.php endpoint doesn't exist, so we use the local state fallback directly
                  // This should rarely happen now that we delay refreshScheduler by 200ms

                  // Create task from last known state
                  const s = last.calendar_start ? parseUTCDateTime(last.calendar_start) : new Date();
                  const e = last.calendar_end ? parseUTCDateTime(last.calendar_end) : new Date(s.getTime() + 30 * 60 * 1000);
                  const fallback = {
                    id: localIdCounter++,
                    task_id: last.task_id,
                    title: last.service_info || 'Task',
                    mechanic_id: last.mechanic_id,
                    status: last.status || 'Open',
                    calendar_start: last.calendar_start,
                    calendar_end: last.calendar_end,
                    startDate: s,  // Use Date objects, not strings
                    endDate: e,
                  };
                  allTasks.push(fallback);
                  setSnackbar({ open: true, message: `Task ${last.task_id} not returned by server; showing locally`, severity: 'warning' });
                setAppointments(allTasks);
                taskIdCounterRef.current = localIdCounter;
                  return;
                }
                // If the task is present but the server returned stale times or stale mechanic,
                // override the display with the locally confirmed values.
                if (last) {
                  const idx = allTasks.findIndex(t => t.task_id == last.task_id);
                  if (idx >= 0) {
                    // Both server and lastTaskOpRef store UTC strings, so parse them as UTC
                    const serverStart = (allTasks[idx].calendar_start || allTasks[idx].appointment_time)
                      ? parseUTCDateTime(allTasks[idx].calendar_start || allTasks[idx].appointment_time)
                      : null;
                    const serverEnd = allTasks[idx].calendar_end ? parseUTCDateTime(allTasks[idx].calendar_end) : null;
                    const localStart = last.calendar_start ? parseUTCDateTime(last.calendar_start) : null;
                    const localEnd = last.calendar_end ? parseUTCDateTime(last.calendar_end) : null;
                    const differs = !!(localStart && localEnd && (
                      !serverStart || !serverEnd || Math.abs(serverStart - localStart) >= 30000 || Math.abs(serverEnd - localEnd) >= 30000
                    ));
                    if (differs) {
                      console.warn('[Calendar][refreshScheduler] last updated task present but stale in response; showing local times', {
                        task_id: last.task_id,
                        serverStart_utc: allTasks[idx].calendar_start,
                        localStart_utc: last.calendar_start,
                        serverStart_parsed: serverStart?.toString(),
                        localStart_parsed: localStart?.toString()
                      });
                      allTasks[idx] = {
                        ...allTasks[idx],
                        calendar_start: last.calendar_start,
                        calendar_end: last.calendar_end,
                        startDate: localStart || allTasks[idx].startDate,
                        endDate: localEnd || allTasks[idx].endDate,
                      };
                    }
                    // Bridge mechanic assignment if server hasn't caught up
                    // IMPORTANT: Only bridge when server has a NON-NULL value that differs from local.
                    // When server returns NULL, it's the source of truth (task was unassigned) - don't override it.
                    const lastHadMechanic = 'mechanic_id' in last;
                    const serverMechanicId = allTasks[idx].mechanic_id;

                    if (lastHadMechanic && serverMechanicId != last.mechanic_id) {
                      // Only bridge if the server returned a non-NULL value
                      // When server says NULL, respect it - don't override with stale local data
                      if (serverMechanicId !== null && serverMechanicId !== undefined) {
                        console.warn('[Calendar][refreshScheduler] bridging mechanic_id to last local value', { task_id: last.task_id, from: serverMechanicId, to: last.mechanic_id });
                        if (last.mechanic_id) {
                          // Assigning to a mechanic
                          const lastMech = mechanicMap[last.mechanic_id];
                          allTasks[idx] = {
                            ...allTasks[idx],
                            mechanic_id: last.mechanic_id,
                            mechanic_first_name: lastMech?.first_name || '',
                            mechanic_last_name: lastMech?.last_name || '',
                          };
                        } else {
                          // Unassigning (mechanic_id = null)
                          allTasks[idx] = {
                            ...allTasks[idx],
                            mechanic_id: null,
                            mechanic: null,
                            mechanic_first_name: '',
                            mechanic_last_name: '',
                          };
                        }
                      } else {
                        if (DEBUG) console.log('[Calendar][refreshScheduler] Server returned NULL mechanic_id for task', last.task_id, '- respecting unassignment, not bridging');
                      }
                    }
                  }
                }
              }

              // If the server omitted the just-updated task, add it optimistically so it doesn't vanish

              // Final logging before setting appointments
              const finalUnassigned = allTasks.filter(t => t.mechanic_id === null || t.mechanic_id === undefined);
              if (finalUnassigned.length > 0 && DEBUG) {
                console.log('[Calendar][refreshScheduler] Final allTasks array has', finalUnassigned.length, 'unassigned tasks:',
                  finalUnassigned.map(t => ({ task_id: t.task_id, service_info: t.service_info, mechanic_id: t.mechanic_id })));
              }

              setAppointments(allTasks);
              taskIdCounterRef.current = localIdCounter;
              // Don't turn off refreshing yet - wait for availability to load too
              setFrozenAppointments(null);
              setFrozenAvailability(null);
              })
              .catch((error) => {
                console.error("Error fetching mechanics:", error);
                setMechData([]); // Handling error by setting mechanics data to empty array
                mech_details = []
              });
    if (DEBUG) console.log("Mech", mechDate)

            

            

            // const newPendingTasks = allTasks
            //   .filter((task) => task.status === "Pending") // First, filter the tasks
            //   .map((task) => {
            //     const startDate = new Date(task.appointment_time);
            //     const endDate = new Date(startDate.getTime() + 30 * 60 * 1000); // Assuming a 30 minute duration

            //     return {
            //       ...task,
            //       startDate: task.appointment_time,
            //       endDate: formattedDateTime(endDate),
            //     };
            //   });

            // const filteredAppointments = allTasks
            //   .filter((task) => task.status !== "Pending") // First, filter the tasks
            //   .map((task) => {
            //     return {
            //       ...task,
            //       startDate: task.calendar_start,
            //       endDate: task.calendar_end,
            //     };
            //   });

            // const filteredAppointments = allTasks.filter(
            //   (task) => task.status !== "Pending"
            // );

            // setPendingBooking(newPendingTasks);
            // Update the taskIdCounter after all operations
          })
          .catch((error) => {
            console.error("Error fetching tasks:", error);
            // Turn off refreshing if tasks fetch fails
            setIsRefreshing(false);
          });

        // Mechanic availability fetch call

        const availabilityUrl = new URL(envVariable.serverId + `/mechanicSlots/getFreeslot.php`);
        availabilityUrl.searchParams.set("sc_id", SC_Id);
        if (selectedLocationId) {
          availabilityUrl.searchParams.set("location_id", selectedLocationId);
        }

        fetch(availabilityUrl.toString(), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        })
          .then((response) => response.json())
          .then((data) => {
    if (DEBUG) console.log("RESPONSE IS \n");
    if (DEBUG) console.log(data.message);

            // Count before filtering
            const totalCount = data.message.length;
            const leaveCount = data.message.filter(a => a.mech_status === "Requested off").length;
            const pendingLeaveCount = data.message.filter(a => a.mech_status === "Requested off" && a.status !== "approved").length;

            const filtered = data.message.filter(
            (appointment) => {
              // Include all availability blocks
              if (appointment.mech_status === "Available") return true;
              // Include only approved leave blocks
              if (appointment.mech_status === "Requested off") {
                if (appointment.status === "approved") {
                  return true;
                } else {
                  // Debug: Log pending leave blocks that are being filtered out
                  console.log('[Calendar] Filtering out pending leave block:', {
                    mechanic_id: appointment.mechanic_id,
                    status: appointment.status,
                    start: appointment.start_date_time,
                    end: appointment.end_date_time
                  });
                  return false;
                }
              }
              return false;
            }
            );

            console.log('[Calendar] Availability filter summary:', {
              total: totalCount,
              totalLeave: leaveCount,
              pendingLeave: pendingLeaveCount,
              filtered: filtered.length,
              excludedPending: pendingLeaveCount
            });
    if (DEBUG) console.log("filtered mechanic AVAILABILITIES:", filtered);
            const modified = mergeAvailability(filtered);
    if (DEBUG) console.log("MODIFIED LIST IS ", modified);
            // console.log(modified);

            if (modified.length > 0) {
              const mappedAppointments = modified.map((entry, index) => {
              // Availability is stored in UTC in DB - use parseUTCDateTime for consistency with tasks
              // JavaScript will automatically convert to browser's local timezone for display
              const startDate = parseUTCDateTime(entry.start_date_time);
              const endDate = parseUTCDateTime(entry.end_date_time);
                // Use unique ID that won't conflict with task IDs
                const id = `avail_${entry.slot_id || index}`;
              // Find mechanic details for this entry
              const mechanic = mechanicMap[entry.mechanic_id];
                // Set title based on status for leave blocks - use short labels for narrow day view
                let title;
                if (entry.mech_status === 'Requested off') {
                  // Use leave reason if available, otherwise generic "Leave"
                  const leaveType = entry.leave_reason === 'sick' ? 'Sick' : entry.leave_reason === 'vacation' ? 'Vacation' : 'Leave';
                  title = entry.status === 'approved' ? leaveType : 'Pending';
                } else {
                  title = 'Available';
                }
                return {
                  startDate,
                  endDate,
                  id,
                  title,
                  mech_status: entry.mech_status,
                  status: entry.status, // Preserve status field (e.g., 'approved', 'pending')
                  slot_id: entry.slot_id,
                  task_id: entry.task_id,
                  sc_id: entry.sc_id,
                  location_id: entry.location_id,
                  date: entry.date,
                  mechanic_id: entry.mechanic_id,
                leave_reason: entry.leave_reason,
                mechanic_first_name: mechanic?.first_name || "",
                mechanic_last_name: mechanic?.last_name || "",
                };
              });
              _setAvailability(mappedAppointments);
            } else {
              _setAvailability([]);
            }
            // Now that both tasks and availability are loaded, turn off refreshing
            setIsRefreshing(false);
          })
          .catch((error) => {
            console.error("Error:", error);
            _setAvailability([]);
            // Turn off refreshing even if availability fetch fails
            setIsRefreshing(false);
          });
      }, [selectedLocationId]);

      useEffect(() => {
    if (DEBUG) console.log("UseEffect Call Check;");
    if (DEBUG) console.log("Effect triggered");
        refreshScheduler();
      }, [refreshScheduler, refreshTrigger]);

      // console.log("Appointments in Default Scheduler: ", appointments);
      // console.log("PendingBookings in Default Scheduler: ", pendingBooking);
      // console.log("pendingbookings", pendingBooking);
      // console.log("mechanic availibility in default scheduler:", availability);

      // const getSchedulerAppointments = () => {
      //   const schedulerAppointments = [...appointments, ...availability];
      //       return schedulerAppointments;
      // };

      // const schedulerAppointments = getSchedulerAppointments();

      // Define AppointmentContent component
      // const mechanicResource = {
      //   fieldName: "mechanicId",
      //   title: "Mechanic",
      //   instances: mechanics.map((mech) => ({
      //     id: mech.user_id,
      //     text: mech.first_name,
      //   })),
      // };
      // console.log("mechanicResources:", mechanicResource);

      // const [ReactCalendarDate, setReactCalendarDate] = useState(new Date());
      // setReactCalendarDate(currentDate);
      
    const jumpToDayView = useCallback(
      (rawDate) => {
        const targetDate = toDayjs(rawDate);
        if (!targetDate.isValid()) {
          return;
        }
        onExternalDateChange(targetDate);
        setCurrentDate(targetDate);
        setCurrentViewName("Day");
      },
      [onExternalDateChange, setCurrentDate, setCurrentViewName]
    );

    const handleAppointmentClick = (appointmentData) => {
        if (!appointmentData) {
          console.error("Appointment data is undefined");
        return;
        }
    if (DEBUG) console.log("Hey AppointmentClick Handler, appointmentData:", appointmentData);

      // Open task sidebar for editing
      setSidebarTask(appointmentData);
      setSidebarMode('edit');
      // If task has a customer_id, mark as existing customer (read-only for customer/vehicle editing)
      setSidebarIsExistingCustomer(!!appointmentData.customer_id || !!appointmentData.customer_user_id);
      setSidebarOpen(true);
      // Don't change view when clicking a task - stay in current view (week/month/day)
      // jumpToDayView(appointmentData.startDate);
    };

    // const handleAppointmentClick = (appointmentData) => {
    //   if (!appointmentData) {
    //     console.error("Appointment data is undefined");
    //     // return;
    //   }
    //   if (!appointmentData.startDate) {
    //     console.error("Start date is undefined");
    //     // return;
    //   }
    //   console.log("Hey AppointmentClick Handler,appointmentData :", appointmentData);
    //   console.log("handleAppointment Click date current date: before", currentDate);
    //   // setCurrentDate(appointmentData.startDate); // Set the date to the clicked appointment date
      
    //   setIsCustomDayView(true);
    // };

    const handleSaveTask = async () => {
    if (DEBUG) console.log('[handleSaveTask] Called with sidebarMode:', sidebarMode, 'sidebarTask:', sidebarTask);
      if (!sidebarTask) return;

      // Validate time range
      if (sidebarTask.startDate && sidebarTask.endDate) {
        const start = new Date(sidebarTask.startDate);
        const end = new Date(sidebarTask.endDate);

        if (start >= end) {
          setSnackbar({ open: true, message: 'Start time must be before end time', severity: 'error' });
          return;
        }
      }

      // Validate that required fields are filled
      if (!sidebarTask.service_info || !sidebarTask.service_info.trim()) {
        setSnackbar({ open: true, message: 'Service information is required', severity: 'error' });
        return;
      }

      if (!sidebarTask.startDate || !sidebarTask.endDate) {
        setSnackbar({ open: true, message: 'Start time and end time are required', severity: 'error' });
        return;
      }

      // Validate customer email if provided
      if (sidebarTask.customer_email && sidebarTask.customer_email.trim()) {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(sidebarTask.customer_email.trim())) {
          setSnackbar({ open: true, message: 'Invalid email format', severity: 'error' });
          return;
        }
      }

      // Validate mechanic availability if mechanic is assigned
      if (sidebarTask.mechanic_id) {
    if (DEBUG) console.log('[handleSaveTask] Validating mechanic availability');
        const validation = validateMechanicAvailability(
          sidebarTask.mechanic_id,
          sidebarTask.startDate,
          sidebarTask.endDate,
          sidebarTask.task_id // Exclude current task if editing
        );
    if (DEBUG) console.log('[handleSaveTask] Validation result:', validation);

        if (!validation.valid) {
    if (DEBUG) console.log('[handleSaveTask] Validation failed, returning early');
          setSnackbar({ open: true, message: `Cannot assign task: ${validation.reason}`, severity: 'error' });
          return;
        }
    if (DEBUG) console.log('[handleSaveTask] Validation passed, proceeding to save');
      } else {
    if (DEBUG) console.log('[handleSaveTask] No mechanic assigned, skipping validation');
      }

      const SC_Id = localStorage.getItem("SC_Id");

      // Handle customer linking/unlinking
      let linkedUserCarId = sidebarTask.user_car_id || null;
      const wasLinked = sidebarMode === 'edit' && sidebarTask.user_car_id;
      const emailCleared = wasLinked && (!sidebarTask.customer_email || !sidebarTask.customer_email.trim());

      if (emailCleared) {
        // Email was cleared - unlink the customer
        try {
    if (DEBUG) console.log('[handleSaveTask] Unlinking customer from task');
          const unlinkResponse = await fetch(envVariable.serverId + "/SCO/unlinkCustomerFromTask.php", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              task_id: sidebarTask.task_id,
              user_car_id: sidebarTask.user_car_id
            }),
          });

          const unlinkResult = await unlinkResponse.json();
    if (DEBUG) console.log('[handleSaveTask] unlinkCustomerFromTask response:', unlinkResult);

          if (unlinkResult.type === 'success') {
            linkedUserCarId = null;
            setSnackbar({ open: true, message: 'Customer unlinked from task', severity: 'info' });
          } else {
            setSnackbar({ open: true, message: 'Failed to unlink customer: ' + unlinkResult.message, severity: 'warning' });
          }
        } catch (unlinkError) {
          console.error('Error unlinking customer:', unlinkError);
          setSnackbar({ open: true, message: 'Could not unlink customer', severity: 'warning' });
        }
      } else if (sidebarTask.customer_email && sidebarTask.customer_email.trim()) {
        // Email provided - link the customer
        try {
    if (DEBUG) console.log('[handleSaveTask] Linking customer by email:', sidebarTask.customer_email);
          const linkResponse = await fetch(envVariable.serverId + "/SCO/linkCustomerByEmail.php", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              email: sidebarTask.customer_email.trim(),
              vin_number: sidebarTask.vehicle_vin || sidebarTask.vin_number || null,
              sc_id: SC_Id
            }),
          });

          const linkResult = await linkResponse.json();
    if (DEBUG) console.log('[handleSaveTask] linkCustomerByEmail response:', linkResult);

          if (linkResult.type === 'success') {
            linkedUserCarId = linkResult.user_car_id;

            // Update sidebar task with customer info for display
            if (linkResult.customer) {
              setSidebarTask({
                ...sidebarTask,
                customer_id: linkResult.customer.user_id,
                customer_first_name: linkResult.customer.first_name,
                customer_last_name: linkResult.customer.last_name,
                customer_email: linkResult.customer.email,
              });
            }

            if (linkResult.is_existing_customer) {
              setSnackbar({ open: true, message: 'Customer account found and linked', severity: 'info' });
            } else {
              setSnackbar({ open: true, message: 'Guest customer created - will link when they sign up', severity: 'info' });
            }
          } else {
            setSnackbar({ open: true, message: 'Failed to link customer: ' + linkResult.message, severity: 'warning' });
          }
        } catch (linkError) {
          console.error('Error linking customer:', linkError);
          setSnackbar({ open: true, message: 'Could not link customer, but task will be saved', severity: 'warning' });
        }
      }

      // Show loading spinner and freeze display to prevent flicker
      const operationStartTime = Date.now();
      setIsRefreshing(true);
      try {
        const snapshot = (appointments || []).map(a =>
          String(a?.task_id) === String(sidebarTask?.task_id) ? { ...a, ...sidebarTask } : a
        );
        setFrozenAppointments(snapshot);
      } catch (_) {
        setFrozenAppointments((prev) => (appointments ? [...appointments] : prev));
      }
      setFrozenAvailability((prev) => (availability ? [...availability] : prev));

      try {
        if (sidebarMode === 'create') {
    if (DEBUG) console.log('[handleSaveTask] Creating new task');
          const payload = {
            sc_id: SC_Id,
            service_info: sidebarTask.service_info,
            status: sidebarTask.status,
            mechanic_id: sidebarTask.mechanic_id || null,
            calendar_start: formatDateTimeUTC(sidebarTask.startDate),
            calendar_end: formatDateTimeUTC(sidebarTask.endDate),
            appointment_time: formatDateTimeUTC(sidebarTask.startDate),
            user_car_id: linkedUserCarId,
          };
    if (DEBUG) console.log('[handleSaveTask] createTask.php payload:', payload);
          // Create new task
          const response = await fetch(envVariable.serverId + "/SCO/createTask.php", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify(payload),
          });

          const result = await response.json();
    if (DEBUG) console.log('[handleSaveTask] createTask.php response:', result);

          // Calculate remaining time to reach minimum 750ms loading duration
          const elapsedTime = Date.now() - operationStartTime;
          const minimumLoadingTime = 750;
          const minimumCommitDelay = 500;
          const remainingTime = Math.max(minimumLoadingTime - elapsedTime, minimumCommitDelay);

          setTimeout(() => {
            if (result.type === 'success') {
              setSnackbar({ open: true, message: 'Task created successfully!', severity: 'success' });
              setSidebarOpen(false);
              refreshScheduler();
            } else {
              setIsRefreshing(false);
              setFrozenAppointments(null);
              setFrozenAvailability(null);
              setSnackbar({ open: true, message: 'Failed to create task: ' + result.message, severity: 'error' });
            }
          }, remainingTime);
        } else {
    if (DEBUG) console.log('[handleSaveTask] Updating existing task');
          // Use directly selected user_car_id if available, otherwise use linkedUserCarId from email linking
          const finalUserCarId = sidebarTask.user_car_id || linkedUserCarId;
          const payload = {
            task_id: sidebarTask.task_id,
            service_info: sidebarTask.service_info,
            status: sidebarTask.status,
            mechanic_id: sidebarTask.mechanic_id || null,
            calendar_start: formatDateTimeUTC(sidebarTask.startDate),
            calendar_end: formatDateTimeUTC(sidebarTask.endDate),
            appointment_time: formatDateTimeUTC(sidebarTask.startDate),
            user_car_id: finalUserCarId,
          };
    if (DEBUG) console.log('[handleSaveTask] updateTask.php payload:', payload);

          // Validate mechanic availability without mutating availability blocks
          // (matching the approach used in postUpdateTask - availability represents working hours, not task occupancy)
          if (sidebarTask.mechanic_id) {
            const snap = (d) => formatDateTimeUTC(roundToNearestQuarterHour(new Date(d)));
            const prev = (appointments || []).find(a => String(a.task_id) === String(sidebarTask.task_id));
            const nextPayload = {
              task_id: sidebarTask.task_id,
              sc_id: prev?.sc_id || SC_Id,
              mechanic_id: sidebarTask.mechanic_id,
              calendar_start: snap(sidebarTask.startDate),
              calendar_end: snap(sidebarTask.endDate),
              status: sidebarTask.status,
              service_info: sidebarTask.service_info,
            };
            try {
              const check = await fetch(envVariable.serverId + `/mechanicSlots/checkSlotsValid.php`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(nextPayload),
              });
              const cj = await check.json();
              if (cj?.type === 'failure' || cj?.type === 'error') {
                // Calculate remaining time for smooth UX
                const elapsedTime = Date.now() - operationStartTime;
                const minimumLoadingTime = 750;
                const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
                setTimeout(() => {
                  setIsRefreshing(false);
                  setFrozenAppointments(null);
                  setFrozenAvailability(null);
                  setSnackbar({ open: true, message: cj.message || 'Cannot assign task: Mechanic not available for this time slot', severity: 'error' });
                }, remainingTime);
                return; // Stop here if validation fails
              }
    if (DEBUG) console.log('[handleSaveTask] checkSlotsValid passed:', cj);
            } catch (e) {
              console.warn('[handleSaveTask] checkSlotsValid failed (non-fatal):', e);
            }
          }

          // Update existing task
          const response = await fetch(envVariable.serverId + "/SCO/updateTask.php", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify(payload),
          });

          const result = await response.json();
    if (DEBUG) console.log('[handleSaveTask] updateTask.php response:', result);

          // Calculate remaining time to reach minimum 750ms loading duration
          const elapsedTime = Date.now() - operationStartTime;
          const minimumLoadingTime = 750;
          const minimumCommitDelay = 500;
          const remainingTime = Math.max(minimumLoadingTime - elapsedTime, minimumCommitDelay);

          setTimeout(() => {
            if (result.type === 'success') {
              // Record last operation so refresh reconciliation can use it
              lastTaskOpRef.current = {
                task_id: sidebarTask.task_id,
                mechanic_id: sidebarTask.mechanic_id || null,
                calendar_start: formatDateTimeUTC(sidebarTask.startDate),
                calendar_end: formatDateTimeUTC(sidebarTask.endDate),
                status: sidebarTask.status,
                service_info: sidebarTask.service_info,
              };
    if (DEBUG) console.log('[handleSaveTask] recorded lastTaskOp', lastTaskOpRef.current);

              setSnackbar({ open: true, message: 'Task updated successfully!', severity: 'success' });
              setSidebarOpen(false);
              refreshScheduler();
            } else {
              setIsRefreshing(false);
              setFrozenAppointments(null);
              setFrozenAvailability(null);
              setSnackbar({ open: true, message: 'Failed to update task: ' + result.message, severity: 'error' });
            }
          }, remainingTime);
        }
      } catch (error) {
        console.error('Error saving task:', error);
        // Calculate remaining time for smooth UX
        const elapsedTime = Date.now() - operationStartTime;
        const minimumLoadingTime = 750;
        const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
        setTimeout(() => {
          setIsRefreshing(false);
          setFrozenAppointments(null);
          setFrozenAvailability(null);
          setSnackbar({ open: true, message: 'An error occurred while saving the task', severity: 'error' });
        }, remainingTime);
      }
    };

    // Handle adding a new vehicle for customer in create/edit task dialog
    const handleAddVehicle = async () => {
      // Get customer ID from either create mode (sidebarCustomerFound) or edit mode (sidebarTask)
      const customerId = sidebarCustomerFound?.user_id || sidebarTask?.customer_id || sidebarTask?.customer_user_id;

      if (!customerId) {
        setSnackbar({ open: true, message: 'No customer linked to add vehicle', severity: 'error' });
        return;
      }

      // Validate car_name is provided
      if (!sidebarNewVehicle.car_name.trim()) {
        setSnackbar({ open: true, message: 'Vehicle name is required', severity: 'error' });
        return;
      }

      try {
        const response = await fetch(
          `${envVariable.serverId}/SCO/addCustomerVehicle.php`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              user_id: customerId,
              ...sidebarNewVehicle,
            }),
          }
        );
        const data = await response.json();

        if (data.type === 'success') {
          // Add the new vehicle to the list and select it
          const vehicle = data.vehicle || {
            user_car_id: data.user_car_id,
            ...sidebarNewVehicle,
            display_name: sidebarNewVehicle.car_name || `${sidebarNewVehicle.year} ${sidebarNewVehicle.make} ${sidebarNewVehicle.model}`.trim() || 'New Vehicle',
          };
          setSidebarCustomerVehicles((prev) => [vehicle, ...prev]);
          setSidebarTask((prev) => ({ ...prev, user_car_id: data.user_car_id }));
          setSidebarAddVehicleOpen(false);
          setSidebarNewVehicle({ car_name: '', make: '', model: '', year: '', submodel: '', vin_number: '' });
          setSnackbar({ open: true, message: 'Vehicle added successfully', severity: 'success' });
        } else {
          setSnackbar({ open: true, message: data.message || 'Failed to add vehicle', severity: 'error' });
        }
      } catch (error) {
        console.error('Error adding vehicle:', error);
        setSnackbar({ open: true, message: 'Failed to add vehicle', severity: 'error' });
      }
    };

    // Handle editing an existing vehicle
    const handleEditVehicle = async () => {
      if (!sidebarEditVehicle.user_car_id) {
        setSnackbar({ open: true, message: 'No vehicle selected to edit', severity: 'error' });
        return;
      }

      // Validate car_name is provided
      if (!sidebarEditVehicle.car_name.trim()) {
        setSnackbar({ open: true, message: 'Vehicle name is required', severity: 'error' });
        return;
      }

      try {
        const response = await fetch(
          `${envVariable.serverId}/SCO/editCustomerVehicle.php`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(sidebarEditVehicle),
          }
        );
        const data = await response.json();

        if (data.type === 'success') {
          // Update the vehicle in the list
          const updatedVehicle = data.vehicle || sidebarEditVehicle;
          setSidebarCustomerVehicles((prev) =>
            prev.map((v) =>
              v.user_car_id === updatedVehicle.user_car_id ? { ...v, ...updatedVehicle } : v
            )
          );
          setSidebarEditVehicleOpen(false);
          setSidebarEditVehicle({ user_car_id: null, car_name: '', make: '', model: '', year: '', submodel: '', vin_number: '' });
          setSnackbar({ open: true, message: 'Vehicle updated successfully', severity: 'success' });
        } else {
          setSnackbar({ open: true, message: data.message || 'Failed to update vehicle', severity: 'error' });
        }
      } catch (error) {
        console.error('Error updating vehicle:', error);
        setSnackbar({ open: true, message: 'Failed to update vehicle', severity: 'error' });
      }
    };

    // Open edit vehicle form with selected vehicle's data
    const openEditVehicleForm = () => {
      const selectedVehicle = sidebarCustomerVehicles.find(
        (v) => v.user_car_id === sidebarTask?.user_car_id
      );
      if (selectedVehicle) {
        setSidebarEditVehicle({
          user_car_id: selectedVehicle.user_car_id,
          car_name: selectedVehicle.car_name || '',
          make: selectedVehicle.make || '',
          model: selectedVehicle.model || '',
          year: selectedVehicle.year || '',
          submodel: selectedVehicle.submodel || '',
          vin_number: selectedVehicle.vin_number || '',
        });
        setSidebarEditVehicleOpen(true);
        setSidebarAddVehicleOpen(false); // Close add form if open
      }
    };

    // Handle Change Customer button click - looks up customer by email
    const handleSidebarChangeCustomer = async () => {
      const email = sidebarNewCustomerEmail.trim();
      if (!email || !email.includes('@')) {
        setSnackbar({ open: true, message: 'Please enter a valid email address', severity: 'error' });
        return;
      }

      setSidebarLookupLoading(true);
      try {
        const response = await fetch(
          `${envVariable.serverId}/SCO/lookupCustomerByEmail.php?email=${encodeURIComponent(email)}`,
          { credentials: 'include' }
        );
        const data = await response.json();

        if (data.type === 'success' && data.found) {
          // Found existing customer - update task with new customer info
          setSidebarCustomerFound(data.customer);
          setSidebarCustomerVehicles(data.vehicles || []);
          setSidebarIsExistingCustomer(true);
          setSidebarTask(prev => ({
            ...prev,
            customer_id: data.customer.user_id,
            customer_user_id: data.customer.user_id,
            customer_first_name: data.customer.first_name,
            customer_last_name: data.customer.last_name,
            customer_email: data.customer.email,
            customer_phone: data.customer.phone,
            user_car_id: null, // Reset vehicle selection for new customer
          }));
          // Close dialog and reset input
          setSidebarChangeCustomerOpen(false);
          setSidebarNewCustomerEmail('');
          setSidebarEditVehicleOpen(false);
          setSidebarAddVehicleOpen(false);
          setSnackbar({ open: true, message: 'Customer found and linked', severity: 'success' });
        } else {
          // No customer found - allow creating new customer with this email
          setSidebarCustomerFound(null);
          setSidebarCustomerVehicles([]);
          setSidebarIsExistingCustomer(false);
          setSidebarTask(prev => ({
            ...prev,
            customer_id: null,
            customer_user_id: null,
            customer_first_name: '',
            customer_last_name: '',
            customer_email: email,
            customer_phone: '',
            user_car_id: null,
            // Clear stale vehicle data from previous customer
            vehicle_vin: '',
            vin_number: '',
            make: '',
            model: '',
            year: '',
          }));
          // Reset vehicle form states
          setSidebarEditVehicle({ user_car_id: null, car_name: '', make: '', model: '', year: '', submodel: '', vin_number: '' });
          setSidebarNewVehicle({ car_name: '', make: '', model: '', year: '', submodel: '', vin_number: '' });
          setSidebarEditVehicleOpen(false);
          setSidebarAddVehicleOpen(false);
          // Close dialog and reset input
          setSidebarChangeCustomerOpen(false);
          setSidebarNewCustomerEmail('');
          setSnackbar({ open: true, message: 'Email not found - you can enter new customer details', severity: 'info' });
        }
      } catch (error) {
        console.error('Error looking up customer by email:', error);
        setSnackbar({ open: true, message: 'Error looking up customer', severity: 'error' });
      } finally {
        setSidebarLookupLoading(false);
      }
    };

    // Handle Remove Customer button click
    const handleSidebarRemoveCustomer = () => {
      if (!window.confirm('Are you sure you want to remove the customer from this task?')) {
        return;
      }
      setSidebarCustomerFound(null);
      setSidebarCustomerVehicles([]);
      setSidebarIsExistingCustomer(false);
      setSidebarTask(prev => ({
        ...prev,
        customer_id: null,
        customer_user_id: null,
        customer_first_name: '',
        customer_last_name: '',
        customer_email: '',
        customer_phone: '',
        user_car_id: null,
        // Clear stale vehicle data
        vehicle_vin: '',
        vin_number: '',
        make: '',
        model: '',
        year: '',
      }));
      // Reset vehicle form states
      setSidebarEditVehicle({ user_car_id: null, car_name: '', make: '', model: '', year: '', submodel: '', vin_number: '' });
      setSidebarNewVehicle({ car_name: '', make: '', model: '', year: '', submodel: '', vin_number: '' });
      setSidebarEditVehicleOpen(false);
      setSidebarAddVehicleOpen(false);
      setSnackbar({ open: true, message: 'Customer removed from task', severity: 'success' });
    };

    const handleDeleteTask = async () => {
      if (!sidebarTask || !sidebarTask.task_id) return;

      if (!window.confirm('Are you sure you want to delete this task?')) {
        return;
      }

      // Show loading spinner
      const operationStartTime = Date.now();
      setIsRefreshing(true);
      try {
        const snapshot = (appointments || []).filter(a => String(a?.task_id) !== String(sidebarTask?.task_id));
        setFrozenAppointments(snapshot);
      } catch (_) {
        setFrozenAppointments((prev) => (appointments ? [...appointments] : prev));
      }
      setFrozenAvailability((prev) => (availability ? [...availability] : prev));

      try {
        const user_id = localStorage.getItem("User_Id");
        const sc_id = localStorage.getItem("SC_Id");

        const response = await fetch(envVariable.serverId + "/SCO/deleteTask.php", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            task_id: sidebarTask.task_id,
            deleted_by: user_id,
            sc_id: sc_id,
          }),
        });

        const result = await response.json();

        // Calculate remaining time to reach minimum 750ms loading duration
        const elapsedTime = Date.now() - operationStartTime;
        const minimumLoadingTime = 750;
        const minimumCommitDelay = 500;
        const remainingTime = Math.max(minimumLoadingTime - elapsedTime, minimumCommitDelay);

        setTimeout(() => {
          if (result.type === 'success') {
            setSnackbar({ open: true, message: 'Task deleted successfully!', severity: 'success' });
            setSidebarOpen(false);
            refreshScheduler();
            // Reset loading state after successful delete
            setIsRefreshing(false);
            setFrozenAppointments(null);
            setFrozenAvailability(null);
          } else {
            setIsRefreshing(false);
            setFrozenAppointments(null);
            setFrozenAvailability(null);
            setSnackbar({ open: true, message: 'Failed to delete task: ' + result.message, severity: 'error' });
          }
        }, remainingTime);
      } catch (error) {
        console.error('Error deleting task:', error);
        // Calculate remaining time for smooth UX
        const elapsedTime = Date.now() - operationStartTime;
        const minimumLoadingTime = 750;
        const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
        setTimeout(() => {
          setIsRefreshing(false);
          setFrozenAppointments(null);
          setFrozenAvailability(null);
          setSnackbar({ open: true, message: 'An error occurred while deleting the task', severity: 'error' });
        }, remainingTime);
      }
    };

    // Format datetime in local timezone (prevents UTC conversion issues)
    // eslint-disable-next-line no-unused-vars
    const formattedDateTime = (datetime) => {
      if (!datetime) return null;
      const d = new Date(datetime);
      if (isNaN(d.getTime())) return null;

      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const seconds = String(d.getSeconds()).padStart(2, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };


    const handleCurrDateChange = (newdate) => {
      if (DEBUG) console.log("New date in date change handler before handleCurrDateChange:", newdate);
          // const formatted_date = formattedDateTime(newdate);
      if (DEBUG) console.log("Formatted date in date change handler:");

          onExternalDateChange(newdate);
          setCurrentDate(newdate);
      if (DEBUG) console.log("New date in date change handler after handleCurrDateChange:", newdate);
        };

    const getMergedAppointments = () => {
        const maxAppointmentId = appointments.length > 0 ? Math.max(...appointments.map((a) => a.id)) : 0;
        const idOffset = maxAppointmentId + 1;

        const mergedAppointments = [
          ...appointments,
          ...pendingBooking.map((booking, index) => ({
            ...booking,
            id: idOffset + index, // Ensure unique IDs by offsetting with index
            title: booking.service_info || "Pending Service",
            isPending: true, // Flag to identify pending bookings
          })),
        ];

        return mergedAppointments;
      };

    const mergedAppointments = useMemo(getMergedAppointments, [appointments, pendingBooking]);

    const monthCellComponent = useMemo(
      () =>
        createMonthCellRenderer({
          getAppointments: () => mergedAppointments,
          onItemClick: (selection) => jumpToDayView(selection.startDate),
        }),
      [mergedAppointments, jumpToDayView]
    );

    // const schedulerData = mergedAppointments;

      const [editingOptions, /* setEditingOptions */] = useState({
        allowAdding: true,
        allowDeleting: true,
        allowUpdating: true,
        allowDragging: true,
        allowResizing: true,
      });
      // const [addedAppointment, setAddedAppointment] = useState({});

      // const [isAppointmentBeingCreated, setIsAppointmentBeingCreated] = useState(false);

      // eslint-disable-next-line no-unused-vars
      const { allowAdding, allowDeleting, allowUpdating, allowResizing, allowDragging } = editingOptions;

      const hasSameTaskIdentity = (left, right) => {
        if (!left || !right) return false;
        if (left.task_id != null && right.task_id != null) {
          return String(left.task_id) === String(right.task_id);
        }
        if (left.id != null && right.id != null) {
          return String(left.id) === String(right.id);
        }
        return false;
      };

      const upsertAppointment = (nextAppointment) => {
        setAppointments((prevAppointments) => {
          const existingIndex = prevAppointments.findIndex((appointment) => hasSameTaskIdentity(appointment, nextAppointment));
          if (existingIndex === -1) {
            return [...prevAppointments, nextAppointment];
          }

          return prevAppointments.map((appointment, index) => (
            index === existingIndex
              ? { ...appointment, ...nextAppointment }
              : appointment
          ));
        });
      };

      const removeExternalPendingTask = (taskLike) => {
        setPendingBooking((prevPendingBooking) => prevPendingBooking.filter(
          (appointment) => !hasSameTaskIdentity(appointment, taskLike)
        ));
      };

    // Round a Date to the nearest 15-minute increment
      function roundToNearestQuarterHour(date) {
        const d = new Date(date);
        const quarterMs = 15 * 60 * 1000;
        const rounded = Math.round(d.getTime() / quarterMs) * quarterMs;
        return new Date(rounded);
      }

    // Normalize seconds/millis and only snap to :00 or :30 when within a small epsilon (in minutes)
      function normalizeToMinuteAndSnap(date, epsilonMinutes = 2) {
        if (!date) return null;
        const d = new Date(date);
        if (isNaN(d.getTime())) return null;
        d.setSeconds(0, 0);
        const m = d.getMinutes();
        const mod = m % 30;
        if (mod <= epsilonMinutes) {
          d.setMinutes(m - mod);
        } else if ((30 - mod) <= epsilonMinutes) {
          d.setMinutes(m + (30 - mod));
        }
        return d;
      }

      const postWeekViewUpdate = useCallback(async (data) => {
        const response = await fetch(envVariable.serverId + `/mechanicSlots/pendingTaskdrag.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            task_id: data.task_id,
            appointment_time: data.appointment_time,
          }),
        });
        await response.json();

        refreshScheduler();
      }, [refreshScheduler]);

      const onCommitChanges = useCallback(
        ({ changed }) => {
          setPendingBooking((prevPendingBooking) => {
            let updatedAppointments = [...prevPendingBooking];
            if (changed) {
              updatedAppointments = updatedAppointments.map((appointment) => {
                if (appointment.status === "Pending") {
    if (DEBUG) console.log("Hello oncommit updatedAppointments: ", updatedAppointments);
    if (DEBUG) console.log("Hello oncommit chnaged", changed);
    if (DEBUG) console.log("Hello oncommit length", mergedAppointments.length + 1);
                  const change = changed[appointment.id]; // Get changes by appointment ID

                  if (change) {
                    if (change.startDate) {
                    const roundedStartDate = roundToNearestQuarterHour(new Date(change.startDate));
                    const roundedEndDate = roundToNearestQuarterHour(new Date(change.endDate));
                      change.startDate = roundedStartDate.toISOString();
                      change.endDate = roundedEndDate.toISOString();
                    }
                    // Apply the changes if available
                    appointment.startDate = change.startDate || appointment.startDate;
                    appointment.endDate = change.endDate || appointment.endDate;

                    const sendData = {
                      task_id: appointment.task_id,
                    appointment_time: formatDateTimeUTC(new Date(appointment.startDate).getTime()),
                    };

    if (DEBUG) console.log("Sent appointment in default scheduler:", change);
                    postWeekViewUpdate(sendData);

                    return { ...appointment, ...change };
                  }
                }
                return appointment;
              });
            }

            return updatedAppointments;
          });
        },
        [mergedAppointments.length, postWeekViewUpdate, setPendingBooking]
      );
    if (DEBUG) console.log(onCommitChanges)

      // eslint-disable-next-line no-unused-vars -- Usage is commented out around line 1603
      // const onAddedAppointmentChange = React.useCallback((appointment) => {
      //   setAppointments(appointment);
      //   // setIsAppointmentBeingCreated(true);
      // }, []);

      const navigate = useNavigate();

      const fetchTaskDetails = async (taskId) => {
        try {
          const response = await fetch(
          `${envVariable.serverId}/SCO/updateTask.php?TaskId=${taskId}`, // Consolidated endpoint
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          const data = await response.json(); // Convert the response to JSON

          if (data.status === "Success") {
            return data.data.task; // Return the desired property
          } else {
            console.error("Failed to fetch task details:", data.message); // Error handling
            return null; // Return null on failure
          }
        } catch (error) {
          console.error("Error fetching task details:", error); // Error handling
          return null; // Return null on exception
        }
      };

      const handleChat = async (data) => {
        if (DEBUG) console.log("handle chat inside")
        const task = await fetchTaskDetails(data.task_id); // Await the async function
    if (DEBUG) console.log("task fetched in handleChat:", task); // Check for null
      if (task === null) {
        console.error("Failed to fetch task details");
        return; // Early return if no task
      }

      const createdDate = new Date(task.created_at);
      const formattedDate = createdDate.toISOString().split("T")[0]; // Extract YYYY-MM-DD
      navigate(`/tasks/chat/${task.task_id}`, {
        state: {
          customerId: task.Customer_id,
          customerName: task.Customer,
          mechanicId: task.mechanic_id,
          mechanicName: task.Mechanic,
          SC_Id: task.sc_id,
          originDate: formattedDate,
        },
      });
    };

    // eslint-disable-next-line no-unused-vars
    const ExternalAppointment = ({ text, data, onDragStart }) => {
      return (
        <div
          draggable
          onDragStart={(e) => onDragStart(e, data)}
          style={{
            position: "relative",
            padding: "10px",
            margin: "15px", // Increase margin for more spacing
            backgroundColor: "rgb(197 201 227)", // Slightly lighter shade of pink for contrast
            border: "1px solid #ccc",
            borderRadius: "2px", // More rounded corners
            cursor: "grab",
          }}
        >
          <div style={{ position: "absolute", top: "5px", right: "5px" }}>
            <FontAwesomeIcon
              icon={faTimes}
              onClick={() => handleDeletePending(data)}
              style={{
                cursor: "pointer",
                color: "red",
              }}
            />
          </div>

          <div style={{ position: "absolute", top: "5px", right: "25px" }}>
              <FontAwesomeIcon
                icon={faCommentDots}
                onClick={() => handleChat(data)}
                style={{
                  cursor: "pointer",
                  color: "blue", // Distinct color for the chat icon
                }}
              />
            </div>

            <div
              style={{
                textAlign: "center",
                padding: "12px",
                fontSize: "12px",
                // top: "5px",
              }}
            >
              {text} {/* Center-aligned text */}
            </div>
          </div>
        );
      };

      // handleDeletePending
      const handleDeletePending = async (data) => {
    if (DEBUG) console.log("data task id:", data.task_id);
        try {
          const response = await fetch(
            //envVariable.serverId + `/mechanicSlots/checkSlotsValid.php`,
            envVariable.serverId + `/SCO/cancelTask.php`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                task_id: data.task_id,
            }),
            }
          );

        const result = await response.json();

        if (result.type === 'success') {
    if (DEBUG) console.log("Delete successful");
          setSnackbar({ open: true, message: 'Task deleted successfully', severity: 'success' });
          } else {
          console.error("Delete failed:", result.message);
          setSnackbar({ open: true, message: 'Failed to delete task', severity: 'error' });
          }
        } catch (error) {
          console.error("An error occurred while deleting:", error);
        setSnackbar({ open: true, message: 'Error deleting task', severity: 'error' });
        }
        refreshScheduler();
      };
    // const allowDrag = React.useCallback(() => allowDragging && allowUpdating, [allowDragging, allowUpdating]);
    // const allowResize = React.useCallback(() => allowResizing && allowUpdating, [allowResizing, allowUpdating]);
      // const getColorForMechanic = (name) => {
      //   // Hash the name into a number
      //   const hash = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);

      //   // Use HSL to generate dynamic but visually distinct colors
      //   const hue = hash % 360; // spread across 360° hue wheel
      //   return `hsl(${hue}, 70%, 50%)`; // keep saturation and lightness fixed for readability
      // };
    // Note: Week view colors now use module-scoped getMechanicColor(mechanic_id).
    // The previous local getColorForMechanic helper is no longer needed here.
      const CustomAppointment = ({ children, style, data, ...restProps }) => {
        // this is the one
        // const mechanicName = `${data.mechanic_first_name} ${data.mechanic_last_name}`;
        
      // Use distinct color for unassigned tasks
      const isUnassigned = !data.mechanic_id && !data.mechanic;
      // Week view should color by mechanic; prefer mechanic_id, then mechanic, then (legacy) user_id
      const mechIdForColor = data?.mechanic_id ?? data?.mechanic ?? data?.user_id;
      const sidebarColor = isUnassigned ? '#FF5722' : (getMechanicColor(mechIdForColor) || '#999'); // Bright orange for unassigned

      // Debug logging to verify color assignment
      if (DEBUG) console.log('[CustomAppointment] Task:', data?.task_id, 'mechanic_id:', data?.mechanic_id, 'mechIdForColor:', mechIdForColor, 'color:', sidebarColor);

        // const statusColors = {
        //   Open: "orange",
        //   Completed: "green",
        //   "In-Progress": "orange",
        //   Pending: "red",
        // };
        // const sidebarColor = statusColors[data.status];
    if (DEBUG) console.log("CustomAppointment data:", data);
        const statusIcons = {
          Pending: faCircleNotch,
          Completed: faCheckCircle,
          // "In-Progress": faCircleNotch,
          "In-Progress":faHourglassHalf,
          Open: faCircleNotch,
        };

        return (
        
          <Appointments.Appointment
            {...restProps}
            style={{
              ...style,
              backgroundColor: sidebarColor,
              borderRadius: "8px",
              width: "65%",
              minWidth: "48px",
              maxWidth: "160px"
            }}
            
          >
          < Tooltip
            title={
              <div style={{ fontSize: "12px", lineHeight: "1.4", opacity: "0.9"}}>
                <p><b>Service:</b> {data.service_info || "N/A"}</p>
                <p><b>Status:</b> {data.status}</p>
              <p><b>Start Time:</b> {parseUTCDateTime(data.startDate)?.toLocaleString([], {month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'})}</p>
              <p><b>Duration:</b> {(() => {
                const start = parseUTCDateTime(data.startDate);
                const end = parseUTCDateTime(data.endDate);
                if (start && end) {
                  const durationMs = end - start;
                  const hours = Math.floor(durationMs / (1000 * 60 * 60));
                  const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
                  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
                }
                return 'N/A';
              })()}</p>
              {data.appointment_time && (
                <p><b>Appointment Time:</b> {parseUTCDateTime(data.appointment_time)?.toLocaleString([], {month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'})}</p>
              )}
              <p><b>Mechanic:</b> {isUnassigned ? 'Unassigned' : `${data.mechanic_first_name || ''} ${data.mechanic_last_name || ''}`.trim()}</p>
              </div>
            }
            // arrow
            placement="bottom"
          disableInteractive
          >
            <div
              className="appointment-container"
              onClick={() => handleAppointmentClick(data)} // Click event
            style={{ userSelect: 'none', cursor: 'move' }}
            >
            <div style={{ display: "flex" }}>
                <div
                  style={{
                    // width: "1vw",
                    height: "150vh",
                    backgroundColor: sidebarColor,
                  }}
                />
                <div style={{marginLeft:"5px"}}>
                <FontAwesomeIcon
                      icon={statusIcons[data.status] || faCircle}
                      style={{
                        cursor: "pointer",
                        color: "white", // Distinct color for chat
                      }}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent click event from propagating to parent
                        handleChat(data); // Handler for chat functionality
                      }}
                    /> 
                </div>
                <div style={{overflow:"auto"}}>
                  
                  
              <div style={{color:"white",fontWeight:"bold"}}>
                {isUnassigned ? 'Unassigned' : `${data.mechanic_first_name || ''} ${data.mechanic_last_name || ''}`.trim()}
              </div>
                  {/* <br />   */}
                  
                  {children}
                  </div>
                  <div style={{ marginRight: "5px" }}>
                    <FontAwesomeIcon
                      icon={faCommentDots} // Chat icon
                      style={{
                        cursor: "pointer",
                        color: "white", // Distinct color for chat
                      }}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent click event from propagating to parent
                        handleChat(data); // Handler for chat functionality
                      }}
                    />
                  </div>
              </div>
            </div>
            </Tooltip>
          </Appointments.Appointment>
          
        );
      };

      const CustomRenderAppointment = ({ children, style, data, ...restProps }) => {
      // Day view: use three clear colors for readability:
      // - Tasks (any mechanic): single task color
      // - Availability: single availability color
      // - Sick/vacation/leave: single leave color
      const isAvailabilityBlock = data?.mech_status === 'Available';
      const isLeaveBlock = data?.mech_status === 'Requested off';
      const isTaskBlock = !isAvailabilityBlock && !isLeaveBlock;

      let sidebarColor;
      if (isLeaveBlock) {
        sidebarColor = '#ab47bc'; // Leave (sick/vacation)
      } else if (isAvailabilityBlock) {
        sidebarColor = '#90caf9'; // Availability
      } else if (isTaskBlock) {
        sidebarColor = '#FF9800'; // Tasks
      } else {
        sidebarColor = '#FF9800';
      }

      // Start from scheduler-provided geometry (including column placement and
      // overlap layout) but override background color for our three types.
      // Give availability blocks lower z-index and opacity so they show behind tasks
      const appointmentStyle = {
        ...(style || {}),
              backgroundColor: sidebarColor,
              title: data.status,
              opacity: isAvailabilityBlock ? 0.5 : 1,
              zIndex: isAvailabilityBlock ? 0 : 1,
      };


      const handleBlockClick = () => {
        // Only handle clicks on availability/leave blocks, not tasks
        if (isAvailabilityBlock || isLeaveBlock) {
          setSelectedBlock(data);
          setEditDialogOpen(true);
        }
      };

      const handleTaskClick = () => {
        // Non-availability/leave blocks are tasks: open the task sidebar
        if (!isAvailabilityBlock && !isLeaveBlock) {
          handleAppointmentClick(data);
        }
      };

      return (
        <div
          onClick={(isAvailabilityBlock || isLeaveBlock) ? handleBlockClick : undefined}
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            cursor: (isAvailabilityBlock || isLeaveBlock) ? 'pointer' : 'default',
            }}
        >
          <Appointments.Appointment
            {...restProps}
            style={appointmentStyle}
          >
            <div
              className={classes.button}
              onClick={handleTaskClick}
              size="large"
              style={{ position: "relative", zIndex: isAvailabilityBlock ? 0 : 1 }}
            >
              {/* For availability and leave blocks, use custom compact text display */}
              {(isAvailabilityBlock || isLeaveBlock) ? (
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  padding: "4px",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  color: "#fff",
                  textAlign: "center",
                  wordWrap: "break-word",
                  overflow: "hidden"
                }}>
                  {data?.title || 'Block'}
                </div>
              ) : (
                <div style={{ display: "flex" }}>
                  <div
                    style={{
                      width: "2vw",
                      height: "100%",
                      backgroundColor: sidebarColor,
                    }}
                  ></div>
                  {/* Sidebar-like strip */}
                  <div style={{ flex: 1 }}>{children}</div>
                </div>
              )}
            </div>
          </Appointments.Appointment>
        </div>
        );
      };

      // eslint-disable-next-line no-unused-vars -- Unused, not even a commented-out attempt
      // const CommandButton = React.useCallback(
      //   ({ id, ...restProps }) => {
      //     if (id === "deleteButton") {
      //       return <AppointmentForm.CommandButton id={id} {...restProps} disabled={!allowDeleting} />;
      //     }
      //     return <AppointmentForm.CommandButton id={id} {...restProps} />;
      //   },
      //   [allowDeleting]
      // );

    // Removed legacy cell-click creation handler to prevent conflicts with DnD

      // const [mainResourceName, setMainResourceName] = useState("mechanic");

      // const changeMainResource = (mainResourceName) => {
      //   setMainResourceName(mainResourceName);
      // };

    const allowDrag = React.useCallback((args) => {
      const data = args?.appointmentData || args?.data || args;
      const status = data?.mech_status;
      // Prevent dragging availability/leave blocks in the main calendar scheduler;
      // they should be edited via the dedicated dialogs instead of task logic.
      if (status === 'Available' || status === 'Requested off') {
    if (DEBUG) console.log('[DefaultScheduler][allowDrag] block drag for', status);
        return false;
      }
    if (DEBUG) console.log('[DefaultScheduler][allowDrag] allow for task');
      return true;
    }, []);
    const allowResize = React.useCallback((args) => {
      const data = args?.appointmentData || args?.data || args;
      const status = data?.mech_status;
      if (status === 'Available' || status === 'Requested off') {
    if (DEBUG) console.log('[DefaultScheduler][allowResize] block resize for', status);
        return false;
      }
    if (DEBUG) console.log('[DefaultScheduler][allowResize] allow for task');
      return true;
    }, []);

    // computeCellMeta is already defined above with unassigned task detection

    if (DEBUG) console.log("Pending bookings in default sCheduler:", pendingBooking);

    const getSchedulerStartTime = (e, baseDateForDay) => {
      // Instead of manual coordinate calculations, find the actual cell element under the cursor
      // This leverages the scheduler's own layout and avoids coordinate calculation errors
    if (DEBUG) console.log("[getSchedulerStartTime] Finding cell at coordinates:", e.clientX, e.clientY);

      // Get all elements at the cursor position
      const elementsUnderCursor = document.elementsFromPoint(e.clientX, e.clientY);
    if (DEBUG) console.log("[getSchedulerStartTime] Elements under cursor:", elementsUnderCursor.map(el => ({
        tag: el.tagName,
        className: el.className,
        textContent: el.textContent?.substring(0, 50)
      })));

      // Look for a table cell (TD) which is how the scheduler renders time cells
      const cellElement = elementsUnderCursor.find(el => el.tagName === 'TD');

      if (cellElement) {
    if (DEBUG) console.log("[getSchedulerStartTime] Found TD cell:", {
          className: cellElement.className,
          textContent: cellElement.textContent,
          dataset: cellElement.dataset
        });

        // Try to extract time from the cell's vertical position within its column
        // Find the parent table to calculate relative position
        let tableElement = cellElement;
        while (tableElement && tableElement.tagName !== 'TABLE') {
          tableElement = tableElement.parentElement;
        }

        if (tableElement) {
          // Get all cells in the same column to determine which row we're in
          const tableBounds = tableElement.getBoundingClientRect();
          const cellBounds = cellElement.getBoundingClientRect();

          // The cell's relative Y position tells us the time
          const relativeY = cellBounds.top - tableBounds.top;
          const cellHeight = cellBounds.height;

          // Each cell represents 30 minutes (standard scheduler cell height)
          const halfHourIndex = Math.round(relativeY / cellHeight);
          const hour = Math.floor(halfHourIndex / 2);
          const minute = (halfHourIndex % 2) * 30;

    if (DEBUG) console.log("[getSchedulerStartTime] Cell position calculation:", {
            tableBoundsTop: tableBounds.top,
            cellBoundsTop: cellBounds.top,
            relativeY,
            cellHeight,
            halfHourIndex,
            hour,
            minute
          });

          const baseDate = new Date(baseDateForDay);
          baseDate.setHours(hour, minute, 0, 0);
    if (DEBUG) console.log("[getSchedulerStartTime] Calculated time:", baseDate.toString());
          return baseDate;
        }
      }

      // Fallback to manual calculation if we can't find the cell
      console.warn("[getSchedulerStartTime] Could not find cell element, falling back to manual calculation");
      const bounds = e.currentTarget.getBoundingClientRect();
      const DAY_HEADER_HEIGHT = 65;
      const y = e.clientY - bounds.top - DAY_HEADER_HEIGHT;
      const timeGridHeight = bounds.height - DAY_HEADER_HEIGHT;
      const pixelsPerHour = timeGridHeight / 24;
      const hoursFromTop = Math.max(0, y) / pixelsPerHour;
      let hour = Math.floor(hoursFromTop);
      const minuteFrac = (hoursFromTop - hour) * 60;
      let minute = minuteFrac < 15 ? 0 : (minuteFrac < 45 ? 30 : 0);
      if (minuteFrac >= 45) { minute = 0; hour += 1; }

      const baseDate = new Date(baseDateForDay);
      baseDate.setHours(hour, minute, 0, 0);
    if (DEBUG) console.log("[getSchedulerStartTime] Fallback calculation result:", baseDate.toString());
      return baseDate;
      };

      const identifyMechanicFromPosition = (e) => {
        const bounds = e.currentTarget.getBoundingClientRect();
      // Account for the left time panel width in the scheduler
      const TIME_PANEL_WIDTH = 80; // px, approximate time-scale column width
      let x = e.clientX - bounds.left - TIME_PANEL_WIDTH;
      if (x < 0) x = 0;
      const resourceCount = mechanics.length || 1;
      const contentWidth = Math.max(bounds.width - TIME_PANEL_WIDTH, 1);
      const pixelsPerResource = contentWidth / resourceCount;

        // Determine the mechanic based on the drop position
      let mechanicIndex = Math.floor(x / pixelsPerResource);
      if (mechanicIndex < 0) mechanicIndex = 0;
      if (mechanicIndex >= resourceCount) mechanicIndex = resourceCount - 1;
      return mechanics[mechanicIndex]?.user_id;
      };

      const postPendingTask = async (appointment) => {
      // Track start time to ensure minimum 750ms loading duration
      const operationStartTime = Date.now();

      // Show loading spinner
      setIsRefreshing(true);
      // Freeze display during operation
      try {
        const snapshot = (appointments || []).map(a => (
          String(a?.id) === String(appointment?.id) ||
          (a?.task_id != null && appointment?.task_id != null && String(a.task_id) === String(appointment.task_id))
        ) ? appointment : a);
        setFrozenAppointments(snapshot);
      } catch (_) {
        setFrozenAppointments((prev) => (appointments ? [...appointments] : prev));
      }
      setFrozenAvailability((prev) => (availability ? [...availability] : prev));

      try {
    if (DEBUG) console.log("[postPendingTask] Starting with appointment:", appointment);
    if (DEBUG) console.log("[postPendingTask] Sending to backend:", {
          calendar_start: appointment.calendar_start,
          calendar_end: appointment.calendar_end,
          mechanic_id: appointment.mechanic_id,
          task_id: appointment.task_id,
          sc_id: appointment.sc_id,
        });

    if (DEBUG) console.log("[postPendingTask] Calling checkSlotsValid.php");
        let res = await fetch(envVariable.serverId + `/mechanicSlots/checkSlotsValid.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(appointment),
        });

        if (!res.ok) {
          console.error("[postPendingTask] checkSlotsValid HTTP error:", res.status, res.statusText);
          throw new Error(`checkSlotsValid failed: ${res.status} ${res.statusText}`);
        }

        let jsonRes = await res.json();
    if (DEBUG) console.log("[postPendingTask] checkSlotsValid response:", jsonRes);

        if (jsonRes.type === "failure" || jsonRes.type === "error") {
          console.error("[postPendingTask] Slot validation failed:", jsonRes.message);
          // Ensure minimum loading time before showing error
          const elapsedTime = Date.now() - operationStartTime;
          const minimumLoadingTime = 750;
          const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
          setTimeout(() => {
            setSnackbar({
              open: true,
              message: `Cannot assign task: ${jsonRes.message || 'Slot validation failed'}`,
              severity: 'error'
            });
          refreshScheduler();
          }, remainingTime);
          return;
        }

        // Update the task with the new slot assignment; fail the flow if backend rejects
    if (DEBUG) console.log("[postPendingTask] Calling updateTaskSlot.php");
        const updateRes = await fetch(envVariable.serverId + `/mechanicSlots/updateTaskSlot.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(appointment),
        });
        if (!updateRes.ok) {
          console.error("[postPendingTask] updateTaskSlot HTTP error:", updateRes.status, updateRes.statusText);
          throw new Error(`updateTaskSlot failed: ${updateRes.status} ${updateRes.statusText}`);
        }
        try {
          const updateJson = await updateRes.json();
    if (DEBUG) console.log("[postPendingTask] updateTaskSlot response:", updateJson);
          if (updateJson?.type && updateJson.type !== 'success') {
            console.error("[postPendingTask] updateTaskSlot returned non-success:", updateJson);
            throw new Error(updateJson?.message || 'Task slot update rejected');
          }
        } catch (err) {
          console.warn("[postPendingTask] updateTaskSlot response parsing warning:", err);
          // Some endpoints may not return JSON; ignore if HTTP ok
        }

        // Persist the task end time (and start) to the task record to avoid duration reverting
        try {
    if (DEBUG) console.log("[postPendingTask] Calling updateTask.php");
          const payload = {
            task_id: appointment.task_id,
            service_info: appointment.service_info,
            status: appointment.status,
            mechanic_id: appointment.mechanic_id,
            calendar_start: appointment.calendar_start || appointment.startDate,
            calendar_end: appointment.calendar_end || appointment.endDate,
            appointment_time: appointment.calendar_start || appointment.startDate,
          };
    if (DEBUG) console.log("[postPendingTask] updateTask.php payload:", payload);
          const taskUpdateRes = await fetch(envVariable.serverId + `/SCO/updateTask.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          const taskUpdateJson = await taskUpdateRes.json();
    if (DEBUG) console.log("[postPendingTask] updateTask.php response:", taskUpdateJson);

          // Remember last successfully updated task so refreshScheduler can verify it appears correctly
          lastTaskOpRef.current = {
            task_id: appointment.task_id,
            mechanic_id: appointment.mechanic_id,
            calendar_start: appointment.calendar_start || appointment.startDate,
            calendar_end: appointment.calendar_end || appointment.endDate,
            status: appointment.status,
            service_info: appointment.service_info,
          };
    if (DEBUG) console.log('[postPendingTask] recorded lastTaskOp', lastTaskOpRef.current);
        } catch (e) {
          console.warn('[postPendingTask] Task end-time update failed (non-critical):', e);
        }

        // Send notification (non-critical - don't fail if this errors)
        try {
    if (DEBUG) console.log("[postPendingTask] Calling assignTaskNotif.php");
        await fetch(envVariable.serverId + `/mechanicSlots/assignTaskNotif.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(appointment),
        });
    if (DEBUG) console.log("[postPendingTask] Notification sent");
        } catch (notifError) {
          console.warn("[postPendingTask] Failed to send notification (non-critical):", notifError);
        }

    if (DEBUG) console.log("[postPendingTask] ✓ All backend calls completed successfully");

        // Calculate remaining time to reach minimum 750ms loading duration
        // ALSO ensure at least 200ms after operations complete for DB transaction commit
        const elapsedTime = Date.now() - operationStartTime;
        const minimumLoadingTime = 750;
        const minimumCommitDelay = 200;
        const remainingTime = Math.max(minimumLoadingTime - elapsedTime, minimumCommitDelay);

    if (DEBUG) console.log(`[postPendingTask] Elapsed: ${elapsedTime}ms, will wait ${remainingTime}ms more (ensuring both 750ms total and 200ms commit time)`);

        // Ensure minimum 750ms loading experience AND 200ms DB commit time, then show success and refresh
        setTimeout(() => {
          setSnackbar({
            open: true,
            message: 'Task assigned successfully!',
            severity: 'success'
          });
        refreshScheduler();
        }, remainingTime);
      } catch (error) {
        console.error("[postPendingTask] ✗ Error occurred:", error);

        // Ensure minimum loading time before showing error
        const elapsedTime = Date.now() - operationStartTime;
        const minimumLoadingTime = 750;
        const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);

        setTimeout(() => {
          setSnackbar({
            open: true,
            message: `Failed to assign task: ${error.message}`,
            severity: 'error'
          });
          // Task was optimistically added to UI; remove it since backend failed
          setAppointments((prev) => prev.filter(apt => apt.id !== appointment.id));
          refreshScheduler();
        }, remainingTime);
      }
      };

      const handleDrop = (e) => {
      e.preventDefault();
        const data = e.dataTransfer.getData("application/json");
      if (!data) return;
        const draggedAppointment = JSON.parse(data);

      // Determine target day depending on current view
      let targetDayDate;
      if (currentViewName === 'Week') {
        // Use elementsFromPoint to find the actual cell under the cursor
        const elementsUnderCursor = document.elementsFromPoint(e.clientX, e.clientY);
        const cellElement = elementsUnderCursor.find(el => el.tagName === 'TD');

        if (cellElement) {
          // Find the column index by looking at parent TR and counting TD siblings
          const rowElement = cellElement.parentElement;
          if (rowElement && rowElement.tagName === 'TR') {
            const cells = Array.from(rowElement.children);
            const rawCellIndex = cells.indexOf(cellElement);

            // The scheduler table structure in week view has exactly 7 cells (no time label column)
            // Cells 0-6 represent Sunday through Saturday
            let dayIndex = rawCellIndex;
            if (dayIndex < 0) dayIndex = 0;
            if (dayIndex > 6) dayIndex = 6;

            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    if (DEBUG) console.log("[handleDrop] Cell index:", rawCellIndex, "→ Day:", dayNames[dayIndex]);

            const weekStart = new Date(currentDate);
            weekStart.setHours(0,0,0,0);
            const weekDay = weekStart.getDay();
            weekStart.setDate(weekStart.getDate() - weekDay);
            targetDayDate = new Date(weekStart);
            targetDayDate.setDate(weekStart.getDate() + dayIndex);

    if (DEBUG) console.log("[handleDrop] Week calculation from cell:", {
              rawCellIndex,
              adjustedDayIndex: dayIndex,
              dayName: dayNames[dayIndex],
              weekStart: weekStart.toDateString(),
              targetDayDate: targetDayDate.toDateString()
            });
          }
        }

        // Fallback to manual calculation if we couldn't find the cell
        if (!targetDayDate || isNaN(targetDayDate.getTime())) {
          console.warn("[handleDrop] Could not determine day from cell, falling back to coordinate calculation");
          const bounds = e.currentTarget.getBoundingClientRect();
          const TIME_PANEL_WIDTH = 80;
          let x = e.clientX - bounds.left - TIME_PANEL_WIDTH;
          if (x < 0) x = 0;
          const contentWidth = Math.max(bounds.width - TIME_PANEL_WIDTH, 1);
          const dayWidth = contentWidth / 7;
          let dayIndex = Math.floor(x / dayWidth);
          if (dayIndex < 0) dayIndex = 0; if (dayIndex > 6) dayIndex = 6;

          const weekStart = new Date(currentDate);
          weekStart.setHours(0,0,0,0);
          const weekDay = weekStart.getDay();
          weekStart.setDate(weekStart.getDate() - weekDay);
          targetDayDate = new Date(weekStart);
          targetDayDate.setDate(weekStart.getDate() + dayIndex);

    if (DEBUG) console.log("[handleDrop] Fallback day calculation:", {
            dayIndex,
            targetDayDate: targetDayDate.toDateString()
          });
        }
      } else {
        // Day view: single day displayed = currentDate
        targetDayDate = new Date(currentDate);
      }

      // Build start time within that day using the improved cell-based detection
      const dropTime = getSchedulerStartTime(e, targetDayDate);
    if (DEBUG) console.log("[handlePendingDrop] Drop event details:", {
        currentDate: currentDate,
        currentViewName: currentViewName,
        targetDayDate: targetDayDate,
        dropTime: dropTime,
        dropTimeISO: dropTime.toISOString(),
        dropTimeLocal: dropTime.toString(),
      });

      // Preserve task duration when available; fallback to 30 minutes
      let durationMs = 30 * 60 * 1000;
      try {
        if (draggedAppointment.calendar_end && draggedAppointment.calendar_start) {
          const maybe = parseUTCDateTime(draggedAppointment.calendar_end) - parseUTCDateTime(draggedAppointment.calendar_start);
          if (!Number.isNaN(maybe) && maybe > 0) durationMs = maybe;
        }
      } catch (_) { /* ignore */ }
      const endTime = new Date(dropTime.getTime() + durationMs);

      if (currentViewName === 'Day') {
        // In Day view, columns represent mechanics – identify mechanic from x position
        const assignedMechanic = identifyMechanicFromPosition(e);
        if (!assignedMechanic) {
          setSnackbar({ open: true, message: 'Drop on a mechanic column.', severity: 'error' });
          return;
        }
        const validation = validateMechanicAvailability(assignedMechanic, dropTime, endTime);
        if (!validation.valid) {
          setSnackbar({ open: true, message: `Cannot assign task: ${validation.reason}`, severity: 'error' });
          return;
        }

        const newAppointment = {
          ...draggedAppointment,
          id: draggedAppointment.id ?? draggedAppointment.task_id ?? uuidv4(),
          title: draggedAppointment.title,
          startDate: dropTime,
          endDate: endTime,
          calendar_start: formatDateTimeUTC(dropTime),
          calendar_end: formatDateTimeUTC(endTime),
          mechanic_id: assignedMechanic,
          status: 'Open',
        };
    if (DEBUG) console.log("[handlePendingDrop] Day view - Calling postPendingTask with:", newAppointment);
        postPendingTask(newAppointment);
        upsertAppointment(newAppointment);
        removeExternalPendingTask(draggedAppointment);
        return;
      }

      // Week view: find all available mechanics and prompt if multiple
    if (DEBUG) console.log("[handlePendingDrop] Week view - Finding available mechanics...");
      const availableMechanics = mechDate.filter(mech => (
        validateMechanicAvailability(mech.user_id, dropTime, endTime).valid
      ));
    if (DEBUG) console.log("[handlePendingDrop] Available mechanics found:", availableMechanics.length, availableMechanics.map(m => `${m.first_name} ${m.last_name} (${m.user_id})`));

      if (availableMechanics.length === 0) {
    if (DEBUG) console.log("[handlePendingDrop] No available mechanics - showing error");
        setSnackbar({ open: true, message: 'No mechanics available for this time slot', severity: 'error' });
        return;
      }

      if (availableMechanics.length === 1) {
    if (DEBUG) console.log("[handlePendingDrop] Exactly one mechanic available - auto-assigning");
        const only = availableMechanics[0];
        const newAppointment = {
          ...draggedAppointment,
          id: draggedAppointment.id ?? draggedAppointment.task_id ?? uuidv4(),
          title: draggedAppointment.title,
          startDate: dropTime,
          endDate: endTime,
          calendar_start: formatDateTimeUTC(dropTime),
          calendar_end: formatDateTimeUTC(endTime),
          mechanic_id: only.user_id,
          status: 'Open',
        };
    if (DEBUG) console.log("[handlePendingDrop] Week view (single mechanic) - Calling postPendingTask with:", newAppointment);
        postPendingTask(newAppointment);
        upsertAppointment(newAppointment);
        removeExternalPendingTask(draggedAppointment);
      } else {
        // Multiple mechanics: defer assignment to selection dialog
        const newAppointment = {
          ...draggedAppointment,
          id: draggedAppointment.id ?? draggedAppointment.task_id ?? uuidv4(),
          title: draggedAppointment.title,
          startDate: dropTime,
          endDate: endTime,
          calendar_start: formatDateTimeUTC(dropTime),
          calendar_end: formatDateTimeUTC(endTime),
          mechanic_id: null,
          status: 'Open',
        };
    if (DEBUG) console.log("[handlePendingDrop] Week view (multiple mechanics) - Showing selection dialog");
        setPendingTaskDrop({ mode: 'assignPending', newAppointment, availableMechanics });
        setMechanicSelectOpen(true);
      }
      };

      const allowDrop = (e) => {
      // Only prevent default for external drags carrying our JSON payload
      const types = Array.from(e.dataTransfer?.types || []);
      if (types.includes('application/json')) {
        e.preventDefault();

        // Log calculated drop position in real-time to help user aim
        if (currentViewName === 'Week' && e.clientX && e.clientY) {
          // Use elementsFromPoint to find the actual cell under cursor (same as handleDrop)
          const elementsUnderCursor = document.elementsFromPoint(e.clientX, e.clientY);
          const cellElement = elementsUnderCursor.find(el => el.tagName === 'TD');

          if (cellElement) {
            // Get day from column index (table has 7 cells representing Sun-Sat, no time column)
            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const rowElement = cellElement.parentElement;
            if (rowElement && rowElement.tagName === 'TR') {
              const cells = Array.from(rowElement.children);
              let dayIndex = cells.indexOf(cellElement);
              if (dayIndex < 0) dayIndex = 0;
              if (dayIndex > 6) dayIndex = 6;

              // Get time from row position
              let tableElement = cellElement;
              while (tableElement && tableElement.tagName !== 'TABLE') {
                tableElement = tableElement.parentElement;
              }

              if (tableElement) {
                const tableBounds = tableElement.getBoundingClientRect();
                const cellBounds = cellElement.getBoundingClientRect();
                const relativeY = cellBounds.top - tableBounds.top;
                const cellHeight = cellBounds.height;
                const halfHourIndex = Math.round(relativeY / cellHeight);
                const hour = Math.floor(halfHourIndex / 2);
                const minute = (halfHourIndex % 2) * 30;

    if (DEBUG) console.log(`[DRAG PREVIEW] Hovering over: ${dayNames[dayIndex]} ${hour}:${String(minute).padStart(2, '0')}`);
              }
            }
          }
        }
      }
      };

      const transformedAppointments = appointments.map((app) => ({
        ...app,
        mechanicId: app.Mech_assigned,
      }));
    if (DEBUG) console.log("transformedAppointments:", transformedAppointments);

      // const mechanicSchedules = mechanics.map((mechanic) => ({
      //   mechanic,
      //   appointments: appointments.filter((appointment) => appointment.Mech_assigned === mechanic.user_id),
      // }));

      const [/* showMechanicDayView */, setShowMechanicDayView] = useState(false);

      const handleDayCellClick = (startDate) => {
    if (DEBUG) console.log("Hey handleDayCellClick Handler, start date:", startDate);
        setShowMechanicDayView(true);
        setCurrentDate(startDate);

        setCurrentViewName("Day");
      };

    // Normalize various date inputs (Date | string | number) to a valid Date in local time
    const toLocalDate = (val) => {
      if (!val) return new Date('Invalid');
      if (val instanceof Date) return new Date(val.getTime());
      if (typeof val === 'number') return new Date(val);
      if (typeof val === 'string') {
        const s = val.includes('T') ? val : val.replace(' ', 'T');
        const d = new Date(s);
        if (!isNaN(d.getTime())) return d;
        const p = Date.parse(val);
        return isNaN(p) ? new Date('Invalid') : new Date(p);
      }
      const d = new Date(val);
      return isNaN(d.getTime()) ? new Date('Invalid') : d;
    };

    // Validate if mechanic has availability for the task time slot
    const validateMechanicAvailability = useCallback((mechanicId, startDate, endDate, excludeTaskId = null) => {
      if (!mechanicId) return { valid: false, reason: "No mechanic assigned" };

      const taskStart = toLocalDate(startDate);
      const taskEnd = toLocalDate(endDate);
      if (isNaN(taskStart.getTime()) || isNaN(taskEnd.getTime())) {
        return { valid: false, reason: 'Invalid task time range' };
      }

    if (DEBUG) console.log("Validating availability for mechanic:", mechanicId, "time:", taskStart, "-", taskEnd);
    if (DEBUG) console.log("  Total availability blocks:", (availability || []).length);
      if (availability && availability.length > 0) {
        const sample = availability[0];
    if (DEBUG) console.log("  Sample availability block (raw):", {
          mechanic_id: sample.mechanic_id,
          startDate: sample.startDate,
          endDate: sample.endDate,
          mech_status: sample.mech_status,
          startDate_parsed: toLocalDate(sample.startDate),
          endDate_parsed: toLocalDate(sample.endDate),
        });
      }

      // OPTIMIZED: Only check blocks that could possibly overlap with our task time range
      // Using interval overlap: block overlaps if (block.end > task.start AND block.start < task.end)
      // IMPORTANT: Include blocks occupied by the task being resized (excludeTaskId)
      const relevantAvailability = (availability || []).filter(a => {
        if (a.mechanic_id != mechanicId) return false; // Use == to handle type coercion

        // Include blocks that are:
        // 1. Available (mech_status === 'Available'), OR
        // 2. Occupied by the current task being resized (task_id matches excludeTaskId)
        const isAvailable = a.mech_status === 'Available';
        const isOccupiedByCurrentTask = excludeTaskId && (a.task_id == excludeTaskId);
        if (!isAvailable && !isOccupiedByCurrentTask) return false;

        const availStart = toLocalDate(a.startDate);
        const availEnd = toLocalDate(a.endDate);
        if (isNaN(availStart.getTime()) || isNaN(availEnd.getTime())) return false;

        // Interval overlap check: blocks that could possibly contain or overlap with our task
        return availEnd > taskStart && availStart < taskEnd;
      });

    if (DEBUG) console.log("Relevant availability blocks for this time range:", relevantAvailability.length);
      relevantAvailability.forEach((avail, idx) => {
        const availStart = toLocalDate(avail.startDate);
        const availEnd = toLocalDate(avail.endDate);
    if (DEBUG) console.log(`  Block ${idx}: ${availStart} - ${availEnd} (status: ${avail.mech_status}, raw: ${avail.startDate} - ${avail.endDate})`);
      });

      if (relevantAvailability.length === 0) {
        return { valid: false, reason: "Mechanic has no availability for this time" };
      }

      // Prevent assigning during sick/vacation time (also optimized with interval overlap)
      const leaveOverlaps = (availability || []).some(a => {
        if (!(a.mech_status === 'Requested off' && a.mechanic_id == mechanicId)) return false;
        const ls = toLocalDate(a.startDate);
        const le = toLocalDate(a.endDate);
        if (isNaN(ls.getTime()) || isNaN(le.getTime())) return false;
        // Interval overlap: leave block overlaps with task
        return le > taskStart && ls < taskEnd;
      });
      if (leaveOverlaps) {
        return { valid: false, reason: 'Mechanic has sick/vacation time during this slot' };
      }

      // Check if the task time is fully covered by the UNION of availability slots
      // Sort blocks by start time and merge overlapping/adjacent ones
      const sortedBlocks = relevantAvailability
        .map(a => ({
          start: toLocalDate(a.startDate).getTime(),
          end: toLocalDate(a.endDate).getTime()
        }))
        .filter(b => !isNaN(b.start) && !isNaN(b.end))
        .sort((a, b) => a.start - b.start);

      // Merge overlapping/adjacent blocks
      const mergedBlocks = [];
      for (const block of sortedBlocks) {
        if (mergedBlocks.length === 0) {
          mergedBlocks.push({ ...block });
        } else {
          const last = mergedBlocks[mergedBlocks.length - 1];
          // If blocks overlap or are adjacent (within 1 second), merge them
          if (block.start <= last.end + 1000) {
            last.end = Math.max(last.end, block.end);
          } else {
            mergedBlocks.push({ ...block });
          }
        }
      }

      // Check if any merged block fully covers the task
      const taskStartMs = taskStart.getTime();
      const taskEndMs = taskEnd.getTime();
      const isFullyCovered = mergedBlocks.some(block =>
        taskStartMs >= block.start && taskEndMs <= block.end
      );

      if (DEBUG && isFullyCovered) console.log("✓ Task fits within merged availability blocks");

      if (!isFullyCovered) {
        if (DEBUG) console.log("✗ Task does NOT fit within merged availability blocks");
        return { valid: false, reason: "Mechanic is not available for the entire task duration" };
      }

      // Check for overlapping tasks with the same mechanic
      const overlappingTasks = (appointments || []).filter(apt => {
        // Skip availability/leave blocks - only check actual tasks
        if (apt.mech_status === 'Available' || apt.mech_status === 'Requested off') return false;

        // Exclude the task being moved (handle both scheduler id and backend task_id)
        if (excludeTaskId != null) {
          const ex = String(excludeTaskId);
          const isSelf = (apt.id != null && String(apt.id) === ex) || (apt.task_id != null && String(apt.task_id) === ex);
          if (isSelf) return false;
        }
        if (apt.mechanic_id != mechanicId && apt.mechanic != mechanicId) return false;

        const aptStart = toLocalDate(apt.startDate);
        const aptEnd = toLocalDate(apt.endDate);
        if (isNaN(aptStart.getTime()) || isNaN(aptEnd.getTime())) {
    if (DEBUG) console.log(`    Skipping task ${apt.task_id} with invalid dates: ${apt.startDate} - ${apt.endDate}`);
          return false;
        }

        // Log the raw database values vs parsed values for debugging timezone issues
        if (apt.calendar_start && apt.calendar_end) {
    if (DEBUG) console.log(`    Task ${apt.task_id} times: DB(${apt.calendar_start} - ${apt.calendar_end}) → Local(${aptStart.toISOString()} - ${aptEnd.toISOString()})`);
        }

        // Check if times overlap
        return taskStart < aptEnd && taskEnd > aptStart;
      });

    if (DEBUG) console.log(`  Overlapping tasks for mechanic ${mechanicId}:`, overlappingTasks.length);
      if (overlappingTasks.length > 0) {
        overlappingTasks.forEach(task => {
    if (DEBUG) console.log(`    - Task ${task.task_id}: ${toLocalDate(task.startDate)} - ${toLocalDate(task.endDate)} (${task.service_info || 'No description'}) [Status: ${task.status}]`);
        });
        return {
          valid: false,
          reason: `Mechanic already has a task scheduled during this time: ${overlappingTasks[0].service_info || 'Task'} (${overlappingTasks[0].status})`
        };
      }

      return { valid: true };
    }, [appointments, availability]);

      const postUpdateTask = useCallback(async (appointment, prevappointment) => {
      // Track start time to ensure minimum 750ms loading duration
      const operationStartTime = Date.now();

      // Freeze current display to avoid flicker while persisting + refreshing
      setIsRefreshing(true);
      // Take a local snapshot that already includes the updated appointment,
      // without relying on async state resolution of setAppointments.
      try {
        const snapshot = (appointments || []).map(a => (
          String(a?.id) === String(appointment?.id) ||
          (a?.task_id != null && appointment?.task_id != null && String(a.task_id) === String(appointment.task_id))
        ) ? appointment : a);
        setFrozenAppointments(snapshot);
      } catch (_) {
        setFrozenAppointments((prev) => (appointments ? [...appointments] : prev));
      }
      setFrozenAvailability((prev) => (availability ? [...availability] : prev));
    if (DEBUG) console.log("[Calendar][postUpdateTask] Begin", {
        prev: {
          start: prevappointment?.startDate,
          end: prevappointment?.endDate,
          mechanic_id: prevappointment?.mechanic_id,
        },
        next: {
          start: appointment?.startDate || appointment?.calendar_start,
          end: appointment?.endDate || appointment?.calendar_end,
          mechanic_id: appointment?.mechanic_id,
        },
        task_id: appointment?.task_id,
      });

      // Normalize seconds/millis and always snap to the nearest 15-minute boundary
      // so calendar_start/calendar_end are in sync with the current startDate/endDate.
      try {
        const toStr = (d) => formatDateTimeUTC(new Date(d).getTime());
        const snapQuarter = (val) => roundToNearestQuarterHour(toLocalDate(val));
        const s = snapQuarter(appointment.startDate || appointment.calendar_start);
        const e = snapQuarter(appointment.endDate || appointment.calendar_end);
        if (s) appointment.calendar_start = toStr(s);
        if (e) appointment.calendar_end = toStr(e);
      } catch (err) { void err; }

      // Check if the task is being moved to a different day
      const prevStartForCompare = prevappointment?.startDate || prevappointment?.calendar_start || null;
      const newStartForCompare = appointment?.startDate || appointment?.calendar_start || null;
      const prevDate = prevStartForCompare ? new Date(prevStartForCompare).toDateString() : null;
      const newDate = newStartForCompare ? new Date(newStartForCompare).toDateString() : null;
      const dateChanged = !!(prevDate && newDate && prevDate !== newDate);

      if (dateChanged) {
        const confirmMove = window.confirm(
          "You are moving this task to a different day. Please contact the customer to inform them of the schedule change.\n\nDo you want to continue?"
        );
        if (!confirmMove) {
          // Calculate remaining time for smooth UX (no DB commit delay needed - user cancelled)
          const elapsedTime = Date.now() - operationStartTime;
          const minimumLoadingTime = 750;
          const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
          setTimeout(() => {
            setIsRefreshing(false);
            setFrozenAppointments(null);
            setFrozenAvailability(null);
            refreshScheduler();
          }, remainingTime);
          return;
        }
      }

      // Validate mechanic availability (client-side check)
      const mechanicId = appointment.mechanic_id || appointment.mechanic;
      if (mechanicId) {
        const validation = validateMechanicAvailability(
          mechanicId,
          appointment.startDate,
          appointment.endDate,
          (appointment.task_id ?? appointment.id) // Prefer backend task_id; fallback to scheduler id
        );

        if (!validation.valid) {
          // Show error toast and ensure minimum loading time
          const elapsedTime = Date.now() - operationStartTime;
          const minimumLoadingTime = 750;
          const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
          setTimeout(() => {
            setSnackbar({ open: true, message: `Cannot assign task: ${validation.reason}`, severity: 'error' });
            refreshScheduler();
          }, remainingTime);
          return;
        }
      }

      // Build normalized payloads for server endpoints (use 15-minute snapped strings)
      const toQuarterHourStr = (val) => {
        const d = roundToNearestQuarterHour(toLocalDate(val));
        return formatDateTimeUTC(new Date(d).getTime());
      };
      const nextPayload = {
        task_id: appointment.task_id,
        sc_id: appointment.sc_id,
        mechanic_id: appointment.mechanic_id,
        calendar_start: appointment.calendar_start || toQuarterHourStr(appointment.startDate),
        calendar_end: appointment.calendar_end || toQuarterHourStr(appointment.endDate),
        status: appointment.status,
        service_info: appointment.service_info,
      };
    if (DEBUG) console.log('[postUpdateTask] Sending payload to backend:', {
        ...nextPayload,
        startDate_from_appointment: appointment.startDate?.toString(),
        endDate_from_appointment: appointment.endDate?.toString(),
      });

      // Validate required fields before making API calls
      if (!nextPayload.mechanic_id || !nextPayload.sc_id || !nextPayload.calendar_start || !nextPayload.calendar_end) {
        console.error('[postUpdateTask] Missing required fields in payload:', nextPayload);
        const elapsedTime = Date.now() - operationStartTime;
        const minimumLoadingTime = 750;
        const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
        setTimeout(() => {
          setSnackbar({
            open: true,
            message: 'Cannot update task: missing required information',
            severity: 'error'
          });
          refreshScheduler();
        }, remainingTime);
        return;
      }

      try {
        const res = await fetch(envVariable.serverId + `/mechanicSlots/checkSlotsValid.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(nextPayload),
        });

        const jsonRes = await res.json();

        if (jsonRes.type === "failure" || jsonRes.type === "error") {
          // Show error toast and ensure minimum loading time
          const elapsedTime = Date.now() - operationStartTime;
          const minimumLoadingTime = 750;
          const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
          setTimeout(() => {
            setSnackbar({
              open: true,
              message: jsonRes.message || 'Cannot assign task: Mechanic not available for this time slot',
              severity: 'error'
            });
            refreshScheduler();
          }, remainingTime);
          return;
        }
      } catch (checkError) {
        console.error('[postUpdateTask] checkSlotsValid failed:', checkError);
        // Show error and rollback
        const elapsedTime = Date.now() - operationStartTime;
        const minimumLoadingTime = 750;
        const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
        setTimeout(() => {
          setSnackbar({
            open: true,
            message: 'Cannot validate mechanic availability. Please try again.',
            severity: 'error'
          });
          refreshScheduler();
        }, remainingTime);
        return;
      }

      // We no longer mutate individual 30-minute availability slots when
      // moving/resizing tasks. The availability table represents working
      // hours + leave; task overlap/conflict is enforced via tasks and
      // validateMechanicAvailability/checkSlotsValid.
      try {
        const response = await fetch(envVariable.serverId + `/mechanicSlots/updateTaskSlot.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(nextPayload),
        });
        try {
          const slotJson = await response.json();
    if (DEBUG) console.log("[Calendar][postUpdateTask] updateTaskSlot response", slotJson);
        } catch (_) {
          // Some legacy endpoints may not return JSON; ignore if HTTP OK.
        }
      } catch (e) {
        console.warn('[Calendar][postUpdateTask] Task slot update failed (non-critical):', e);
      }

      // Persist task record start/end so duration sticks across refresh
      // NOTE: We do NOT update appointment_time here - that's the customer drop-off time and should only be manually edited
      try {
        const taskUpdatePayload = {
          task_id: appointment.task_id,
          calendar_start: appointment.calendar_start || appointment.startDate,
          calendar_end: appointment.calendar_end || appointment.endDate,
        };
        // Always include mechanic_id if it exists to ensure assignment persists
        if (appointment.mechanic_id !== undefined && appointment.mechanic_id !== null) {
          taskUpdatePayload.mechanic_id = appointment.mechanic_id;
        }
    if (DEBUG) console.log("[Calendar][postUpdateTask] updateTask.php payload", taskUpdatePayload);
        const tuRes = await fetch(envVariable.serverId + `/SCO/updateTask.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(taskUpdatePayload),
        });
        const tuText = await tuRes.text();
        try {
          const maybeJson = JSON.parse(tuText);
    if (DEBUG) console.log("[Calendar][postUpdateTask] updateTask.php response (JSON)", maybeJson);
        } catch {
    if (DEBUG) console.log("[Calendar][postUpdateTask] updateTask.php response (text)", tuText);
        }
        // Remember last successfully updated task so we can verify it appears after refresh
        lastTaskOpRef.current = {
          task_id: appointment.task_id,
          mechanic_id: appointment.mechanic_id,
          calendar_start: appointment.calendar_start || appointment.startDate,
          calendar_end: appointment.calendar_end || appointment.endDate,
          status: appointment.status,
          service_info: appointment.service_info,
        };
    if (DEBUG) console.log('[Calendar][postUpdateTask] recorded lastTaskOp', lastTaskOpRef.current);
      } catch (e) {
        console.warn('Task end-time update failed (non-critical):', e);
      }

      if (prevappointment && (appointment.mechanic_id !== prevappointment.mechanic_id)) {
        try {
          await fetch(envVariable.serverId + `/mechanicSlots/assignTaskNotif.php`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(appointment),
          });
        } catch (err) {
          console.warn('Notification send failed (non-critical):', err);
        }
      }

    if (DEBUG) console.log("[Calendar][postUpdateTask] Done; calculating delay for smooth UX");

      // Calculate remaining time to reach minimum 750ms loading duration
      // ALSO ensure at least 500ms after operations complete for DB transaction commit
      const elapsedTime = Date.now() - operationStartTime;
      const minimumLoadingTime = 750;
      const minimumCommitDelay = 500;
      const remainingTime = Math.max(minimumLoadingTime - elapsedTime, minimumCommitDelay);

    if (DEBUG) console.log(`[Calendar][postUpdateTask] Elapsed: ${elapsedTime}ms, will wait ${remainingTime}ms more (ensuring both 750ms total and 500ms commit time)`);

      // Ensure minimum 750ms loading experience AND 500ms DB commit time, then refresh
      setTimeout(() => {
        // Show success toast
        const mechanicChanged = prevappointment && (appointment.mechanic_id !== prevappointment.mechanic_id);
        if (mechanicChanged) {
          setSnackbar({
            open: true,
            message: 'Task successfully reassigned',
            severity: 'success'
          });
        } else {
          setSnackbar({
            open: true,
            message: 'Task updated successfully',
            severity: 'success'
          });
        }
        refreshScheduler();
      }, remainingTime);
    }, [appointments, availability, refreshScheduler, validateMechanicAvailability]);
    // Removed EditTask (moved to slot update flow to avoid conflicting persistence)
    // eslint-disable-next-line no-unused-vars
    const renderedcommitChanges = useCallback(({ added, changed, deleted }) => {
    if (DEBUG) console.log('[renderedcommitChanges] Called with:', { added, changed, deleted });

      if (added) {
    if (DEBUG) console.log('[renderedcommitChanges] WARNING: "added" parameter received but not handled!', added);
      }

      // Handle drag/resize of existing tasks with proper validation.
      if (changed) {
    if (DEBUG) console.log('[renderedcommitChanges] Processing changed tasks');
        for (const appointmentId in changed) {
          const key = String(appointmentId);
          const appointment = appointments.find(a => String(a.id) === key || String(a.task_id) === key);
          const changedData = changed[appointmentId];
    if (DEBUG) console.log('[renderedcommitChanges] Processing appointment:', { appointmentId, key, appointment, changedData });

          // Skip availability/leave blocks
          if (appointment?.mech_status === 'Available' || appointment?.mech_status === 'Requested off') {
    if (DEBUG) console.log('[renderedcommitChanges] Skipping availability/leave block');
            continue;
          }

          if (changedData.startDate || changedData.endDate || changedData.mechanic_id) {
    if (DEBUG) console.log('[renderedcommitChanges] Time or mechanic change detected');
            // Snap to 15-minute grid so drag resolution matches what the user sees
            const snapQuarterHour = (val) => {
              const d = toLocalDate(val);
              return roundToNearestQuarterHour(new Date(d));
            };
            const desiredStart = snapQuarterHour(changedData.startDate || appointment?.startDate);
            const desiredEnd = snapQuarterHour(changedData.endDate || appointment?.endDate);
    if (DEBUG) console.log('[renderedcommitChanges] Snapped times:', { desiredStart, desiredEnd });

            // Check if user dragged to a different mechanic column (in day view)
            const currentMechanic = appointment?.mechanic_id || appointment?.mechanic || null;
            const targetMechanic = changedData.mechanic_id !== undefined ? changedData.mechanic_id : currentMechanic;
            const mechanicChanged = targetMechanic != null && currentMechanic != null && String(targetMechanic) !== String(currentMechanic);

    if (DEBUG) console.log('[renderedcommitChanges] Current mechanic:', currentMechanic, 'Target mechanic:', targetMechanic, 'Changed:', mechanicChanged);

            // 1) If mechanic changed (dragged to different column), validate and assign to new mechanic
            if (mechanicChanged) {
    if (DEBUG) console.log('[renderedcommitChanges] Mechanic change detected via drag to different column');
              const excludeId = appointment?.task_id ?? appointment?.id;
              const targetOk = validateMechanicAvailability(targetMechanic, desiredStart, desiredEnd, excludeId).valid;
    if (DEBUG) console.log('[renderedcommitChanges] Target mechanic validation:', targetOk);
              if (targetOk) {
    if (DEBUG) console.log('[renderedcommitChanges] ✓ Target mechanic OK, reassigning task');
                const updatedAppointment = {
                  ...appointment,
                  ...changedData,
                  startDate: desiredStart,
                  endDate: desiredEnd,
                  calendar_start: formatDateTimeUTC(new Date(desiredStart).getTime()),
                  calendar_end: formatDateTimeUTC(new Date(desiredEnd).getTime()),
                  mechanic_id: targetMechanic,
                };
                setAppointments(prev => prev.map(a => (
                  String(a?.id) === String(updatedAppointment?.id) ||
                  (a?.task_id != null && updatedAppointment?.task_id != null && String(a.task_id) === String(updatedAppointment.task_id))
                ) ? updatedAppointment : a));
                try {
                  lastTaskOpRef.current = {
                    task_id: updatedAppointment.task_id ?? appointment.task_id ?? appointment.id,
                    mechanic_id: updatedAppointment.mechanic_id,
                    calendar_start: updatedAppointment.calendar_start,
                    calendar_end: updatedAppointment.calendar_end,
                    status: updatedAppointment.status,
                    service_info: updatedAppointment.service_info,
                  };
                } catch (_) { /* noop */ }
                postUpdateTask(updatedAppointment, appointment);
                try {
                  const mech = mechDate.find(m => m.user_id == targetMechanic);
                  if (mech) {
                    setSnackbar({ open: true, message: `Task reassigned to ${mech.first_name} ${mech.last_name}`, severity: 'success' });
                  } else {
                    setSnackbar({ open: true, message: 'Task reassigned', severity: 'success' });
                  }
                } catch (_) { /* noop */ }
                return; // Done handling this change
              } else {
    if (DEBUG) console.log('[renderedcommitChanges] ✗ Target mechanic not available');
                setSnackbar({ open: true, message: 'Target mechanic is not available for this time slot', severity: 'error' });
                return; // Cancel the change
              }
            }

            // 2) If task already has a mechanic and that same mechanic remains valid, apply inline (no dialog)
            if (currentMechanic) {
              const excludeId = appointment?.task_id ?? appointment?.id;
              const currentOk = validateMechanicAvailability(currentMechanic, desiredStart, desiredEnd, excludeId).valid;
    if (DEBUG) console.log('[renderedcommitChanges] Current mechanic validation:', currentOk);
              if (currentOk) {
    if (DEBUG) console.log('[renderedcommitChanges] ✓ Current mechanic OK, updating inline');
                const updatedAppointment = {
                  ...appointment,
                  ...changedData,
                  startDate: desiredStart,
                  endDate: desiredEnd,
                  calendar_start: formatDateTimeUTC(new Date(desiredStart).getTime()),
                  calendar_end: formatDateTimeUTC(new Date(desiredEnd).getTime()),
                  mechanic_id: currentMechanic,
                };
    if (DEBUG) console.log('[renderedcommitChanges] updatedAppointment times:', {
                  desiredStart_local: desiredStart.toString(),
                  desiredEnd_local: desiredEnd.toString(),
                  calendar_start_utc: updatedAppointment.calendar_start,
                  calendar_end_utc: updatedAppointment.calendar_end,
                });
                // Robust ID match (string or number, id or task_id)
                setAppointments(prev => prev.map(a => (
                  String(a?.id) === String(updatedAppointment?.id) ||
                  (a?.task_id != null && updatedAppointment?.task_id != null && String(a.task_id) === String(updatedAppointment.task_id))
                ) ? updatedAppointment : a));
                // Record intent immediately so refresh reconciliation prefers these values
                try {
                  lastTaskOpRef.current = {
                    task_id: updatedAppointment.task_id ?? appointment.task_id ?? appointment.id,
                    mechanic_id: updatedAppointment.mechanic_id ?? appointment.mechanic_id ?? appointment.mechanic ?? null,
                    calendar_start: updatedAppointment.calendar_start,
                    calendar_end: updatedAppointment.calendar_end,
                    status: updatedAppointment.status,
                    service_info: updatedAppointment.service_info,
                  };
                } catch (_) { /* noop */ }
                postUpdateTask(updatedAppointment, appointment);
                try {
                  const mech = mechDate.find(m => m.user_id == currentMechanic);
                  if (mech) {
                    setSnackbar({ open: true, message: `Task updated; kept ${mech.first_name} ${mech.last_name}`, severity: 'success' });
                  } else {
                    setSnackbar({ open: true, message: 'Task updated', severity: 'success' });
                  }
                } catch (_) { /* noop */ }
                return; // Done handling this change
              }
            }

            // 3) Find all mechanics that can fully cover the new time without overlaps
    if (DEBUG) console.log('[renderedcommitChanges] Current mechanic not OK or no mechanic, finding available mechanics');
            const excludeId2 = appointment?.task_id ?? appointment?.id;
            const availableMechanics = mechDate.filter(mech => (
              validateMechanicAvailability(mech.user_id, desiredStart, desiredEnd, excludeId2).valid
            ));
    if (DEBUG) console.log('[renderedcommitChanges] Available mechanics:', availableMechanics.length);

            if (availableMechanics.length === 0) {
    if (DEBUG) console.log('[renderedcommitChanges] ✗ No mechanics available');
              setSnackbar({ open: true, message: 'No mechanics available for this time slot', severity: 'error' });
              return; // Cancel the change
            }

            if (availableMechanics.length === 1) {
    if (DEBUG) console.log('[renderedcommitChanges] ✓ Only one mechanic available');

              // In day view, don't auto-assign to a different mechanic - fail instead
              // This prevents confusion when user drags to a specific mechanic's row
              if (currentViewName === 'Day') {
                const only = availableMechanics[0];
                // If the only available mechanic is not the one the user tried to drop on, fail
                if (targetMechanic && only.user_id != targetMechanic) {
    if (DEBUG) console.log('[renderedcommitChanges] ✗ Day view: Available mechanic does not match target');
                  setSnackbar({
                    open: true,
                    message: 'Target mechanic is not available for this time slot',
                    severity: 'error'
                  });
                  return; // Cancel the change
                }
              }

              // Auto-assign to the only available mechanic
              const only = availableMechanics[0];
              const updatedAppointment = {
                ...appointment,
                ...changedData,
                startDate: desiredStart,
                endDate: desiredEnd,
                calendar_start: formatDateTimeUTC(new Date(desiredStart).getTime()),
                calendar_end: formatDateTimeUTC(new Date(desiredEnd).getTime()),
                mechanic_id: only.user_id,
              };
              // Robust ID match (string or number, id or task_id)
              setAppointments(prev => prev.map(a => (
                String(a?.id) === String(updatedAppointment?.id) ||
                (a?.task_id != null && updatedAppointment?.task_id != null && String(a.task_id) === String(updatedAppointment.task_id))
              ) ? updatedAppointment : a));
              // Record intent immediately for reconciliation on refresh
              try {
                lastTaskOpRef.current = {
                  task_id: updatedAppointment.task_id ?? appointment.task_id ?? appointment.id,
                  mechanic_id: updatedAppointment.mechanic_id ?? appointment.mechanic_id ?? appointment.mechanic ?? null,
                  calendar_start: updatedAppointment.calendar_start,
                  calendar_end: updatedAppointment.calendar_end,
                  status: updatedAppointment.status,
                  service_info: updatedAppointment.service_info,
                };
              } catch (_) { /* noop */ }
              postUpdateTask(updatedAppointment, appointment);
              setSnackbar({ open: true, message: `Task assigned to ${only.first_name} ${only.last_name}`, severity: 'success' });
              return;
            }

            // 4) Multiple mechanics available → prompt user to choose
    if (DEBUG) console.log('[renderedcommitChanges] Multiple mechanics available, showing dialog');
            setPendingTaskDrop({
              appointmentId: appointment ? appointment.id : key,
              appointment,
              changedData: { ...changedData, startDate: desiredStart, endDate: desiredEnd },
              availableMechanics,
            });
            setMechanicSelectOpen(true);
            return; // Pause the change until user selects a mechanic
          } else {
    if (DEBUG) console.log('[renderedcommitChanges] No time change in changedData');
          }
        }
      }

      // No time-related changes; keep legacy update path for anything else
    if (DEBUG) console.log('[renderedcommitChanges] Using legacy update path');
        setAppointments((prevCalendarData) => {
          let data = [...prevCalendarData];

          if (changed) {
            data = data.map((appointment) => {
                if (changed[appointment.id]) {
              const changedAppointment = { ...changed[appointment.id] };
              if (changedAppointment.startDate) {
                changedAppointment.startDate = normalizeToMinuteAndSnap(changedAppointment.startDate, 2);
              }
              if (changedAppointment.endDate) {
                changedAppointment.endDate = normalizeToMinuteAndSnap(changedAppointment.endDate, 2);
              }
              const nextStart = changedAppointment.startDate ? normalizeToMinuteAndSnap(changedAppointment.startDate, 2) : appointment.startDate;
              const nextEnd = changedAppointment.endDate ? normalizeToMinuteAndSnap(changedAppointment.endDate, 2) : appointment.endDate;

                const updatedAppointment = {
                  ...appointment,
                ...changedAppointment,
                startDate: nextStart,
                endDate: nextEnd,
                calendar_start: formatDateTimeUTC(new Date(nextStart).getTime()),
                calendar_end: formatDateTimeUTC(new Date(nextEnd).getTime()),
                };

              if (appointment.status !== "Available") {
                postUpdateTask(updatedAppointment, appointment);
                }

                return updatedAppointment;
              }
              return appointment;
            });
          }

          return data;
        });
    }, [appointments, availability, currentViewName, mechDate, postUpdateTask, setSnackbar, setPendingTaskDrop, setMechanicSelectOpen, validateMechanicAvailability]);

    // eslint-disable-next-line no-unused-vars
    const parseDate = (dateStr) => {
      try {
        return new Date(dateStr);
      } catch (error) {
        // Fallback if parsing fails
        const parsedDate = Date.parse(dateStr);
        if (isNaN(parsedDate)) {
          throw new Error(`Invalid date format: ${dateStr}`);
        }
        return new Date(parsedDate);
      }
    };
      const [addedAppointment, setAddedAppointment] = useState({});

      const onAddedAppointmentChange = React.useCallback((appointment) => {
        setAddedAppointment(appointment);
        // setIsAppointmentBeingCreated(true);
      }, []);

    // Custom GroupingPanel.Cell component with sticky positioning for mechanic headers
    const StickyGroupingPanelCell = ({ group, ...restProps }) => {
      // Extract mechanic ID from the group to get the correct color
      // The 'group.id' is the mechanic_id value from the appointment data
      const mechanicId = group?.id;
      const mechanicColor = mechanicId ? getMechanicColor(mechanicId) : null;

      console.log('[StickyGroupingPanelCell] group:', group, 'mechanicId:', mechanicId, 'color:', mechanicColor);

    return (
        <GroupingPanel.Cell
        {...restProps}
          group={group}
        style={{
            position: 'sticky',
            top: 0,
            zIndex: 101,
            backgroundColor: mechanicColor || 'white', // Use mechanic color for background
            color: mechanicColor ? 'white' : 'inherit', // White text on colored background
            fontWeight: mechanicColor ? 'bold' : 'normal',
            ...restProps.style,
          }}
        />
      );
    };

    const CustomDayScaleCell = ({ startDate, today, ...restProps }) => {
    const fmt = d => d.toLocaleDateString("en-CA");
    const isExpanded = expandedDate && fmt(startDate) === fmt(expandedDate);

    const weekday = startDate
      .toLocaleDateString("en-US", { weekday: "short" })
      .toUpperCase();  // MON
    const dayNum = startDate.getDate(); // 14

    return (
      <th
        {...restProps}
        style={{
          backgroundColor: isExpanded ? "#d1ecf1" : today ? "#d9d9d9" : undefined,
          width: isExpanded ? "480px" : "200px",
          minWidth: isExpanded ? "480px" : "200px",
          textAlign: "center",
          padding: "4px 2px",
          borderBottom: "1px solid #ccc",
          color: "#333",
        }}
      >
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          lineHeight: "1.1",
        }}>
          <span style={{ fontSize: "10px", fontWeight: 700 }}>
            {weekday}
          </span>
          <span style={{ fontSize: "20px", fontWeight: 700, marginTop: "2px" }}>
            {dayNum}
          </span>
        </div>
      </th>
    );
  };










  const CustomTimeTableCell = ({  startDate, endDate, computeMeta, ...restProps }) => {
      const fmtDate = d => new Date(d).toLocaleDateString("en-CA"); // YYYY-MM-DD

      const isExpanded = expandedDate && fmtDate(startDate) === fmtDate(expandedDate);
    // eslint-disable-next-line no-unused-vars
    // const handleMouseDown = (event) => {
    //   event.stopPropagation();
    //   onCellClick({ start: startDate });
    // };
    // // eslint-disable-next-line no-unused-vars
    // const handleMouseUp = (event) => {
    //   onCellClick({ end: startDate });
    // };

    // computeMeta and overflow handlers are provided by parent component

    const meta = computeMeta ? computeMeta(startDate, endDate) : null;
    const maxTasksInline = 2;
    // Only count ASSIGNED tasks for overflow (unassigned tasks don't show in week view)
    const assignedTasks = meta?.tasksInCell?.filter(t => t.mechanic_id || t.mechanic) || [];
    const overflow = assignedTasks.length > maxTasksInline ? (assignedTasks.length - maxTasksInline) : 0;

    const handleCellClick = (e) => {
      try {
        if (typeof restProps.onCreate === 'function') {
          // Only allow click to create when availability is present in this cell
          if (meta && meta.availableCount > 0) {
            e.stopPropagation();
            restProps.onCreate(startDate);
          }
        }
      } catch (_) { /* noop */ }
    };

    // Week view expansion styles (don't apply in day view)
    const expansionStyles = currentViewName === 'Week' && isExpanded ? {
      width: "500px",
      minWidth: "500px",
    } : (currentViewName === 'Week' ? {
      width: "200px",
      minWidth: "200px",
    } : {});

    return (
      <WeekView.TimeTableCell
        {...restProps}
        // onMouseDown={handleMouseDown}
        // onMouseUp={handleMouseUp}
        onClick={handleCellClick}
        onDoubleClick={handleCellClick}
        style={{
          position: "relative",
          ...expansionStyles,
          cursor: (meta && meta.availableCount > 0) ? 'pointer' : undefined,
          // Red tint border for unassigned tasks (Week view only)
          ...(currentViewName === 'Week' && meta && meta.hasUnassigned ? {
            boxShadow: 'inset 0 0 0 2px rgba(229, 57, 53, 0.6)',
            backgroundColor: 'rgba(229, 57, 53, 0.05)'
          } : {})
        }}
      >
        {meta && (
          <>
            
          
            {/* Overflow/collapse indicator - show +X when collapsed with overflow, show - when expanded (Week view only) */}
            {currentViewName === 'Week' && (overflow > 0 || isExpanded) && (
              <div
              onMouseDownCapture={(e) => {
                e.stopPropagation(); // ⛔ stops capture-phase event
              }}
                // onClick={(e) => {e.stopPropagation();e.preventDefault(); onShowOverflow && onShowOverflow(e, meta.tasksInCell)}}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();

                  setExpandedDate((prev) =>
                    prev && new Date(prev).toDateString() === new Date(startDate).toDateString()
                      ? null // collapse if already expanded
                      : startDate // expand this date
                  );
                }}
                style={{
                  position: "absolute",
                  bottom: 2,
                  right: 6,
                  padding: "0 6px",
                  borderRadius: 10,
                  background: "rgba(0,0,0,0.5)",
                  color: "white",
                  fontSize: 11,
                  cursor: "pointer",
                  zIndex: 5,
                }}
                title={isExpanded ? "Collapse cell" : `+${overflow} more tasks`}
              >
                {isExpanded ? "−" : `+${overflow}`}
              </div>
            )}
            {/* Availability bubble - only show in Week view if count > 0 */}
            {currentViewName === 'Week' && meta.availableCount > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: 4,
                  // left: 4,
                  right:5,
                  width: 22,
                  height: 22,
                  borderRadius: 11,
                  background: "#1976d2",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  zIndex: 4,
                  opacity: 0.9,
                }}
                title={`Available mechanics: ${meta.availableCount}`}
              >
                {meta.availableCount}
              </div>
            )}

          </>
        )}
      </WeekView.TimeTableCell>
    );
  };

  const visibleAppointments = useMemo(
        () => getVisibleAppointments(schedulerData, 2, expandedDate),
        [schedulerData, expandedDate] // 👈 so it recomputes whenever expandedDate changes
      );


    const renderMechanicDayView = () => {
      const mechResources = [
        {
          fieldName: "mechanic_id",
          title: "Mechanics",
          instances: mechanics
            .filter((mechanic) => mechanic && mechanic.user_id && mechanic.first_name && mechanic.last_name)
            .map((mechanic) => ({
              id: mechanic.user_id,
              text: `${mechanic.first_name} ${mechanic.last_name}`,
              color: getMechanicColor(mechanic.user_id), // Use consistent color palette
            })),
        },
      ];
      const grouping = [
        {
          resourceName: "mechanic_id",
        },
      ];


      const formattedDate = (date) => date.toISOString().split("T")[0];

      const currentTasks = pendingBooking.filter((task) => {
        const parsedTaskDate = parseDate(task.appointment_time);
        const taskDateFormatted = formattedDate(parsedTaskDate);
        return taskDateFormatted === formattedDate(currentDate);
      });

      return (
        <div
        className="scheduler-day-scroll"
          style={{
            height: "85vh", // Adjust the height as needed
            overflowY: "auto", // Enable vertical scrolling if content overflows
          }}
        >
          <div className="scheduler-container" onDrop={handleDrop} onDragOver={allowDrop}>
            <Scheduler data={visibleAppointments}>
              <ViewState
                // currentDate={formattedDateTime(currentDate)}
                currentDate={currentDate.$d}
                onCurrentDateChange={handleCurrDateChange}
              />
              <EditingState onCommitChanges={renderedcommitChanges} 
              addedAppointment={addedAppointment}
              onAddedAppointmentChange={onAddedAppointmentChange}
              />
              <GroupingState grouping={grouping} />
              <DayView
                // startDayHour={6}
                // endDayHour={19.5}
                cellDuration={30}
                intervalCount={1}
                // currentDate={formattedDateTime(currentDate)}
                currentDate={currentDate.$d}
                dayScaleCellComponent={() => null}
                timeTableCellComponent={(props) => (
                  <CustomTimeTableCell
                    {...props}
                    computeMeta={computeCellMeta}
                    onCreate={openCreateTaskAt}
                  />
                )}
                // sdayScaleCellComponent={() => null}
              />
              <Appointments appointmentComponent={CustomRenderAppointment} />
              <Resources data={mechResources} mainResourceName="mechanic_id" />
              {/* <GroupingState grouping={grouping} />; */}
              <IntegratedGrouping />
              <IntegratedEditing />
            <GroupingPanel cellComponent={StickyGroupingPanelCell} />
              {/* <DragDropProvider /> */}
              {/* <DragDropProvider allowDrag={() => true} allowResize={() => true} /> */}
            <DragDropProvider
              allowDrag={allowDrag}
              allowResize={allowResize}
              draftAppointmentComponent={DraftAppointment}
            />

            </Scheduler>
            <div className="external-appointments-container">
              <div
                style={{
                  color: "white",
                  fontWeight: "600", // Adjust the height as needed
                }}
              >
                {/* {" "}
                Pending Tasks{" "} */}
              </div>
              {currentTasks.map((task, /* index */) => (
                <ExternalAppointment
                  key={task.task_id}
                  text={task.service_info}
                  data={task}
                  onDragStart={(e) => {
                    e.dataTransfer.setData("application/json", JSON.stringify(task));
                    e.dataTransfer.effectAllowed = "move";
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      );
    };

    // eslint-disable-next-line no-unused-vars -- Usage commented out around line 1616
    const TimeTableCell = (props) => (
      <DayView.TimeTableCell {...props} onClick={() => handleDayCellClick(props.startDate)} />
    );

  // Handle dialog close and refresh for availability/leave blocks
  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedBlock(null);
  };

  const handleDialogSuccess = () => {
    // Show loading spinner during refresh
    setIsRefreshing(true);
    // Refresh data without changing view or date
    refreshScheduler();
    // Don't forcibly turn off refreshing - let refreshScheduler handle it after data loads
    handleDialogClose();
  };

  return (
      <div style={{ display: 'flex', gap: '0', height: '100%' }}>
      {/* Main Scheduler */}
      <div ref={schedulerContainerRef} style={{ flex: 1, minWidth: 0 }} onDrop={currentViewName === 'Week' ? handleDrop : undefined} onDragOver={currentViewName === 'Week' ? allowDrop : undefined}>
        {/* Show banner when viewing a single mechanic */}
        {focusedMechanicId && currentViewName === 'Week' && (
          <div style={{
            backgroundColor: '#e3f2fd',
            borderBottom: '2px solid #2196F3',
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '14px',
            fontWeight: '500',
            color: '#1565c0'
          }}>
            <span>
              Viewing schedule for: {displayMechanics[0]?.first_name} {displayMechanics[0]?.last_name}
            </span>
            <Button
              variant="outlined"
              size="small"
              onClick={onExitFocus}
              sx={{
                borderColor: '#2196F3',
                color: '#2196F3',
                '&:hover': {
                  borderColor: '#1976d2',
                  backgroundColor: '#bbdefb'
                }
              }}
            >
              Back to All Mechanics
            </Button>
          </div>
        )}
        <Scheduler data={visibleAppointments}>
          <div>
            <div>
              <ViewState
                // currentDate={formattedDateTime(currentDate)}
                currentDate={currentDate.$d}
                onCurrentDateChange={handleCurrDateChange}
                currentViewName={currentViewName}
                data={appointments}
                position="top"
              />
            </div>
          </div>
          <EditingState onCommitChanges={renderedcommitChanges} />
          {/* <EditingState
            onCommitChanges={onCommitChanges}
            addedAppointment={addedAppointment}
            onAddedAppointmentChange={onAddedAppointmentChange}
          /> */}
          <IntegratedEditing />
          {currentViewName === "Day" && renderMechanicDayView()}
          {currentViewName === "Week" && (
            <WeekView
            name="Week"
              // startDayHour={6}
              // endDayHour={19.5}
              cellDuration={30}
              intervalCount={1}
              excludedDays={[]}
              dayScaleCellComponent={CustomDayScaleCell}
              // cellComponent={CustomDayCell}
              timeTableCellComponent={(props) => (
                <CustomTimeTableCell
                  {...props}
                  // onCellClick={handleCellClick}
                  computeMeta={computeCellMeta}
                  // onShowOverflow={onShowOverflow}
                  onCreate={openCreateTaskAt}
                />
              )}
            />
          )}
          <MonthView name="Month" timeTableCellComponent={monthCellComponent} />
          {/* <Appointments appointmentComponent={CustomAppointment} /> */}
          <Appointments appointmentComponent={currentViewName === "Month" ? HiddenAppointment : CustomAppointment} />
          <Resources
          data={[
            {
              fieldName: "mechanic_id",
              title: "Mechanics",
              instances: displayMechanics.map((mechanic) => {
                const color = getMechanicColor(mechanic.user_id);
                if (DEBUG) console.log('[Resources] Mechanic:', mechanic.first_name, mechanic.last_name, 'user_id:', mechanic.user_id, 'color:', color);
                return {
                  id: mechanic.user_id,
                  text: `${mechanic.first_name} ${mechanic.last_name}`,
                  color: color, // Use consistent color palette
                };
              }),
            },
          ]}
          mainResourceName="mechanic_id"
        />
        {currentViewName !== "Month" && <DragDropProvider
          allowDrag={allowDrag}
          allowResize={allowResize}
          draftAppointmentComponent={DraftAppointment}
        />}
        </Scheduler>
      </div>

      {/* Unassigned Tasks Column - hidden in Month view */}
      {currentViewName !== "Month" && (
        <div style={{
          width: '250px',
          borderLeft: '1px solid #ddd',
          backgroundColor: '#f9f9f9',
          overflowY: 'auto',
          height: 'calc(100vh - 200px)', // Adjust based on your header height
          padding: '10px',
        }}>
          <div style={{ marginBottom: '15px' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 'bold', color: '#333' }}>
              Unassigned Tasks ({unassignedTasks.length}{filterUnassignedByDate ? ` of ${allUnassignedTasks.length}` : ''})
            </h3>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '12px',
              color: '#666',
              cursor: 'pointer',
              userSelect: 'none',
            }}>
              <input
                type="checkbox"
                checked={filterUnassignedByDate}
                onChange={(e) => setFilterUnassignedByDate(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Show only selected day
            </label>
          </div>

          {unassignedTasks.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#999', padding: '20px 0', fontSize: '14px' }}>
              {filterUnassignedByDate ? 'No unassigned tasks for this day' : 'No unassigned tasks'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {unassignedTasks.map((task) => (
                <div
                  key={task.id || task.task_id}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData("application/json", JSON.stringify(task));
                    e.dataTransfer.effectAllowed = "move";

                    // Create a small custom drag image
                    const dragImage = document.createElement('div');
                    dragImage.style.position = 'absolute';
                    dragImage.style.top = '-1000px';
                    dragImage.style.backgroundColor = '#FF5722';
                    dragImage.style.color = 'white';
                    dragImage.style.padding = '8px 12px';
                    dragImage.style.borderRadius = '4px';
                    dragImage.style.fontSize = '12px';
                    dragImage.style.fontWeight = 'bold';
                    dragImage.style.border = '2px solid white';
                    dragImage.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
                    dragImage.style.whiteSpace = 'nowrap';
                    dragImage.style.maxWidth = '200px';
                    dragImage.style.overflow = 'hidden';
                    dragImage.style.textOverflow = 'ellipsis';
                    dragImage.textContent = task.service_info || task.title || 'Task';
                    document.body.appendChild(dragImage);
                    e.dataTransfer.setDragImage(dragImage, 10, 10);
                    // Clean up after drag starts
                    setTimeout(() => document.body.removeChild(dragImage), 0);
                  }}
                  onClick={(e) => {
                    // Only open dialog if not dragging
                    if (!e.defaultPrevented) {
                      setSidebarTask(task);
                      setSidebarMode('edit');
                      setSidebarOpen(true);
                    }
                  }}
                    style={{
                    backgroundColor: '#FF5722',
                      color: 'white',
                    padding: '12px',
                    borderRadius: '6px',
                    cursor: 'grab',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                  }}
                >
                  <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                    {task.service_info || task.title || 'Untitled Task'}
                  </div>
                  <div style={{ fontSize: '12px', opacity: 0.9 }}>
                    <b>Start:</b> {task.startDate ? parseUTCDateTime(task.startDate)?.toLocaleString([], {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : 'No time set'}
                  </div>
                  <div style={{ fontSize: '11px', opacity: 0.85 }}>
                    <b>Duration:</b> {(() => {
                      const start = parseUTCDateTime(task.startDate);
                      const end = parseUTCDateTime(task.endDate);
                      if (start && end) {
                        const durationMs = end - start;
                        const hours = Math.floor(durationMs / (1000 * 60 * 60));
                        const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
                        return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
                      }
                      return 'N/A';
                    })()}
                  </div>
                  {task.appointment_time && (
                    <div style={{ fontSize: '11px', opacity: 0.8 }}>
                      <b>Appt:</b> {parseUTCDateTime(task.appointment_time)?.toLocaleString([], {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  )}
                  {task.year && task.make && (
                    <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '4px' }}>
                      {task.year} {task.make} {task.model}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* <Popover
        open={Boolean(overflowAnchor)}
        anchorEl={overflowAnchor}
        onClose={closeOverflow}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <List dense sx={{ maxWidth: 320 }}>
          {overflowTasks.map((t, idx) => (
            <ListItem key={idx}>
                  <ListItemText
                primary={`${t.title || t.service_info || 'Task'}${(t.mechanic_id || t.mechanic) ? '' : ' (Unassigned)'}`}
                secondary={`${new Date(t.startDate).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})} - ${new Date(t.endDate).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}`}
                  />
                </ListItem>
              ))}
            </List>
          </Popover> */} 

      {/* Task Detail Modal */}
      <Dialog
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div>{sidebarMode === 'create' ? 'Create Task' : 'Edit Task'}</div>
              {sidebarMode === 'edit' && sidebarTask?.task_id && (
                <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '4px' }}>
                  Task ID: {sidebarTask.task_id}
                </div>
              )}
            </div>
            <IconButton onClick={() => setSidebarOpen(false)} size="small">
              <FontAwesomeIcon icon={faTimes} />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent>
          {sidebarTask && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', paddingTop: '10px' }}>
              {/* Service Info */}
              <TextField
                label="Service Info"
                fullWidth
                multiline
                rows={3}
                value={sidebarTask.service_info || ''}
                onChange={(e) => setSidebarTask({ ...sidebarTask, service_info: e.target.value })}
              />

              {/* Status */}
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={sidebarTask.status || 'Pending'}
                  label="Status"
                  onChange={(e) => setSidebarTask({ ...sidebarTask, status: e.target.value })}
                >
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Open">Open</MenuItem>
                  <MenuItem value="In-Progress">In Progress</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                </Select>
              </FormControl>

              {/* Mechanic Assignment */}
              <FormControl fullWidth>
                <InputLabel>Assign Mechanic</InputLabel>
                <Select
                  value={sidebarTask.mechanic_id || ''}
                  label="Assign Mechanic"
                  onChange={(e) => setSidebarTask({ ...sidebarTask, mechanic_id: e.target.value })}
                >
                  <MenuItem value="">Unassigned</MenuItem>
                  {mechDate.map((mech) => (
                    <MenuItem key={mech.user_id} value={mech.user_id}>
                      {mech.first_name} {mech.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Start Time */}
              <TextField
                label="Start Time"
                type="datetime-local"
                fullWidth
                value={(() => {
                  if (!sidebarTask.startDate) return '';
                  const d = new Date(sidebarTask.startDate);
                  if (isNaN(d.getTime())) return '';
                  // Format as local time, not UTC
                  const year = d.getFullYear();
                  const month = String(d.getMonth() + 1).padStart(2, '0');
                  const day = String(d.getDate()).padStart(2, '0');
                  const hours = String(d.getHours()).padStart(2, '0');
                  const minutes = String(d.getMinutes()).padStart(2, '0');
                  return `${year}-${month}-${day}T${hours}:${minutes}`;
                })()}
                onChange={(e) => setSidebarTask({ ...sidebarTask, startDate: e.target.value ? new Date(e.target.value) : null })}
                InputLabelProps={{ shrink: true }}
              />

              {/* End Time */}
              <TextField
                label="End Time"
                type="datetime-local"
                fullWidth
                value={(() => {
                  if (!sidebarTask.endDate) return '';
                  const d = new Date(sidebarTask.endDate);
                  if (isNaN(d.getTime())) return '';
                  // Format as local time, not UTC
                  const year = d.getFullYear();
                  const month = String(d.getMonth() + 1).padStart(2, '0');
                  const day = String(d.getDate()).padStart(2, '0');
                  const hours = String(d.getHours()).padStart(2, '0');
                  const minutes = String(d.getMinutes()).padStart(2, '0');
                  return `${year}-${month}-${day}T${hours}:${minutes}`;
                })()}
                onChange={(e) => setSidebarTask({ ...sidebarTask, endDate: e.target.value ? new Date(e.target.value) : null })}
                InputLabelProps={{ shrink: true }}
              />

              {/* Customer Info Section */}
              {sidebarMode === 'create' ? (
                <>
                  {/* Email lookup for create mode */}
                  <TextField
                    label="Customer Email (Optional)"
                    fullWidth
                    type="email"
                    value={sidebarCustomerEmail}
                    onChange={(e) => setSidebarCustomerEmail(e.target.value)}
                    placeholder="customer@example.com"
                    helperText={
                      sidebarLookupLoading ? "Looking up customer..." :
                      sidebarCustomerFound ? `Found: ${sidebarCustomerFound.first_name} ${sidebarCustomerFound.last_name}` :
                      sidebarCustomerEmail.length >= 5 && sidebarCustomerEmail.includes('@') ? "No customer found with this email" :
                      "Enter email to lookup existing customer"
                    }
                    InputProps={{
                      endAdornment: sidebarLookupLoading ? (
                        <span style={{ fontSize: '0.75rem', color: '#666' }}>...</span>
                      ) : null
                    }}
                  />

                  {/* Show customer info when found */}
                  {sidebarCustomerFound && (
                    <>
                      <TextField
                        label="Customer Name"
                        fullWidth
                        value={`${sidebarCustomerFound.first_name} ${sidebarCustomerFound.last_name}`.trim()}
                        InputProps={{ readOnly: true }}
                      />
                      {sidebarCustomerFound.phone && (
                        <TextField
                          label="Phone Number"
                          fullWidth
                          value={sidebarCustomerFound.phone}
                          InputProps={{ readOnly: true }}
                        />
                      )}

                      {/* Vehicle Selection */}
                      <FormControl fullWidth>
                        <InputLabel>Select Vehicle</InputLabel>
                        <Select
                          value={sidebarTask.user_car_id ? String(sidebarTask.user_car_id) : ''}
                          label="Select Vehicle"
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '__add_new__') {
                              setSidebarAddVehicleOpen(true);
                              setSidebarEditVehicleOpen(false);
                            } else {
                              setSidebarTask({ ...sidebarTask, user_car_id: value });
                              setSidebarEditVehicleOpen(false); // Close edit form when selecting different vehicle
                            }
                          }}
                        >
                          {/* Only show "No vehicle selected" for new customers who can add vehicles */}
                          {!sidebarIsExistingCustomer && (
                            <MenuItem value="">No vehicle selected</MenuItem>
                          )}
                          {sidebarCustomerVehicles.map((vehicle) => (
                            <MenuItem key={vehicle.user_car_id} value={String(vehicle.user_car_id)}>
                              {vehicle.display_name || `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.trim() || vehicle.car_name || 'Vehicle'}
                            </MenuItem>
                          ))}
                          {/* Only show Add New Vehicle option for new customers (not in system) */}
                          {!sidebarIsExistingCustomer && (
                            <>
                              <MenuItem disabled style={{ borderTop: '1px solid #eee', marginTop: '4px', padding: 0, height: '1px' }} />
                              <MenuItem value="__add_new__" style={{ color: '#1976d2', fontWeight: 500 }}>
                                + Add New Vehicle
                              </MenuItem>
                            </>
                          )}
                        </Select>
                      </FormControl>

                      {/* Display selected vehicle details */}
                      {sidebarTask.user_car_id && !sidebarEditVehicleOpen && !sidebarAddVehicleOpen && (() => {
                        const selectedVehicle = sidebarCustomerVehicles.find(v => String(v.user_car_id) === String(sidebarTask.user_car_id));
                        if (!selectedVehicle) return null;
                        return (
                          <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '12px', backgroundColor: '#fafafa' }}>
                            <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '8px', fontWeight: 500 }}>Vehicle Details</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.875rem' }}>
                              {selectedVehicle.year && <div><span style={{ color: '#666' }}>Year:</span> {selectedVehicle.year}</div>}
                              {selectedVehicle.make && <div><span style={{ color: '#666' }}>Make:</span> {selectedVehicle.make}</div>}
                              {selectedVehicle.model && <div><span style={{ color: '#666' }}>Model:</span> {selectedVehicle.model}</div>}
                              {selectedVehicle.submodel && <div><span style={{ color: '#666' }}>Submodel:</span> {selectedVehicle.submodel}</div>}
                              {selectedVehicle.vin_number && <div style={{ gridColumn: '1 / -1' }}><span style={{ color: '#666' }}>VIN:</span> {selectedVehicle.vin_number}</div>}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Edit Selected Vehicle Button - only for new customers (not in system) */}
                      {sidebarTask.user_car_id && !sidebarEditVehicleOpen && !sidebarAddVehicleOpen && !sidebarIsExistingCustomer && (
                        <Button
                          variant="outlined"
                          size="small"
                          color="warning"
                          onClick={openEditVehicleForm}
                          style={{ alignSelf: 'flex-start' }}
                        >
                          Edit Selected Vehicle
                        </Button>
                      )}

                      {/* Inline Edit Vehicle Form - only for new customers */}
                      {sidebarEditVehicleOpen && !sidebarIsExistingCustomer && (
                        <div style={{ border: '1px solid #ed6c02', borderRadius: '8px', padding: '16px', backgroundColor: '#fff8f0' }}>
                          <div style={{ fontWeight: 500, marginBottom: '12px', color: '#ed6c02' }}>Edit Vehicle</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <TextField
                              label="Vehicle Name *"
                              size="small"
                              fullWidth
                              value={sidebarEditVehicle.car_name}
                              onChange={(e) => setSidebarEditVehicle({ ...sidebarEditVehicle, car_name: e.target.value })}
                              placeholder="e.g., My Sedan"
                              helperText="A name to identify this vehicle"
                            />
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <TextField
                                label="Year"
                                size="small"
                                value={sidebarEditVehicle.year}
                                onChange={(e) => setSidebarEditVehicle({ ...sidebarEditVehicle, year: e.target.value })}
                                placeholder="2020"
                                style={{ flex: 1 }}
                              />
                              <TextField
                                label="Make"
                                size="small"
                                value={sidebarEditVehicle.make}
                                onChange={(e) => setSidebarEditVehicle({ ...sidebarEditVehicle, make: e.target.value })}
                                placeholder="Toyota"
                                style={{ flex: 1 }}
                              />
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <TextField
                                label="Model"
                                size="small"
                                value={sidebarEditVehicle.model}
                                onChange={(e) => setSidebarEditVehicle({ ...sidebarEditVehicle, model: e.target.value })}
                                placeholder="Camry"
                                style={{ flex: 1 }}
                              />
                              <TextField
                                label="VIN"
                                size="small"
                                value={sidebarEditVehicle.vin_number}
                                onChange={(e) => setSidebarEditVehicle({ ...sidebarEditVehicle, vin_number: e.target.value })}
                                placeholder="17-digit VIN"
                                style={{ flex: 1 }}
                              />
                            </div>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                              <Button
                                size="small"
                                onClick={() => {
                                  setSidebarEditVehicleOpen(false);
                                  setSidebarEditVehicle({ user_car_id: null, car_name: '', make: '', model: '', year: '', submodel: '', vin_number: '' });
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color="warning"
                                onClick={handleEditVehicle}
                              >
                                Save Changes
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Inline Add Vehicle Form - only for new customers */}
                      {sidebarAddVehicleOpen && !sidebarIsExistingCustomer && (
                        <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '16px', backgroundColor: '#f9f9f9' }}>
                          <div style={{ fontWeight: 500, marginBottom: '12px' }}>Add New Vehicle</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <TextField
                              label="Vehicle Name *"
                              size="small"
                              fullWidth
                              value={sidebarNewVehicle.car_name}
                              onChange={(e) => setSidebarNewVehicle({ ...sidebarNewVehicle, car_name: e.target.value })}
                              placeholder="e.g., My Sedan"
                              helperText="A name to identify this vehicle"
                            />
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <TextField
                                label="Year"
                                size="small"
                                value={sidebarNewVehicle.year}
                                onChange={(e) => setSidebarNewVehicle({ ...sidebarNewVehicle, year: e.target.value })}
                                placeholder="2020"
                                style={{ flex: 1 }}
                              />
                              <TextField
                                label="Make"
                                size="small"
                                value={sidebarNewVehicle.make}
                                onChange={(e) => setSidebarNewVehicle({ ...sidebarNewVehicle, make: e.target.value })}
                                placeholder="Toyota"
                                style={{ flex: 1 }}
                              />
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <TextField
                                label="Model"
                                size="small"
                                value={sidebarNewVehicle.model}
                                onChange={(e) => setSidebarNewVehicle({ ...sidebarNewVehicle, model: e.target.value })}
                                placeholder="Camry"
                                style={{ flex: 1 }}
                              />
                              <TextField
                                label="VIN"
                                size="small"
                                value={sidebarNewVehicle.vin_number}
                                onChange={(e) => setSidebarNewVehicle({ ...sidebarNewVehicle, vin_number: e.target.value })}
                                placeholder="17-digit VIN"
                                style={{ flex: 1 }}
                              />
                            </div>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                              <Button
                                size="small"
                                onClick={() => {
                                  setSidebarAddVehicleOpen(false);
                                  setSidebarNewVehicle({ car_name: '', make: '', model: '', year: '', submodel: '', vin_number: '' });
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={handleAddVehicle}
                              >
                                Add Vehicle
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* VIN for walk-ins without customer account */}
                  {!sidebarCustomerFound && (
                    <TextField
                      label="VIN Number (Optional)"
                      fullWidth
                      value={sidebarTask.vehicle_vin || ''}
                      onChange={(e) => setSidebarTask({ ...sidebarTask, vehicle_vin: e.target.value })}
                      placeholder="17-digit VIN"
                    />
                  )}
                </>
              ) : (
                <>
                  {/* Edit mode - show existing customer/vehicle info with Change/Remove buttons */}
                  {/* Customer Email with Change/Remove buttons */}
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <TextField
                      label="Customer Email"
                      fullWidth
                      type="email"
                      value={sidebarTask.customer_email || ''}
                      InputProps={{ readOnly: true }}
                      disabled
                      helperText={sidebarTask.customer_id ? "Linked to customer account" : "No customer linked"}
                    />
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => setSidebarChangeCustomerOpen(true)}
                      sx={{ height: '56px', minWidth: '80px' }}
                    >
                      {sidebarTask.customer_id ? 'Change' : 'Add'}
                    </Button>
                    {sidebarTask.customer_id && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleSidebarRemoveCustomer}
                        sx={{ height: '56px', minWidth: '80px' }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>

                  {/* Show customer details if linked */}
                  {(sidebarTask.customer_first_name || sidebarTask.customer_last_name) && (
                    <TextField
                      label="Customer Name"
                      fullWidth
                      value={`${sidebarTask.customer_first_name || ''} ${sidebarTask.customer_last_name || ''}`.trim()}
                      InputProps={{ readOnly: true }}
                      disabled
                    />
                  )}
                  {sidebarTask.customer_phone && (
                    <TextField
                      label="Phone Number"
                      fullWidth
                      value={sidebarTask.customer_phone || ''}
                      InputProps={{ readOnly: true }}
                      disabled
                    />
                  )}

                  {/* Vehicle Selection - show if customer has vehicles loaded */}
                  {sidebarCustomerVehicles.length > 0 ? (
                    <>
                      <FormControl fullWidth>
                        <InputLabel>Select Vehicle</InputLabel>
                        <Select
                          value={sidebarTask.user_car_id ? String(sidebarTask.user_car_id) : ''}
                          label="Select Vehicle"
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '__add_new__') {
                              setSidebarAddVehicleOpen(true);
                              setSidebarEditVehicleOpen(false);
                            } else {
                              setSidebarTask({ ...sidebarTask, user_car_id: value });
                              setSidebarEditVehicleOpen(false); // Close edit form when selecting different vehicle
                            }
                          }}
                        >
                          {/* Only show "No vehicle selected" for new customers who can add vehicles */}
                          {!sidebarIsExistingCustomer && (
                            <MenuItem value="">No vehicle selected</MenuItem>
                          )}
                          {sidebarCustomerVehicles.map((vehicle) => (
                            <MenuItem key={vehicle.user_car_id} value={String(vehicle.user_car_id)}>
                              {vehicle.display_name || `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.trim() || vehicle.car_name || 'Vehicle'}
                            </MenuItem>
                          ))}
                          {/* Only show Add New Vehicle option for new customers (not in system) */}
                          {!sidebarIsExistingCustomer && (
                            <>
                              <MenuItem disabled style={{ borderTop: '1px solid #eee', marginTop: '4px', padding: 0, height: '1px' }} />
                              <MenuItem value="__add_new__" style={{ color: '#1976d2', fontWeight: 500 }}>
                                + Add New Vehicle
                              </MenuItem>
                            </>
                          )}
                        </Select>
                      </FormControl>

                      {/* Display selected vehicle details for edit mode */}
                      {sidebarTask.user_car_id && !sidebarEditVehicleOpen && !sidebarAddVehicleOpen && (() => {
                        const selectedVehicle = sidebarCustomerVehicles.find(v => String(v.user_car_id) === String(sidebarTask.user_car_id));
                        if (!selectedVehicle) return null;
                        return (
                          <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '12px', backgroundColor: '#fafafa' }}>
                            <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '8px', fontWeight: 500 }}>Vehicle Details</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.875rem' }}>
                              {selectedVehicle.year && <div><span style={{ color: '#666' }}>Year:</span> {selectedVehicle.year}</div>}
                              {selectedVehicle.make && <div><span style={{ color: '#666' }}>Make:</span> {selectedVehicle.make}</div>}
                              {selectedVehicle.model && <div><span style={{ color: '#666' }}>Model:</span> {selectedVehicle.model}</div>}
                              {selectedVehicle.submodel && <div><span style={{ color: '#666' }}>Submodel:</span> {selectedVehicle.submodel}</div>}
                              {selectedVehicle.vin_number && <div style={{ gridColumn: '1 / -1' }}><span style={{ color: '#666' }}>VIN:</span> {selectedVehicle.vin_number}</div>}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Edit Selected Vehicle Button - only for new customers (not in system) */}
                      {sidebarTask.user_car_id && !sidebarEditVehicleOpen && !sidebarAddVehicleOpen && !sidebarIsExistingCustomer && (
                        <Button
                          variant="outlined"
                          size="small"
                          color="warning"
                          onClick={openEditVehicleForm}
                          style={{ alignSelf: 'flex-start' }}
                        >
                          Edit Selected Vehicle
                        </Button>
                      )}

                      {/* Inline Edit Vehicle Form for Edit Mode - only for new customers */}
                      {sidebarEditVehicleOpen && !sidebarIsExistingCustomer && (
                        <div style={{ border: '1px solid #ed6c02', borderRadius: '8px', padding: '16px', backgroundColor: '#fff8f0' }}>
                          <div style={{ fontWeight: 500, marginBottom: '12px', color: '#ed6c02' }}>Edit Vehicle</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <TextField
                              label="Vehicle Name *"
                              size="small"
                              fullWidth
                              value={sidebarEditVehicle.car_name}
                              onChange={(e) => setSidebarEditVehicle({ ...sidebarEditVehicle, car_name: e.target.value })}
                              placeholder="e.g., My Sedan"
                              helperText="A name to identify this vehicle"
                            />
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <TextField
                                label="Year"
                                size="small"
                                value={sidebarEditVehicle.year}
                                onChange={(e) => setSidebarEditVehicle({ ...sidebarEditVehicle, year: e.target.value })}
                                placeholder="2020"
                                style={{ flex: 1 }}
                              />
                              <TextField
                                label="Make"
                                size="small"
                                value={sidebarEditVehicle.make}
                                onChange={(e) => setSidebarEditVehicle({ ...sidebarEditVehicle, make: e.target.value })}
                                placeholder="Toyota"
                                style={{ flex: 1 }}
                              />
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <TextField
                                label="Model"
                                size="small"
                                value={sidebarEditVehicle.model}
                                onChange={(e) => setSidebarEditVehicle({ ...sidebarEditVehicle, model: e.target.value })}
                                placeholder="Camry"
                                style={{ flex: 1 }}
                              />
                              <TextField
                                label="VIN"
                                size="small"
                                value={sidebarEditVehicle.vin_number}
                                onChange={(e) => setSidebarEditVehicle({ ...sidebarEditVehicle, vin_number: e.target.value })}
                                placeholder="17-digit VIN"
                                style={{ flex: 1 }}
                              />
                            </div>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                              <Button
                                size="small"
                                onClick={() => {
                                  setSidebarEditVehicleOpen(false);
                                  setSidebarEditVehicle({ user_car_id: null, car_name: '', make: '', model: '', year: '', submodel: '', vin_number: '' });
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color="warning"
                                onClick={handleEditVehicle}
                              >
                                Save Changes
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Inline Add Vehicle Form for Edit Mode - only for new customers */}
                      {sidebarAddVehicleOpen && !sidebarIsExistingCustomer && (
                        <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '16px', backgroundColor: '#f9f9f9' }}>
                          <div style={{ fontWeight: 500, marginBottom: '12px' }}>Add New Vehicle</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <TextField
                              label="Vehicle Name *"
                              size="small"
                              fullWidth
                              value={sidebarNewVehicle.car_name}
                              onChange={(e) => setSidebarNewVehicle({ ...sidebarNewVehicle, car_name: e.target.value })}
                              placeholder="e.g., My Sedan"
                              helperText="A name to identify this vehicle"
                            />
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <TextField
                                label="Year"
                                size="small"
                                value={sidebarNewVehicle.year}
                                onChange={(e) => setSidebarNewVehicle({ ...sidebarNewVehicle, year: e.target.value })}
                                placeholder="2020"
                                style={{ flex: 1 }}
                              />
                              <TextField
                                label="Make"
                                size="small"
                                value={sidebarNewVehicle.make}
                                onChange={(e) => setSidebarNewVehicle({ ...sidebarNewVehicle, make: e.target.value })}
                                placeholder="Toyota"
                                style={{ flex: 1 }}
                              />
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <TextField
                                label="Model"
                                size="small"
                                value={sidebarNewVehicle.model}
                                onChange={(e) => setSidebarNewVehicle({ ...sidebarNewVehicle, model: e.target.value })}
                                placeholder="Camry"
                                style={{ flex: 1 }}
                              />
                              <TextField
                                label="VIN"
                                size="small"
                                value={sidebarNewVehicle.vin_number}
                                onChange={(e) => setSidebarNewVehicle({ ...sidebarNewVehicle, vin_number: e.target.value })}
                                placeholder="17-digit VIN"
                                style={{ flex: 1 }}
                              />
                            </div>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                              <Button
                                size="small"
                                onClick={() => {
                                  setSidebarAddVehicleOpen(false);
                                  setSidebarNewVehicle({ car_name: '', make: '', model: '', year: '', submodel: '', vin_number: '' });
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={handleAddVehicle}
                              >
                                Add Vehicle
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Fallback: Show current vehicle info and VIN field if no vehicles loaded */}
                      {(sidebarTask.make || sidebarTask.model || sidebarTask.year) && (
                        <TextField
                          label="Current Vehicle"
                          fullWidth
                          value={`${sidebarTask.year || ''} ${sidebarTask.make || ''} ${sidebarTask.model || ''}`.trim()}
                          InputProps={{ readOnly: true }}
                          helperText="From customer's account"
                        />
                      )}
                      <TextField
                        label="VIN Number"
                        fullWidth
                        value={sidebarTask.vehicle_vin || sidebarTask.vin_number || ''}
                        onChange={(e) => setSidebarTask({ ...sidebarTask, vehicle_vin: e.target.value })}
                        placeholder="17-digit VIN"
                      />
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setSidebarOpen(false)}
          >
            Cancel
          </Button>
          {sidebarMode === 'edit' && sidebarTask && (
            <Button
              variant="outlined"
              color="error"
              onClick={handleDeleteTask}
            >
              Delete
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveTask}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog for Availability/Leave Blocks */}
      <AvailabilityEditDialog
        open={editDialogOpen}
        availability={selectedBlock}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
      />

      {/* Create Availability Dialog */}
      <AvailabilityDialog
        open={availabilityDialogOpen}
        mechanics={mechDate}
        selectedLocationId={selectedLocationId}
        onClose={() => setAvailabilityDialogOpen(false)}
        onSuccess={() => {
          setAvailabilityDialogOpen(false);
          setIsRefreshing(true);
          refreshScheduler();
          // Don't forcibly turn off refreshing - let refreshScheduler handle it after data loads
        }}
      />

      {/* Create Leave/Sick Dialog */}
      <LeaveRequestDialog
        open={leaveDialogOpen}
        mechanics={mechDate}
        appointments={appointments}
        onClose={() => setLeaveDialogOpen(false)}
        onSuccess={() => {
          setLeaveDialogOpen(false);
          setIsRefreshing(true);
          refreshScheduler();
          // Don't forcibly turn off refreshing - let refreshScheduler handle it after data loads
        }}
        onTaskConflict={(conflictData) => {
          setTaskConflictData(conflictData);
          setTaskConflictDialogOpen(true);
        }}
      />

      {/* Task Conflict Dialog - shown when adding leave conflicts with existing tasks */}
      <TaskConflictDialog
        open={taskConflictDialogOpen}
        mechanicName={taskConflictData?.mechanicName}
        conflictingTasks={taskConflictData?.conflictingTasks}
        leaveType={taskConflictData?.leaveType}
        onClose={() => {
          setTaskConflictDialogOpen(false);
          setTaskConflictData(null);
        }}
        onConfirm={async () => {
          const tasksToUnassign = taskConflictData?.conflictingTasks || [];
          try {
            // Unassign each conflicting task
            for (const task of tasksToUnassign) {
              const payload = {
                task_id: task.task_id || task.id,
                mechanic_id: null,
                // Preserve existing times so the task stays on the same slot but unassigned
                appointment_time: task.appointment_time || task.calendar_start || task.startDate,
                calendar_start: task.calendar_start || task.startDate,
                calendar_end: task.calendar_end || task.endDate,
                status: task.status,
                service_info: task.service_info,
                vin_number: task.vin_number,
                sc_id: task.sc_id,
              };
              const res = await fetch(envVariable.serverId + "/SCO/updateTask.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
              });
              // Best‑effort: log response but don't block on errors for individual tasks
              try {
                const json = await res.json();
  if (DEBUG) console.log("[TaskConflict] updateTask response", json);
              } catch (_) {
  if (DEBUG) console.log("[TaskConflict] updateTask response (non‑JSON)");
              }
            }

            // Now submit the leave request itself using the saved leaveData
            // Note: We're passing confirm_unassignment=true so the backend knows we confirmed
            const leaveData = taskConflictData?.leaveData;
            if (leaveData) {
              const response = await fetch(envVariable.serverId + '/SCO/VacationRequest.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  action: 'create',
                  start_date_time: leaveData.startdatetime,
                  end_date_time: leaveData.enddatetime,
                  mechanic_id: leaveData.mechanicid,
                  reason: leaveData.reason,
                  sc_id: leaveData.sc_id,
                  status: 'approved',
                  title: leaveData.reason === 'sick' ? 'Sick Leave' : 'Vacation',
                  confirm_unassignment: true,
                }),
              });
              const result = await response.json();
              if (result.status === 'Success') {
                setTaskConflictDialogOpen(false);
                setTaskConflictData(null);
                refreshScheduler();
              } else {
                alert('Failed to add leave request: ' + (result.message || 'Unknown error'));
              }
            }
          } catch (error) {
            console.error('[TaskConflict] Error unassigning tasks or adding leave:', error);
            alert('Error unassigning tasks or adding leave. Please try again.');
          }
        }}
      />

      {/* Snackbar for toast messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Mechanic Selection Dialog */}
      <Dialog
        open={mechanicSelectOpen}
        onClose={() => setMechanicSelectOpen(false)}
        maxWidth="sm"
        fullWidth
      >
      <DialogTitle>Select Mechanic</DialogTitle>
      <DialogContent>
        <p>Multiple mechanics are available for this time slot. Please select one:</p>
        <List>
          {pendingTaskDrop?.availableMechanics?.map((mech) => (
            <ListItem
              key={mech.user_id}
              component="button"
              onClick={() => {
                if (pendingTaskDrop?.mode === 'assignPending') {
                  // Assign a pending (external) task after Week-view drop
                  const newAppointment = {
                    ...pendingTaskDrop.newAppointment,
                    mechanic_id: mech.user_id,
                  };
                  upsertAppointment(newAppointment);
                  postPendingTask(newAppointment);
                  removeExternalPendingTask(pendingTaskDrop.newAppointment);
                } else {
                  // Apply the task change with selected mechanic (existing appointment move)
                  const base = appointments.find(a => String(a.id) === String(pendingTaskDrop?.appointmentId) || String(a.task_id) === String(pendingTaskDrop?.appointmentId))
                    || pendingTaskDrop.appointment || {};
                  const updatedAppointment = {
                    ...base,
                    ...pendingTaskDrop.changedData,
                    mechanic_id: mech.user_id,
                  };
                  setAppointments(prev => prev.map(a => (
                    (a.id === updatedAppointment.id || (updatedAppointment.task_id && a.task_id == updatedAppointment.task_id))
                      ? updatedAppointment : a
                  )));
                  postUpdateTask(updatedAppointment, base);
                }

                setMechanicSelectOpen(false);
                setPendingTaskDrop(null);
                setSnackbar({
                  open: true,
                  message: `Task assigned to ${mech.first_name} ${mech.last_name}`,
                  severity: 'success'
                });
              }}
              sx={{
                border: '1px solid #ddd',
                borderRadius: '4px',
                marginBottom: '8px',
                '&:hover': { backgroundColor: '#f5f5f5' }
              }}
            >
              <ListItemText
                primary={`${mech.first_name} ${mech.last_name}`}
                secondary={`Available for this time slot`}
              />
            </ListItem>
          ))}
        </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setMechanicSelectOpen(false);
            setPendingTaskDrop(null);
          }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop for drag-and-drop operations */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isRefreshing}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Change Customer Dialog */}
      <Dialog
        open={sidebarChangeCustomerOpen}
        onClose={() => {
          setSidebarChangeCustomerOpen(false);
          setSidebarNewCustomerEmail('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {sidebarTask?.customer_id ? 'Change Customer' : 'Add Customer'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Customer Email"
            type="email"
            fullWidth
            variant="outlined"
            value={sidebarNewCustomerEmail}
            onChange={(e) => setSidebarNewCustomerEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSidebarChangeCustomer();
              }
            }}
            placeholder="Enter customer email to lookup"
            sx={{ mt: 1 }}
          />
          <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '8px' }}>
            Enter the customer&apos;s email address to search for their account. If found, their information and vehicles will be linked to this task.
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSidebarChangeCustomerOpen(false);
              setSidebarNewCustomerEmail('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSidebarChangeCustomer}
            variant="contained"
            disabled={sidebarLookupLoading || !sidebarNewCustomerEmail.trim()}
          >
            {sidebarLookupLoading ? 'Looking up...' : 'Lookup'}
          </Button>
        </DialogActions>
      </Dialog>
      </div>
    );
  }

const Demo = () => {
  // const [dragging, setDragging] = useState(false);

  const [mechData, setMechData] = useState([]);
  const [currentViewName, setCurrentViewName] = useState("Week");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [ReactCalendarDate, setReactCalendarDate] = useState(dayjs());
  const [generating, setGenerating] = useState(false);
  const [generateMessage, setGenerateMessage] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedMechanic, setSelectedMechanic] = useState(null);
  const [isRailCollapsed, setIsRailCollapsed] = useState(() => {
    const storedState = localStorage.getItem("sco-calendar-rail-collapsed");
    if (storedState === "true") return true;
    if (storedState === "false") return false;
    return window.matchMedia("(max-width: 1200px)").matches;
  });
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [shopDialogOpen, setShopDialogOpen] = useState(false);

  useState(null);

  const selectedLocation = useMemo(
    () =>
      locations.find(
        (location) => Number(location.location_id) === Number(selectedLocationId)
      ) || null,
    [locations, selectedLocationId]
  );

  const getLocationLabel = useCallback((location, index = 0) => {
    if (!location) {
      return "Primary Location";
    }

    const base =
      typeof location.nickname === "string" && location.nickname.trim()
        ? location.nickname.trim()
        : `Location ${index + 1}`;

    return Number(location.is_primary) === 1 ? `${base} (Primary)` : base;
  }, []);

  useEffect(() => {
    const SC_Id = localStorage.getItem("SC_Id");

    if (!SC_Id) {
      return;
    }

    fetch(`${envVariable.serverId}/SCO/getProfileInfo.php?sc_id=${SC_Id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data?.type !== "success") {
          setLocations([]);
          setSelectedLocationId(null);
          return;
        }

        const fetchedLocations = Array.isArray(data?.message?.locations)
          ? data.message.locations
          : [];

        setLocations(fetchedLocations);

        if (fetchedLocations.length === 0) {
          setSelectedLocationId(null);
          return;
        }

        setSelectedLocationId((prev) => {
          const stillExists = fetchedLocations.some(
            (location) => Number(location.location_id) === Number(prev)
          );

          if (stillExists) {
            return prev;
          }

          return (
            fetchedLocations.find((location) => Number(location.is_primary) === 1)?.location_id ??
            fetchedLocations[0]?.location_id ??
            null
          );
        });
      })
      .catch((error) => {
        console.error("Error fetching locations:", error);
        setLocations([]);
        setSelectedLocationId(null);
      });
  }, []);

  // Handle date change in the calendar
  const handleDateChange = (ReactCalendarDate) => {
    setReactCalendarDate(ReactCalendarDate);
    setCurrentDate(ReactCalendarDate);
    // setCurrentDate(currentDate);
    // setSelectAll(true);
  };

  const handleViewChange = (viewName) => {
    setCurrentViewName(viewName);

    // When switching away from Week view, clear mechanic selection
    if (viewName !== 'Week' && selectedMechanic) {
      setSelectedMechanic(null);
    }
  };

  // Generate weekly availability from template
  const handleGenerateWeek = async () => {
    const SC_Id = localStorage.getItem("SC_Id");
    if (!SC_Id) {
      window.dispatchEvent(new CustomEvent('calendar:toast', { detail: { message: 'No service center ID found', severity: 'error' } }));
      return;
    }

    if (!selectedLocationId) {
      window.dispatchEvent(new CustomEvent('calendar:toast', { detail: { message: 'Select a shop before generating availability', severity: 'error' } }));
      return;
    }

    // Calculate the start of the current week (Sunday)
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day; // Adjust to Sunday
    startOfWeek.setDate(diff);
    const weekStartDate = startOfWeek.toISOString().split('T')[0]; // YYYY-MM-DD format

    const mechanicsToGenerate = selectedMechanic ? [selectedMechanic] : mechData;

    if (mechanicsToGenerate.length === 0) {
      window.dispatchEvent(new CustomEvent('calendar:toast', { detail: { message: 'No mechanics available to generate schedule for', severity: 'error' } }));
      return;
    }

    setGenerating(true);
    setGenerateMessage("");

    // Optional safety: if any availability exists for this week, confirm reset
    try {
      const availabilityUrl = new URL(`${envVariable.serverId}/mechanicSlots/getFreeslot.php`);
      availabilityUrl.searchParams.set("sc_id", SC_Id);
      if (selectedLocationId) {
        availabilityUrl.searchParams.set("location_id", selectedLocationId);
      }

      const freeslotRes = await fetch(availabilityUrl.toString(), {
        headers: { 'Content-Type': 'application/json' }
      });
      const freeslotJson = await freeslotRes.json();
      if (Array.isArray(freeslotJson?.message)) {
        const start = new Date(weekStartDate);
        const end = new Date(weekStartDate);
        end.setDate(end.getDate() + 6);
        const hasAvail = freeslotJson.message.some(s => s.mech_status === 'Available' &&
          new Date(s.date) >= start && new Date(s.date) <= end);
        if (hasAvail) {
          const ok = window.confirm('Availability exists for this week. Regenerating will reset them based on templates. Continue?');
          if (!ok) { setGenerating(false); return; }
        }
      }
    } catch (e) {
      // non-blocking
    }

    let successCount = 0;
    let errorCount = 0;
    let skipCount = 0;
    let totalReassigned = 0;
    let totalUnassigned = 0;

    for (const mechanic of mechanicsToGenerate) {
      try {
        const apiKey = localStorage.getItem('api_key') || localStorage.getItem('@api_key') || '';
        // Preflight: skip mechanics without a saved weekly template to avoid 400s
        try {
          const pre = await fetch(
            `${envVariable.serverId}/WeeklySchedule/getWeeklySchedule.php?mechanic_id=${mechanic.user_id}&sc_id=${SC_Id}`,
            { headers: { 'X-API-KEY': apiKey } }
          );
          const preData = await pre.json();
          const hasTemplate = pre.ok && preData?.type === 'success' && preData?.schedule_by_day &&
            Object.values(preData.schedule_by_day).some((arr) => Array.isArray(arr) && arr.length > 0);
          if (!hasTemplate) {
            skipCount++;
            console.info(`Skipped ${mechanic.first_name}: no weekly template (not required)`);
            continue;
          }
        } catch (_) {
          // If preflight check fails unexpectedly, fall through to main request (backend will validate)
        }
        // Get timezone offset in minutes (negative for UTC-behind, positive for UTC-ahead)
        // e.g., EST returns -300 (UTC-5 hours)
        const timezoneOffset = new Date().getTimezoneOffset();

        const response = await fetch(`${envVariable.serverId}/WeeklySchedule/generateWeeklyAvailability.php`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-KEY': apiKey,
          },
          body: JSON.stringify({
            mechanic_id: mechanic.user_id,
            sc_id: SC_Id,
            location_id: selectedLocationId,
            week_start_date: weekStartDate,
            timezone_offset: timezoneOffset
          })
        });

        const data = await response.json();
        if (data.type === 'success') {
          // If backend indicates skip, count as skipped not error
          if (data.skipped_no_template) {
            skipCount++;
            console.info(`Skipped ${mechanic.first_name}: no weekly template (backend)`);
          } else {
            successCount++;
  if (DEBUG) console.log(`Generated ${data.slots_inserted} slots for ${mechanic.first_name} ${mechanic.last_name}`);

            // Accumulate conflict resolution stats
            if (data.tasks_reassigned > 0) totalReassigned += data.tasks_reassigned;
            if (data.tasks_unassigned > 0) totalUnassigned += data.tasks_unassigned;
          }
        } else {
          errorCount++;
          console.error(`Failed for ${mechanic.first_name}: ${data.message}`);
        }
      } catch (err) {
        errorCount++;
        console.error(`Error generating for ${mechanic.first_name}:`, err);
      }
    }

    setGenerating(false);

    // Refresh the calendar to show newly generated availability
    setRefreshTrigger((v) => v + 1);

    if (errorCount === 0) {
      const parts = [];
      if (successCount > 0) parts.push(`generated for ${successCount}`);
      if (skipCount > 0) parts.push(`skipped ${skipCount} without templates`);
      if (totalReassigned > 0) parts.push(`${totalReassigned} tasks reassigned`);
      if (totalUnassigned > 0) parts.push(`${totalUnassigned} tasks unassigned`);
      const summary = parts.length ? parts.join(', ') : 'nothing to do';
      setGenerateMessage(`\u2713 ${summary}`);
      setTimeout(() => {
        setGenerateMessage("");
      }, 3000); // Increased timeout since message may be longer
    } else {
      setGenerateMessage(`Generated for ${successCount} mechanic(s), ${errorCount} failed. Check console for details.`);
    }
  };

  useEffect(() => {
  if (DEBUG) console.log("Effect triggered");
    const SC_Id = localStorage.getItem("SC_Id");

    if (!SC_Id) {
      console.error("No SC_Id found in localStorage");
      return; // Early exit if SC_Id is not available
    }

    const url = `${envVariable.serverId}/SCO/Mechanic.php?SC_Id=${SC_Id}`;
    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`); // Throw an error on bad HTTP status
        }
        return response.json();
      })
      .then((data) => {
        if (!data || !data.mechanics || data.mechanics.length === 0) {
  if (DEBUG) console.log("No mechanic data available.");
          setMechData([]); // Setting to empty array or handling accordingly
          return;
        }
  if (DEBUG) console.log("Data:", data.mechanics);
        setMechData(data.mechanics);
      })
      .catch((error) => {
        console.error("Error fetching mechanics:", error);
        setMechData([]); // Handling error by setting mechanics data to empty array
      });
  }, []);

  useEffect(() => {
    localStorage.setItem("sco-calendar-rail-collapsed", String(isRailCollapsed));
  }, [isRailCollapsed]);

  const theme = createTheme({
    palette: {
      primary: {
        main: "#0f4c81",
      },
      secondary: {
        main: "#2f6f5e",
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 10,
            fontWeight: 700,
          },
        },
      },
    },
  });

  return (
    <div>
      <MenuBar />
      <React.Fragment>
        <Paper>
          <div className={`cal-container ${isRailCollapsed ? "rail-collapsed" : ""}`}>
            <div className={`side-bar ${isRailCollapsed ? "collapsed" : ""}`}>
              <button
                type="button"
                className="calendar-rail-toggle"
                onClick={() => setIsRailCollapsed((prev) => !prev)}
                aria-label={isRailCollapsed ? "Expand calendar controls" : "Collapse calendar controls"}
              >
                {isRailCollapsed ? <ChevronRightRoundedIcon /> : <ChevronLeftRoundedIcon />}
                <span>{isRailCollapsed ? "Expand" : "Collapse"}</span>
              </button>

              {!isRailCollapsed && (
                <>
                  <div className="view">
                    <ButtonGroup variant="outlined" aria-label="view mode" fullWidth>
                      <Button
                        onClick={() => handleViewChange("Day")}
                        className={`calendar-view-btn ${currentViewName === "Day" ? "is-active" : ""}`}
                      >
                        Day
                      </Button>
                      <Button
                        onClick={() => handleViewChange("Week")}
                        className={`calendar-view-btn ${currentViewName === "Week" ? "is-active" : ""}`}
                      >
                        Week
                      </Button>
                      <Button
                        onClick={() => handleViewChange("Month")}
                        className={`calendar-view-btn ${currentViewName === "Month" ? "is-active" : ""}`}
                      >
                        Month
                      </Button>
                    </ButtonGroup>
                  </div>

                  <ThemeProvider theme={theme}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateCalendar
                        className="side-calendar"
                        onChange={handleDateChange}
                        value={ReactCalendarDate}
                        showDaysOutsideCurrentMonth
                        fixedWeekNumber={6}
                      />
                    </LocalizationProvider>
                  </ThemeProvider>

                  <div className="mechanic">
                    <div>
                      <Button
                        onClick={() => setSelectedMechanic(null)}
                        className="calendar-mechanic-all"
                        sx={{
                          width: "100%",
                          m: 1,
                          color: "var(--sco-ink-900)",
                          backgroundColor: !selectedMechanic ? "var(--sco-surface-0)" : "#dbe4f1",
                          border: "1px solid var(--sco-border-subtle)",
                          "&:hover": {
                            backgroundColor: !selectedMechanic ? "var(--sco-surface-0)" : "#cdd9ea",
                          },
                        }}
                      >
                        All Mechanics
                      </Button>

                      {currentViewName === "Week" && (
                        <Button
                          onClick={handleGenerateWeek}
                          disabled={generating}
                          sx={{
                            width: "100%",
                            m: 1,
                            color: "white",
                            backgroundColor: "var(--sco-success)",
                            "&:hover": {
                              backgroundColor: "#214f43",
                            },
                            "&:disabled": {
                              backgroundColor: "#8ca69f",
                            },
                          }}
                        >
                          {generating ? "Generating..." : selectedMechanic ? `Generate Week for ${selectedMechanic.first_name}` : "Generate Week for All"}
                        </Button>
                      )}

                      {generateMessage && (
                        <div className="calendar-generate-message">
                          {generateMessage}
                        </div>
                      )}

                      {mechData.length > 0 ? (
                        mechData.map((mechanic) => (
                          <ListItem
                            className="mechanicButton"
                            key={mechanic.user_id}
                            onClick={() => {
                              if (currentViewName !== "Week") return;
                              if (DEBUG) console.log("Setting selected mechanic to:", mechanic);
                              setSelectedMechanic(mechanic);
                            }}
                            style={{
                              margin: "5px",
                              backgroundColor:
                                selectedMechanic && selectedMechanic.user_id === mechanic.user_id ? "#d8e6f8" : "white",
                              cursor: currentViewName === "Week" ? "pointer" : "not-allowed",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              opacity: currentViewName === "Week" ? 1 : 0.5,
                              pointerEvents: currentViewName === "Week" ? "auto" : "none",
                              border: "1px solid var(--sco-border-subtle)",
                            }}
                          >
                            {mechanic.first_name} {mechanic.last_name}
                            <div style={{justifyContent:"flex-end",alignItems:"end",width:"7px",padding:"7px",height:"7px",borderRadius:"50%",backgroundColor:getMechanicColor(mechanic.user_id),marginLeft:"10px"}} />
                          </ListItem>
                        ))
                      ) : (
                        <ListItem
                          style={{
                            margin: "5px",
                            padding: "10px",
                            textAlign: "center",
                            color: "gray",
                          }}
                        >
                          No mechanics available
                        </ListItem>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="custom-scheduler">
              <div className="creation-toolbar">
                {isRailCollapsed && (
                  <Button
                    variant="outlined"
                    className="creation-btn creation-btn-rail"
                    onClick={() => setIsRailCollapsed(false)}
                  >
                    <ChevronRightRoundedIcon fontSize="small" />
                    Controls
                  </Button>
                )}
                <Button
                  variant="contained"
                  className="creation-btn creation-btn-task"
                  onClick={() => {
                    try {
                      const base = currentDate?.$d ? new Date(currentDate.$d) : new Date();
                      const now = new Date();
                      base.setHours(now.getHours(), now.getMinutes(), 0, 0);
                      window.dispatchEvent(new CustomEvent('calendar:new-task', { detail: { baseTime: base } }));
                    } catch (err) { /* noop */ }
                  }}
                >
                  New Task
                </Button>
                <Button
                  variant="contained"
                  className="creation-btn creation-btn-availability"
                  onClick={() => window.dispatchEvent(new Event('calendar:open-availability-dialog'))}
                >
                  New Availability
                </Button>
                <Button
                  variant="contained"
                  className="creation-btn creation-btn-leave"
                  onClick={() => window.dispatchEvent(new Event('calendar:open-leave-dialog'))}
                >
                  New Leave/Sick
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: '#7E57C2',
                    color: 'white',
                    '&:hover': { backgroundColor: '#673AB7' },
                  }}
                  disabled={locations.length === 0}
                  onClick={() => setShopDialogOpen(true)}
                >
                  {`Switch Shop${selectedLocation ? `: ${getLocationLabel(selectedLocation, locations.findIndex((location) => Number(location.location_id) === Number(selectedLocationId)))}` : ""}`}
                </Button>
              </div>
              <Dialog
                open={shopDialogOpen}
                onClose={() => setShopDialogOpen(false)}
                fullWidth
                maxWidth="sm"
              >
                <DialogTitle>Switch Shop</DialogTitle>
                <DialogContent dividers>
                  {locations.length === 0 ? (
                    <Typography color="text.secondary">
                      No shop locations found.
                    </Typography>
                  ) : (
                    <List disablePadding>
                      {locations.map((location, index) => (
                        <React.Fragment key={location.location_id ?? index}>
                          <ListItem
                            button
                            selected={Number(location.location_id) === Number(selectedLocationId)}
                            onClick={() => {
                              setSelectedLocationId(location.location_id);
                              setShopDialogOpen(false);
                            }}
                          >
                            <ListItemText
                              primary={getLocationLabel(location, index)}
                              secondary={
                                [location.address1, location.city, location.state, location.zipcode]
                                  .filter(Boolean)
                                  .join(", ") || "No address entered yet"
                              }
                            />
                          </ListItem>
                          {index < locations.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShopDialogOpen(false)}>Close</Button>
                </DialogActions>
              </Dialog>
              {/* <div onDrop={handleDrop} onDragOver={allowDrop}> */}
              {/*  */}
              {/* Always use DefaultScheduler, but filter data when a mechanic is selected in Week view */}
                <DefaultScheduler
                  currentDate={currentDate}
                  setCurrentDate={setCurrentDate}
                  onExternalDateChange={handleDateChange}
                  currentViewName={currentViewName}
                  setCurrentViewName={setCurrentViewName}
                  mechanics={mechData}
                  refreshTrigger={refreshTrigger}
                  selectedLocationId={selectedLocationId}
                focusedMechanicId={selectedMechanic?.user_id || null}
                onExitFocus={() => setSelectedMechanic(null)}
                />
            </div>
          </div>
        </Paper>
      </React.Fragment>
      <Footer />

      {/* Loading spinner for Generate Week operation */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={generating}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

export default Demo;
